/**
 * The Georgian message catalogue — the single place every visible string in the SPA comes from
 * (FR-UX-05: the interface is Georgian only and its texts live in one place so a second language
 * can be added later without hunting through components).
 *
 * Two rules govern this file and are both enforced by tests:
 *
 *  1. **Boundary B8** (`BOUNDARIES.md` § 4, FR-AUTH-05, R3-2): this catalogue carries no
 *     student-facing privacy or AI-processing notice in any form — no screen, checkbox, banner,
 *     helper text or one-line note. `tests/boundaries/b8-message-catalogue.test.ts` holds the exact
 *     forbidden-phrase list and scans this file on every run.
 *  2. **The server never sends display text** (`BOUNDARIES.md` § 6). A failed operation returns one
 *     of eight stable codes plus a message *key*; `errorMessage()` below is the only place a key
 *     becomes Georgian text, and `messages.test.ts` proves the mapping is total over the codes
 *     `@simple-qa/shared` defines.
 *
 * Scope: T007 ships the application shell — sign-in, the authenticated frame, the reusable
 * primitives and the walking-skeleton route. Strings for screens that do not exist yet are not
 * written here in advance; the task that builds a screen adds its own section.
 *
 * The module has no runtime import: it is data, and every consumer — the shell, the primitives and
 * the boundary scan — reads it as data. The tie to `@simple-qa/shared` is proven by
 * `messages.test.ts` rather than by the type system, because `ERROR_MESSAGE_KEYS` is declared as
 * `Record<ErrorCode, MessageKey>` and its literal key values are widened away before this module
 * could see them.
 */

export const messages = Object.freeze({
  app: Object.freeze({
    /** The product name is a proper noun and stays Latin in every language. */
    name: 'Mr. Nutrition',
    /** One factual sentence about what the application is. No claim, no number, no promise. */
    purpose: 'კურსების, მასალებისა და კითხვა-პასუხის სასწავლო სივრცე.',
    skipToContent: 'პირდაპირ მთავარ შიგთავსზე გადასვლა',
    mainLandmark: 'მთავარი შიგთავსი',
  }),

  /**
   * T073 — the installable shell and the offline fallback page.
   *
   * `apps/web/public/` is served verbatim and is scanned by neither B8 nor B11, so the manifest and
   * `offline.html` would otherwise be a second place a visible Georgian string lives. They are held
   * here instead and `pwa.test.ts` asserts the two static files carry exactly these values, which
   * puts them back under the catalogue's own rules (FR-UX-05).
   */
  pwa: Object.freeze({
    /**
     * The manifest's full `name`. The proper noun is the installed application's identity and stays
     * Latin (`app.name`); what follows it says, in Georgian, what the application is.
     */
    appFullName: 'Mr. Nutrition — სასწავლო სივრცე',
    offlineHeading: 'კავშირი არ არის',
    offlineBody: 'ინტერნეტთან კავშირი ვერ დამყარდა. შეამოწმეთ ქსელი და სცადეთ ხელახლა.',
  }),

  auth: Object.freeze({
    signInHeading: 'შესვლა',
    /** FR-AUTH-01: Google is the only sign-in method; nothing else is offered anywhere. */
    signInWithGoogle: 'Google-ით შესვლა',
    signingIn: 'მიმდინარეობს შესვლა…',
    signInFailed: 'შესვლა ვერ მოხერხდა. სცადეთ ხელახლა.',
    signInCancelled: 'შესვლა შეწყდა.',
    checkingSession: 'სესიის შემოწმება…',
    signedInAs: 'შესული ხართ როგორც',
    signOut: 'გამოსვლა',
    signingOut: 'მიმდინარეობს გამოსვლა…',
    signOutFailed: 'გამოსვლა ვერ მოხერხდა. სცადეთ ხელახლა.',
    signedOutHeading: 'თქვენ არ ხართ შესული',
  }),

  config: Object.freeze({
    heading: 'აპლიკაცია არ არის კონფიგურირებული',
    /** Shown instead of the sign-in screen; the client contacts nothing while this is on screen. */
    body: 'Firebase-ის პარამეტრები არ არის მითითებული, ამიტომ შესვლა ამ გარემოში მიუწვდომელია.',
    missingLabel: 'აკლია შემდეგი პარამეტრები:',
  }),

  state: Object.freeze({
    loading: 'იტვირთება…',
    empty: 'ჩანაწერები არ არის.',
    unavailableHeading: 'ჯერ მიუწვდომელია',
    retry: 'ხელახლა ცდა',
    errorHeading: 'მოქმედება ვერ შესრულდა',
  }),

  access: Object.freeze({
    checking: 'წვდომის შემოწმება…',
    refresh: 'მდგომარეობის განახლება',
    pending: Object.freeze({
      heading: 'მოთხოვნა განხილვაშია',
      body: 'შესვლის მოთხოვნა მიღებულია. სასწავლო სივრცე ადმინისტრატორის დამტკიცების შემდეგ გაიხსნება.',
    }),
    rejected: Object.freeze({
      heading: 'წვდომა ჯერ არ არის ხელმისაწვდომი',
      body: 'ამ ანგარიშისთვის სასწავლო სივრცე არ არის გახსნილი. დეტალებისთვის დაუკავშირდით ადმინისტრატორს.',
    }),
    deactivated: Object.freeze({
      heading: 'წვდომა შეჩერებულია',
      body: 'ამ ანგარიშის წვდომა დროებით შეჩერებულია. დეტალებისთვის დაუკავშირდით ადმინისტრატორს.',
    }),
    approved: Object.freeze({
      heading: 'სასწავლო სივრცე მზადაა',
      body: 'თქვენი წვდომა გახსნილია. კურსები და კითხვა-პასუხის სივრცე აქ გამოჩნდება.',
    }),
  }),

  /**
   * The student question-and-answer surface — T045 + T067.
   *
   * Two rules from the approved sources shape this section. The question form carries **no** notice,
   * helper text or checkbox (FR-Q-07): a course selector when one is needed, the field, the counter
   * and the submit action, nothing else. And the student sees the two simplified states of
   * FR-STATE-04 — waiting and answered — never the four the administrator works with.
   *
   * *(PRD-06)* The approved student workspace adds the words its three regions need — a workspace
   * title, the two context-rail headings, the waiting card's own heading and the identity a published
   * answer is read under. Every one of them is copy the owner approved in the prototype and the
   * reconciliation table keeps (`docs/evidence/product-redesign/contract-reconciliation.md` §§ 2–4);
   * the four excluded prototype strings — the notification promise, the helpful/follow-up controls,
   * the composer helper block and the invented question number — are deliberately **absent**, so no
   * component can reach for one.
   */
  qa: Object.freeze({
    navLabel: 'კითხვა-პასუხი',
    heading: 'კითხვა-პასუხი',
    /** *(PRD-06)* The workspace's own title, under the destination name as its eyebrow. */
    workspaceHeading: 'ჩემი სასწავლო სივრცე',
    signInBody: 'კითხვებისა და პასუხების სანახავად ჯერ შედით სისტემაში.',

    /* Course discovery — a prerequisite of the feed, never a hidden part of it. */
    courseLabel: 'კურსი',
    chooseCourseHeading: 'აირჩიეთ კურსი',
    chooseCourseBody: 'თქვენ რამდენიმე კურსზე ხართ ჩარიცხული. კითხვები კურსის მიხედვით იხსნება.',
    noCoursesHeading: 'კურსი ჯერ არ არის ხელმისაწვდომი',
    noCoursesBody: 'როგორც კი კურსი გაიხსნება, კითხვები და პასუხები აქ გამოჩნდება.',

    /* The remaining question right, as the server reports it. */
    rightHeading: 'კითხვის უფლება',
    rightDaysLeft: 'დარჩენილი დღე',
    rightUntil: 'ბოლო დღე',
    rightClosed: 'კითხვის დასმის ვადა ამოიწურა',

    /* Search. */
    searchLabel: 'კითხვების ძიება',
    searchPlaceholder: 'მოძებნეთ კითხვა ან პასუხი',
    searchHint: 'ძიება იწყება ორი სიმბოლოდან.',
    suggestionsLabel: 'ძიების შეთავაზებები',
    clearSearch: 'ძიების გასუფთავება',

    /*
     * Scope — the group view exists only on a full course.
     *
     * *(PRD-06)* The two labels are the concise author/group pair the approved direction quotes
     * verbatim — `ჩემი` / `ჯგუფის` (`DESIGN.md` § Student Workspace Composition,
     * `docs/design/product-redesign-addendum.md` § 5.2). The operation, its `scope` parameter and the
     * rule that the group option exists only on a full course are exactly what they were; only the
     * long „სრული ჯგუფი" wording is replaced by the short one the owner approved.
     */
    scopeLabel: 'ვისი კითხვები',
    scopeMine: 'ჩემი',
    scopeGroup: 'ჯგუფის',

    /*
     * *(PRD-06)* What is left of the filter rail, and why.
     *
     * The owner's 2026-09-15 annotation pass removed the student sort and date controls from the
     * surface; PRD-06 removed the unreachable component that still held them
     * (`features/student/qa/FilterRail.tsx`), and with it the nine strings only that component read.
     * These two survive as the **names of controls that must not come back** — `qa-page.test.tsx`
     * asserts a search never puts a sort field or a date button on screen, and a rule stated against
     * the catalogue's own wording cannot drift from it.
     *
     * `sourceFavorites` is the same kind of guard: favourites is a card in the context rail, never a
     * source toggle beside the feed.
     */
    sortLabel: 'დალაგება',
    dateFilter: 'თარიღი',
    sourceFavorites: 'რჩეული',

    /* Rows. */
    listLabel: 'კითხვების სია',
    resultsLabel: 'ნაპოვნია',
    statusWaiting: 'პასუხს ელოდება',
    statusAnswered: 'პასუხი გაცემულია',
    editedMarker: 'რედაქტირებულია',
    updatedMarker: 'განახლდა',
    favoriteAdd: 'რჩეულებში დამატება',
    favoriteRemove: 'რჩეულებიდან მოხსნა',
    openQuestion: 'კითხვის სრულად ნახვა',
    unknownDate: 'თარიღი უცნობია',

    /* Pagination — a forward cursor, so there is no page to jump to. */
    previousPage: 'წინა გვერდი',
    nextPage: 'შემდეგი გვერდი',
    currentPage: 'მიმდინარე გვერდი',
    pageSizeNote: 'ერთ გვერდზე 20 კითხვა',

    /* States. */
    emptyHeading: 'კითხვები ჯერ არ არის',
    emptyBody: 'დასვით პირველი კითხვა და პასუხი აქვე გამოჩნდება.',
    noResultsHeading: 'შედეგი ვერ მოიძებნა',
    noResultsBody: 'შეცვალეთ საძიებო ტექსტი ან ფილტრები.',
    favoritesEmptyHeading: 'რჩეული ჯერ არ არის',
    favoritesEmptyBody: 'მონიშნეთ კითხვა ვარსკვლავით და აქ იპოვით.',

    /* Asking. */
    askAction: 'კითხვის დასმა',
    askHeading: 'ახალი კითხვა',
    askCourseLabel: 'რომელ კურსზე',
    askTextLabel: 'თქვენი კითხვა',
    askCounterLabel: 'სიმბოლო',
    askSubmit: 'კითხვის გაგზავნა',
    askSubmitting: 'იგზავნება…',
    askCancel: 'გაუქმება',
    askTooLong: 'კითხვა დასაშვებზე გრძელია.',
    /**
     * *(T074)* The completed state of asking — `SCREEN-MAP.md` and `ACCEPTANCE.md` 12.
     *
     * Until this line existed, a successful submission only closed the sheet: a sighted reader could
     * infer the outcome from a new row appearing at the top of a newest-first list, and a screen
     * reader user was told nothing at all. It is announced after the server has accepted the
     * question and never for a refusal. It states what happened and claims nothing about when an
     * answer will arrive.
     */
    askSuccess: 'კითხვა გაიგზავნა.',

    /*
     * *(PRD-06)* The context rail — the region beside the list and beside the conversation.
     *
     * Two cards on the list (the question right the server reports, and the course and group the feed
     * is being read in) and one on the conversation (the selected question's own facts). The headings
     * are the rail's, so `Panel`'s generic „section" wording never stands in for them.
     */
    contextLabel: 'კონტექსტი',
    courseContextHeading: 'კურსი და ჯგუფი',
    questionContextHeading: 'ამ კითხვის შესახებ',
    favoritesHeading: 'რჩეული კითხვები',
    groupLabel: 'ჯგუფი',

    /* One question, its answer and its deletion. */
    questionHeading: 'კითხვა',
    answerHeading: 'პასუხი',
    /** *(PRD-06)* The conversation region's own name; the heading above is the document's. */
    conversationLabel: 'არჩეული კითხვა',
    /**
     * *(PRD-06)* The waiting card of the approved conversation.
     *
     * It states what is true — the question reached the queue — and nothing about when an answer will
     * arrive. The prototype's „პასუხის მომზადებისას შეტყობინებას აქვე ნახავთ" is an excluded delivery
     * promise (no notification capability exists; FR-AUTH-05 / FR-SQA-05 forbid one), so the body
     * stays the neutral sentence this surface already used.
     */
    waitingHeading: 'კითხვა მიღებულია',
    waitingBody: 'პასუხი ჯერ არ არის გამოქვეყნებული.',
    /** *(PRD-06)* Who a published answer is read under — the product identity, never a person. */
    answerAuthor: 'Mr. Nutrition-ის გუნდი',
    /** The author line of an own question, where the server sends no display name (own scope). */
    authorSelf: 'თქვენი კითხვა',
    backToList: 'სიაში დაბრუნება',
    askedAt: 'დასმულია',
    authorLabel: 'ავტორი',
    answeredAtLabel: 'პასუხის თარიღი',
    updatedAtLabel: 'განახლების თარიღი',
    deleteAction: 'კითხვის წაშლა',
    deleteHeading: 'კითხვის წაშლა',
    deleteBody: 'კითხვა თქვენი სიიდან გაქრება. პასუხგაცემულ კითხვას ვეღარ წაშლით.',
    deleteConfirm: 'დიახ, წაიშალოს',
    deleted: 'კითხვა წაშლილია.',

    /**
     * The Claude Design Student Q&A page (`design/app/student-qa`): the design's own words for the
     * lists and texts the adapter builds from real data (OD-SQ1…SQ18); everything else on the page is
     * the generated design text.
     */
    design: Object.freeze({
      selfPaced: 'თვითტემპი',
      scopeMine: 'ჩემი',
      scopeGroup: 'სრული ჯგუფი',
      scopeFav: 'რჩეულები',
      filterAria: 'ფილტრი',
      allCourses: 'ყველა კურსი',
      courseAria: 'კურსი',
      page: 'გვერდი',
      pageTotal: 'სულ',
      questions: 'კითხვა',
      showQuestions: 'ნახე',
      found: 'ნაპოვნია',
      questionAria: 'კითხვა',
      favoriteGoAria: 'რჩეულზე გადასვლა',
      authorSelf: 'შენი კითხვა',
      factAsked: 'დაისვა',
      factAnswer: 'პასუხი',
      factNoAnswer: 'ჯერ არ არის',
      factUpdated: 'განახლდა',
      factQuestion: 'კითხვა',
      factEdited: 'რედაქტირებულია',
      factAuthor: 'ავტორი',
      factAuthorSelf: 'შენ',
      rightNone: 'აქტიური უფლება არ არის',
      rightUntilPrefix: 'კითხვების უფლება',
      rightUntilSuffix: '-მდე',
      rightExpired: 'ვადა ამოიწურა',
      askOffPrefix: 'კითხვის დასმა გამორთულია — უფლების ვადა ამოიწურა',
      askOffSuffix: '. უკვე გაცემული პასუხები კითხვადი რჩება.',
      askEmpty: 'კითხვის ტექსტი სავალდებულოა.',
      askTooLong: 'კითხვა 3000 სიმბოლოს აჭარბებს — შეამოკლე.',
      askRefusedNone: 'ამ კურსზე კითხვების აქტიური უფლება არ გაქვს.',
      askRefusedExpired: 'კითხვების უფლება ამოიწურა {date}-ს. დაუკავშირდი ადმინისტრატორს გასაგრძელებლად.',
      askRefusedLimit: 'დღიური ლიმიტი ამოწურულია — შემდეგი კითხვა ხელმისაწვდომია {date}-დან.',
      askNetwork: 'გაგზავნა ვერ მოხერხდა — კავშირი შეწყდა. სცადე ხელახლა.',
      /** Not drawn (N10): the ask form opened before the courses and the account arrived. */
      askRightPending: 'კითხვის უფლება მოწმდება…',
      askCoursePending: 'კურსის მონაცემები ჯერ არ ჩატვირთულა — სცადე ცოტა ხანში.',
      asked: 'კითხვა გაიგზავნა — პასუხს ელოდება',
      deleted: 'კითხვა წაიშალა',
      favoriteAdded: 'რჩეულებში დაემატა',
      favoriteRemoved: 'რჩეულებიდან ამოღებულია',
      /** Not drawn: a refused favourite toggle is reported with the design's error toast. */
      favoriteFailed: 'რჩეული ვერ შეიცვალა — სცადე ხელახლა.',
      undone: 'მოქმედება დაბრუნდა',
      /*
       * Continuing education (CE-5…CE-7): the platform tab of a member within term, the technical
       * group's name, another member's name on a CE question, and the drawn ended-access refusals.
       */
      scopeAll: 'ყველა კითხვა',
      noGroup: 'ჯგუფის გარეშე',
      continuing: 'განგრძობითი სწავლება',
      authorPrefix: 'ავტორი —',
      askRefusedCeExpired: 'წვდომა დასრულდა — კითხვის დასმა გამორთულია.',
      askOffCeTitle: 'კითხვის დასმა გამორთულია — წვდომა დასრულდა {date}',
      /* The mobile „+“ pressed while asking is off: the drawn notice sheet (export 10). */
      askNoticeExpiredTitle: 'კითხვების უფლება ამოიწურა',
      askNoticeCeTitle: 'წვდომა დასრულდა',
      askNoticeCeBody: 'წვდომა დასრულდა — საერთო ბაზა დახურულია. ხელმისაწვდომი რჩება მხოლოდ შენი კითხვები და პასუხები.',
    }),
  }),

  admin: Object.freeze({
    navLabel: 'ადმინისტრირება',
    heading: 'დაშვებისა და სტუდენტების მართვა',
    intro: 'განიხილეთ შესვლის მოთხოვნები, მოამზადეთ წინასწარი მიწვევები და მართეთ სტუდენტების ანგარიშები.',
    sectionsLabel: 'ადმინისტრირების სექციები',
    checkingAccess: 'ადმინისტრატორის წვდომა მოწმდება…',
    signInBody: 'ადმინისტრირების გვერდის სანახავად ჯერ შედით სისტემაში.',
    forbiddenHeading: 'გვერდი მიუწვდომელია',
    forbiddenBody: 'ამ გვერდის გახსნა მხოლოდ ადმინისტრატორს შეუძლია.',
    filters: 'ფილტრები',
    applyFilters: 'გაფილტვრა',
    clearFilters: 'გასუფთავება',
    refresh: 'განახლება',
    selectAll: 'ამ გვერდის ყველა ჩანაწერის მონიშვნა',
    selectOne: 'ჩანაწერის მონიშვნა',
    previousPage: 'წინა გვერდი',
    nextPage: 'შემდეგი გვერდი',
    currentPage: 'მიმდინარე გვერდი',
    pageSizeNote: 'ერთ გვერდზე 20 ჩანაწერი',
    noMorePages: 'მეტი გვერდი არ არის.',
    operationResult: 'მოქმედების შედეგი',
    previewAction: 'დაგეგმილი მოქმედება',
    previewRecords: 'მონიშნული ჩანაწერები',
    applied: 'შესრულდა',
    skipped: 'გამოტოვებულია',
    failed: 'ვერ შესრულდა',
    retryRecord: 'განაახლეთ სია, გადაამოწმეთ ჩანაწერი და სცადეთ ხელახლა.',
    skippedFallback: 'ჩანაწერის მდგომარეობა ამ მოქმედებას არ შეესაბამება.',
    /** Bulk invite (owner, 2026-09-25) — the result of one request for many students. */
    bulkInvite: Object.freeze({
      none: 'დაამატე მინიმუმ ერთი სტუდენტი',
      fix: 'შეასწორე მონიშნული ველები',
      tooMany: 'ერთ ჯერზე მაქსიმუმ 500 სტუდენტი',
      added: 'მოწვეულია',
      duplicates: 'უკვე მოწვეული',
      errors: 'შეცდომა',
      duplicateTag: 'უკვე მოწვეულია',
      failedTag: 'ვერ დაემატა — სცადე ხელახლა',
      pasted: 'სიაში დაემატა',
      pastedRows: 'სტრიქონი',
      pasteEmpty: 'ჩასასმელი სტრიქონი არ არის',
    }),
    /** *(T093)* The narrow band's way into the sections; the rail itself is the desktop's. */
    sectionsMenu: 'სექციები',
    batchReasons: Object.freeze({
      REQUEST_NOT_FOUND: 'მოთხოვნა ვერ მოიძებნა.',
      ACCOUNT_NOT_FOUND: 'ანგარიში ვერ მოიძებნა.',
      ACCOUNT_DEACTIVATED: 'ანგარიში დეაქტივირებულია.',
      ACCOUNT_NOT_ACTIVE: 'ანგარიში აქტიურ მდგომარეობაში არ არის.',
      ACCOUNT_NOT_DEACTIVATED: 'ანგარიში დეაქტივირებული არ არის.',
      ACCOUNT_ALREADY_APPROVED: 'ანგარიში უკვე დასტურებულია.',
      ALREADY_APPROVED: 'მოთხოვნა უკვე დასტურებულია.',
      ALREADY_REJECTED: 'მოთხოვნა უკვე უარყოფილია.',
      ALREADY_DEACTIVATED: 'ანგარიში უკვე დეაქტივირებულია.',
      ADMIN_ACCOUNT: 'ადმინისტრატორის ანგარიშის შეცვლა აქედან არ შეიძლება.',
      EXACT_DUPLICATE: 'ზუსტად ასეთი ღია მიწვევა უკვე არსებობს.',
      INVITE_HISTORY_EXISTS: 'ამ ელფოსტისა და ჯგუფის ისტორია უკვე არსებობს.',
      SAME_COURSE_OPEN_INVITE: 'ამავე კურსზე ღია მიწვევა უკვე არსებობს.',
      ACTIVE_ENROLLMENT_EXISTS: 'სტუდენტს ამ კურსზე აქტიური ჩარიცხვა უკვე აქვს.',
      ACCOUNT_EXISTS_USE_ENROLLMENT: 'ანგარიში უკვე არსებობს; გამოიყენეთ ჩარიცხვის მართვა.',
      PENDING_REQUEST_NOT_FOUND: 'განხილვაში მყოფი მოთხოვნა ვერ მოიძებნა.',
      GROUP_NOT_FOUND: 'ჯგუფი ვერ მოიძებნა.',
      GROUP_ARCHIVED: 'ჯგუფი დაარქივებულია.',
      GROUP_INVALID: 'ჯგუფი მითითებულ კურსს არ შეესაბამება.',
      COURSE_NOT_FOUND: 'კურსი ვერ მოიძებნა.',
      COURSE_ARCHIVED: 'კურსი დაარქივებულია.',
      COURSE_DRAFT: 'კურსი ჯერ დრაფტია — ჩარიცხვა გააქტიურების შემდეგ შეიძლება.',
      INVITE_NOT_FOUND: 'მიწვევა ვერ მოიძებნა.',
      IMMUTABLE_CONSUMED: 'გამოყენებული მიწვევა ისტორიაა და არ იცვლება.',
      CONFLICT_REQUIRES_RETARGET: 'კონფლიქტურ მიწვევას ახალი ჯგუფი სჭირდება.',
      NO_CHANGES: 'შესანახი ცვლილება არ არის.',
      TARGET_EXISTS: 'არჩეულ ჯგუფში ამ მიწვევის ჩანაწერი უკვე არსებობს.',
      /* The Q&A workspace's own per-object reasons (T049): the server's stable codes, in Georgian. */
      QUESTION_NOT_FOUND: 'კითხვა ვერ მოიძებნა.',
      QUESTION_DELETED: 'კითხვა წაშლილია.',
      ALREADY_DELETED: 'კითხვა უკვე წაშლილია.',
      NOT_DELETED: 'კითხვა წაშლილი არ არის.',
      PURGE_STARTED: 'სამუდამო წაშლა უკვე დაწყებულია — აღდგენა აღარ შეიძლება.',
      INVALID_TRANSITION: 'კითხვის მიმდინარე მდგომარეობა ამ მოქმედებას არ უშვებს.',
      INVALID_STATUS: 'კითხვის მიმდინარე სტატუსი ამ მოქმედებას არ უშვებს.',
      AUTHOR_INACTIVE: 'კითხვის ავტორი აქტიური არ არის.',
      DRAFT_EXISTS: 'მონახაზი უკვე არსებობს; გამოიყენეთ ხელახლა მომზადება.',
      DRAFT_MISSING: 'მონახაზი არ არსებობს.',
      DRAFT_EMPTY: 'მონახაზი ცარიელია.',
      DRAFT_STALE: 'მონახაზი მოძველებულია; საჭიროა ხელახლა მომზადება ან ცხადი დადასტურება.',
      ACTIVE_LEASE: 'ამ კითხვაზე მომზადება უკვე მიმდინარეობს.',
      LEASE_NOT_OWNED: 'მომზადება სხვა გაშვებას ეკუთვნის.',
      ATTEMPT_CAP_REACHED: 'მცდელობების ზღვარი ამოწურულია; საჭიროა ხელით მომზადება.',
      PARENT_TRASHED: 'კითხვის კურსი ან ჯგუფი სანაგვეშია.',
      PUBLICATION_MISSING: 'გამოქვეყნებული პასუხი არ არსებობს.',
      VERSION_CONFLICT: 'ვერსია შეიცვალა; განაახლეთ და სცადეთ ხელახლა.',
      /*
       * The enrolment and entitlement reasons (T056 WP4).
       *
       * `NO_CHANGE` is the entitlement contract's own singular code and stays distinct from the
       * invitation panel's `NO_CHANGES` above: one says the computed end equals the stored one, the
       * other says the submitted form changed no field.
       */
      ACCOUNT_PENDING: 'ანგარიშის მოთხოვნა ჯერ განხილვაშია.',
      ACCOUNT_REJECTED: 'ანგარიშის მოთხოვნა უარყოფილია.',
      ACCOUNT_NOT_APPROVED: 'ანგარიში დადასტურებული არ არის.',
      ALREADY_IN_TARGET_GROUP: 'სტუდენტი უკვე ამ ჯგუფშია ჩარიცხული.',
      SAME_COURSE_USE_TRANSFER: 'ამავე კურსზე აქტიური ჩარიცხვა უკვე არსებობს; გამოიყენეთ გადაყვანა.',
      ENROLLMENT_EXISTS_CLOSED: 'ამ კურსზე დახურული ჩარიცხვა უკვე არსებობს.',
      ENROLLMENT_CLOSED: 'ჩარიცხვა დახურულია.',
      ALREADY_CLOSED: 'ჩარიცხვა უკვე დახურულია.',
      NOT_FOUND: 'ჩანაწერი ვერ მოიძებნა.',
      RESULT_IN_PAST: 'მიღებული თარიღი წარსულშია; მიუთითეთ ზუსტი თარიღი.',
      NO_CHANGE: 'თარიღი არ იცვლება.',
      /* *(AR-09)* The template propagation's own per-object reason. */
      GROUP_STARTED: 'ჯგუფი უკვე დაწყებულია, ამიტომ ნაგულისხმევი განრიგი არ იცვლება.',
    }),
    /*
     * *(AR-12 correction)* What a surface says when it cannot name a record.
     *
     * The owner decision removes stored identifiers from the interface, and the honest replacement for
     * a name the client does not have is a sentence — never the identifier the reader cannot act on
     * and never an empty cell that reads as "this record has no group".
     */
    unnamedCourse: 'კურსი დაუდგენელია',
    unnamedGroup: 'ჯგუფი დაუდგენელია',
    unnamedRecord: 'ჩანაწერი დაუდგენელია',
    closeResult: 'შედეგის დახურვა',
    retry: 'ხელახლა ცდა',
    required: 'სავალდებულოა',
    notProvided: 'არ არის მითითებული',
    save: 'შენახვა',
    saving: 'ინახება…',
    cancel: 'გაუქმება',
    edit: 'რედაქტირება',
    details: 'დეტალები',
    statusLabel: 'მდგომარეობა',
    createdAt: 'შექმნის დრო',
    updatedAt: 'განახლების დრო',
    email: 'ელფოსტა',
    firstName: 'სახელი',
    lastName: 'გვარი',
    phone: 'ტელეფონი',
    courseId: 'კურსის იდენტიფიკატორი',
    groupId: 'ჯგუფის იდენტიფიკატორი',
    allStatuses: 'ყველა მდგომარეობა',
    statuses: Object.freeze({
      pending: 'განხილვაშია',
      approved: 'დადასტურებულია',
      rejected: 'უარყოფილია',
      deactivated: 'დეაქტივირებულია',
      open: 'ღიაა',
      consumed: 'გამოყენებულია',
      conflict: 'კონფლიქტია',
    }),
    requests: Object.freeze({
      navLabel: 'მოთხოვნები',
      heading: 'შესვლის მოთხოვნები',
      intro: 'განიხილეთ ახალი მოთხოვნები და გამოიყენეთ ჯგუფური დასტური ან უარყოფა.',
      tableLabel: 'შესვლის მოთხოვნების ცხრილი',
      applicant: 'მომთხოვნი',
      emptyHeading: 'შესაბამისი მოთხოვნები არ არის',
      emptyBody: 'შეცვალეთ ფილტრი ან მოგვიანებით განაახლეთ სია.',
      approve: 'დადასტურება',
      reject: 'უარყოფა',
      approveHeading: 'მოთხოვნების დადასტურება',
      approveBody: 'მონიშნული ანგარიშები მიიღებენ პლატფორმაზე შესვლის უფლებას. კურსზე ჩარიცხვა ცალკე იმართება.',
      rejectHeading: 'მოთხოვნების უარყოფა',
      rejectBody: 'მონიშნული ანგარიშები ვერ შევლენ სასწავლო სივრცეში. გადაწყვეტილების შეცვლა მოგვიანებით შესაძლებელია.',
      approveConfirm: 'მოთხოვნების დადასტურება',
      rejectConfirm: 'მოთხოვნების უარყოფა',
      /* *(AR-11)* The routed master-detail workspace. */
      listLabel: 'მოთხოვნების სია',
      detailLabel: 'არჩეული მოთხოვნა',
      backToList: 'მოთხოვნების სიაში დაბრუნება',
      chooseRequest: 'აირჩიეთ მოთხოვნა სიიდან, რომ ნახოთ დეტალები.',
      decideOne: 'გადაწყვეტილება',
      decidedAt: 'გადაწყვეტილების დრო',
    }),
    preInvites: Object.freeze({
      navLabel: 'წინასწარი მიწვევები',
      heading: 'წინასწარი მიწვევები',
      intro: 'დაამატეთ სტუდენტები ჯგუფებში ელფოსტის ზუსტი დამთხვევით და აკონტროლეთ გამოყენებული ან კონფლიქტური ჩანაწერები.',
      intakeHeading: 'მიწვევების ჯგუფურად დამატება',
      /* *(ledger 7)* The grid replaces the pasted list: the operator fills cells, not a syntax. */
      intakeBody: 'აირჩიეთ კურსი და ჯგუფი, შემდეგ შეავსეთ ცხრილი. სახელი, გვარი და ტელეფონი არასავალდებულოა.',
      gridLabel: 'ახალი მიწვევების ცხრილი',
      gridRow: 'ჩანაწერი',
      addRow: 'ხაზის დამატება',
      removeRow: 'ხაზის წაშლა',
      rowsRemaining: 'დარჩენილია {count} ხაზი',
      rowsFull: 'ერთდროულად მაქსიმუმ {max} ადამიანის დამატება შეიძლება.',
      nothingToCreate: 'შეავსეთ სულ მცირე ერთი ხაზი.',
      /* *(AR-12)* One batch, one group — chosen by name rather than typed as an identifier. */
      targetGroup: 'ჯგუფი, რომელშიც ემატება',
      targetGroupRequired: 'ჯერ აირჩიეთ კურსი და ჯგუფი, რომელშიც მიწვევები დაემატება.',
      create: 'მიწვევების დამატება',
      creating: 'ემატება…',
      parseHeading: 'შეასწორეთ მონიშნული ველები',
      invalidEmail: 'ელფოსტის ფორმატი არასწორია.',
      fieldTooLong: 'ველი დასაშვებზე გრძელია.',
      duplicateRow: 'იგივე ელფოსტა ზემოთ უკვე ჩამოთვლილია; დატოვეთ მხოლოდ ერთი ხაზი.',
      tableLabel: 'წინასწარი მიწვევების ცხრილი',
      emptyHeading: 'შესაბამისი მიწვევები არ არის',
      emptyBody: 'შეცვალეთ ფილტრი ან დაამატეთ ახალი მიწვევები ზემოთ მოცემული ფორმით.',
      searchEmail: 'ელფოსტით ძიება',
      delete: 'მიწვევების წაშლა',
      deleteHeading: 'მიწვევების წაშლა',
      deleteBody: 'წაიშლება მხოლოდ მონიშნული ღია ან კონფლიქტური მიწვევები. გამოყენებული ისტორია უცვლელი დარჩება.',
      deleteConfirm: 'მიწვევების წაშლა',
      editHeading: 'მიწვევის რედაქტირება',
      editBody: 'გამოყენებული მიწვევის შეცვლა შეუძლებელია.',
      update: 'ცვლილებების შენახვა',
    }),
    students: Object.freeze({
      navLabel: 'სტუდენტები',
      heading: 'სტუდენტების დირექტორია',
      intro: 'მოძებნეთ სტუდენტი, განაახლეთ შიდა პროფილი და მართეთ ანგარიშის აქტიური მდგომარეობა მონაცემების დაკარგვის გარეშე.',
      search: 'სახელით ან ელფოსტით ძიება',
      tableLabel: 'სტუდენტების ცხრილი',
      student: 'სტუდენტი',
      enrollments: 'ჩარიცხვები',
      questions: 'კითხვები',
      emptyHeading: 'შესაბამისი სტუდენტები არ არის',
      emptyBody: 'შეცვალეთ ფილტრები ან გადაამოწმეთ საძიებო ტექსტი.',
      /* *(AR-11, AR-12)* The routed workspace and its name-based selectors. */
      listLabel: 'სტუდენტების სია',
      detailLabel: 'არჩეული სტუდენტი',
      backToList: 'სტუდენტების სიაში დაბრუნება',
      courseSelector: 'კურსი',
      groupSelector: 'ჯგუფი',
      groupNeedsCourse: 'ჯერ აირჩიეთ კურსი, რომ ჯგუფი დაზუსტდეს.',
      allCourses: 'ყველა კურსი',
      allGroups: 'ყველა ჯგუფი',
      deactivate: 'დეაქტივაცია',
      reactivate: 'რეაქტივაცია',
      deactivateHeading: 'ანგარიშების დეაქტივაცია',
      deactivateBody: 'მონიშნული სტუდენტები ვეღარ შევლენ პლატფორმაზე. მათი პროფილი, ჩარიცხვები და კითხვები შენარჩუნდება.',
      reactivateHeading: 'ანგარიშების რეაქტივაცია',
      reactivateBody: 'მონიშნულ სტუდენტებს პლატფორმაზე წვდომა აღუდგებათ არსებული მონაცემებით.',
      deactivateConfirm: 'ანგარიშების დეაქტივაცია',
      reactivateConfirm: 'ანგარიშების რეაქტივაცია',
      chooseStudent: 'დეტალების სანახავად აირჩიეთ სტუდენტი.',
      profileHeading: 'შიდა პროფილი',
      accessHeading: 'წვდომა და ჩარიცხვები',
      questionsHeading: 'კითხვების მდგომარეობა',
      totalQuestions: 'სულ',
      activeQuestions: 'აქტიური',
      deletedQuestions: 'წაშლილი',
      noEnrollments: 'ჩარიცხვები არ არის.',
      openedAt: 'წვდომა გაიხსნა',
      entitlementEnd: 'კითხვის ვადა',
      /* *(ledger 9)* The group facts an access record shows beside the two dates above. */
      groupPeriod: 'ჯგუფის პერიოდი',
      lectureDayTime: 'ლექციის დღე და საათი',
      /* *(ledger 11)* The scoped question summary. */
      questionsByScope: 'კითხვები კურსისა და ჯგუფის მიხედვით',
      questionsByScopeEmpty: 'ამ სტუდენტს ჯერ არცერთი კითხვა არ დაუსვამს.',
      askedInScope: 'დასმული კითხვა',
      /* *(ledger 15)* The two columns the directory gained. */
      currentCourse: 'მიმდინარე კურსი',
      currentGroup: 'ჯგუფი',
      recordDeletion: 'წაშლის მოთხოვნის დაფიქსირება',
      deletionHeading: 'წაშლის მოთხოვნის დაფიქსირება',
      deletionBody: 'ეს მოქმედება მხოლოდ ისტორიულ მოთხოვნას შექმნის. ანგარიში და მონაცემები ახლა არ წაიშლება.',
      deletionConfirm: 'მოთხოვნის დაფიქსირება',
      deletionRecorded: 'წაშლის მოთხოვნა დაფიქსირდა.',
      profileSaved: 'პროფილი განახლდა.',

      /*
       * *(Claude Design Students screen, `design/app/admin-students`)* The words the owner's design
       * (`Simple-QA-Admin-Students-{Desktop,Mobile}`) says in places its adapter composes itself from
       * real data — counts, issue lines, the activity feed (OD-S7), the sheets' previews and results,
       * toasts and field errors — quoted from the design's own logic so the screen says exactly what
       * was drawn. Nothing is sent to a student (OD-D2.2).
       */
      design: Object.freeze({
        studentsCount: 'სტუდენტი',
        activeCount: 'აქტიური',
        searching: 'იძებნება…',
        allCourses: 'ყველა კურსი',
        courseChipAria: 'კურსი',
        courseChip: 'კურსი:',
        goToStudent: 'სტუდენტზე გადასვლა',
        emptySearchTitle: 'ამ ძებნით სტუდენტი არ მოიძებნა',
        emptySearchBody: 'სცადე სახელი, ელფოსტა ან ტელეფონი.',
        emptySearchAct: 'ძებნის გასუფთავება',
        emptyTitle: 'სტუდენტი ჯერ არ არის',
        emptyBody: 'მოიწვიე პირველი სტუდენტი — ის დაემატება, როცა ამ ელფოსტით პირველად შევა Google-ით.',
        emptyAct: 'სტუდენტის მოწვევა',
        closedShort: 'დაიხურა',
        since: '-დან',
        until: '-მდე',
        accessWord: 'წვდომა',
        groupWord: 'ჯგუფი',
        issueInvitePrefix: 'წინასწარი მოწვევა მოლოდინშია',
        issueInviteTail: 'დაემატება პირველი Google შესვლისას',
        issueSuspendedPrefix: 'ანგარიში შეჩერებულია',
        issueSuspendedTail: 'შესვლისას სტუდენტი მხოლოდ შეჩერების ეკრანს ხედავს',
        issueAllExpired: 'ყველა ჩარიცხვის ვადა ამოიწურა',
        issueEndingPrefix: 'წვდომა',
        issueEndingSuffix: '-ს იწურება',
        actEditInvite: 'მოწვევის რედაქტირება',
        actReactivate: 'ანგარიშის აღდგენა',
        actExtend: 'წვდომის გაგრძელება',
        tlInviteCreated: 'მოწვევა შეიქმნა',
        tlInviteUsed: 'მოწვევა გამოყენებულია',
        tlOpened: 'ჩარიცხვა გაიხსნა',
        tlClosed: 'ჩარიცხვა დაიხურა',
        tlSuspended: 'ანგარიში შეჩერდა',
        tlReactivated: 'ანგარიში აღდგა',
        tlApproved: 'ანგარიში დადასტურდა',
        tlCreated: 'ანგარიში შეიქმნა — პირველი Google შესვლა',
        fieldCourse: 'კურსი',
        fieldGroup: 'ჯგუფი',
        fieldExtUnit: 'გაგრძელება',
        waitCourse: 'ჯერ აირჩიე კურსი',
        coursesEmpty: 'აქტიური კურსი ჯერ არ არის',
        groupsEmpty: 'ამ კურსზე აქტიური ჯგუფი არ არის',
        groupsEmptyOther: 'ამ კურსზე სხვა აქტიური ჯგუფი არ არის',
        addEnrolHint: 'წვდომის დასრულება ჯგუფიდან მოდის',
        transferHintPrefix: 'კურსის აქტიური ჯგუფები · მიმდინარე',
        transferHintSuffix: 'არ ჩანს',
        skipInvited: 'წინასწარი მოწვევა მოლოდინშია',
        skipAlreadySuspended: 'ანგარიში უკვე შეჩერებულია',
        skipSuspended: 'ანგარიში შეჩერებულია',
        skipNoLiveEnrolment: 'ამ კურსზე აქტიური ჩარიცხვა არ აქვს',
        skipSameGroup: 'უკვე ამ ჯგუფშია',
        skipAlreadyEnrolled: 'ამ კურსზე უკვე ჩარიცხულია',
        skipNothingToExtend: 'გასაგრძელებელი ჩარიცხვა არ არის',
        transferKeeps: 'პროგრესი და წვდომის ვადა შენარჩუნდება',
        willEnrol: 'ჩაირიცხება',
        willSuspend: 'ანგარიში შეჩერდება — ჩარიცხვები უცვლელი რჩება',
        tagTransfer: 'გადავა',
        tagEnrol: 'ჩაირიცხება',
        tagSuspend: 'შეჩერდება',
        tagExtend: 'გაგრძელდება',
        tagWillSkip: 'გამოტოვდება',
        tagDone: 'შესრულდა',
        tagSkipped: 'გამოტოვდა',
        tagFailed: 'ვერ შესრულდა',
        transferredPrefix: 'გადაყვანილია',
        transferredSuffix: '-ში',
        enrolled: 'ჩაირიცხა',
        suspendedDone: 'ანგარიში შეჩერებულია — ჩარიცხვები შენარჩუნდა',
        failedWhy: 'ვერ შესრულდა — სცადე ხელახლა',
        extCurrent: 'მიმდინარე',
        extExpiredMark: '(ვადაგასული)',
        extNew: 'ახალი',
        extTooEarly: 'ახალი თარიღი მიმდინარე ვადაზე ადრეა',
        unitMonths: 'თვე',
        unitWeeks: 'კვირა',
        unitExact: 'ზუსტი თარიღი',
        exactRequired: 'შეიყვანე ახალი დასრულების თარიღი',
        exactFormat: 'თარიღის ფორმატი: დდ.თთ.წწ — მაგ. 31.12.26',
        exactAfter: 'თარიღი',
        exactAfterSuffix: '-ზე გვიან უნდა იყოს',
        exactDesc: 'ახალი დასრულების თარიღი იქნება',
        amountRange: 'შეიყვანე რიცხვი 1-დან',
        amountDesc: 'წვდომა გაგრძელდება',
        byWeeks: 'კვირით',
        byMonths: 'თვით',
        amountExpired: 'ვადაგასულ წვდომას ათვლა',
        amountExpiredSuffix: '-დან დაეწყება',
        extHold: 'შეავსე გაგრძელების პარამეტრი — თითოეული ჩარიცხვის მიმდინარე და ახალი ვადა აქ გამოჩნდება.',
        extOthersUnchanged: 'სხვა ჩარიცხვები უცვლელი რჩება.',
        pickedOf: 'მონიშნული სტუდენტიდან',
        bulkExtTail: 'დააკმაყოფილებს პირობას — გამოტოვებული ჩანაწერები მიზეზით ჩანს ქვემოთ.',
        bulkTransMiddle: 'გადავა ჯგუფში',
        bulkTransTail: 'გამოტოვებული სტუდენტები მიზეზით ჩანს ქვემოთ.',
        bulkEnrolTail: 'არსებული ჩარიცხვები უცვლელი რჩება.',
        bulkSuspendMiddle: '-ს შეუჩერდება ანგარიში.',
        bulkSuspendTail: 'ეს ეხება მხოლოდ ანგარიშის მდგომარეობას — ჩარიცხვები და წვდომის ვადები უცვლელი რჩება. სტუდენტები კვლავ შეძლებენ Google-ით შესვლას, მაგრამ დაინახავენ მხოლოდ შეჩერებული ანგარიშის ეკრანს.',
        transferBodyDesktop: 'არჩეული ჩარიცხვა გადავა ჯგუფში',
        transferBodyMobile: 'ჩარიცხვა გადავა ჯგუფში',
        transferBodyTail: 'პროგრესი და წვდომის ვადა შენარჩუნდება.',
        firstRequired: 'სახელი სავალდებულოა',
        lastRequired: 'გვარი სავალდებულოა',
        firstMin: 'სახელი სავალდებულოა — მინიმუმ 2 სიმბოლო',
        lastMin: 'გვარი სავალდებულოა — მინიმუმ 2 სიმბოლო',
        phoneFormat: 'ტელეფონის ფორმატი არასწორია — მაგ. +995 599 12 34 56',
        mailRequired: 'ელფოსტა სავალდებულოა',
        mailFormat: 'ელფოსტის ფორმატი არასწორია',
        mailInvited: 'ამ ელფოსტაზე მოწვევა უკვე მოლოდინშია',
        mailAccount: 'ეს ელფოსტა უკვე რეგისტრირებულ ანგარიშს ეკუთვნის',
        pickCourse: 'აირჩიე კურსი',
        pickGroup: 'აირჩიე ჯგუფი',
        courseInvited: 'ამ კურსზე მოწვევა უკვე მოლოდინშია',
        courseEnrolled: 'სტუდენტი უკვე ჩარიცხულია ამ კურსზე',
        inviteAdded: 'მოწვევა დაემატა',
        pendingWord: 'მოლოდინში',
        inviteSaveFailed: 'მოწვევა ვერ შეინახა',
        enrolAdded: 'ჩარიცხვა დაემატა',
        groupChanged: 'ჯგუფი შეიცვალა',
        partialDone: 'შედეგი მზადაა — ნაწილი გამოტოვდა',
        actionDone: 'მოქმედება შესრულდა',
        accessExtended: 'წვდომა გაგრძელდა',
        suspendedToast: 'ანგარიში შეჩერდა',
        reactivatedToast: 'ანგარიში აღდგა',
        undone: 'მოქმედება დაბრუნდა',
        inviteRevoked: 'მოწვევა გაუქმდა',
        enrolClosed: 'ჩარიცხვა დაიხურა — სხვა ჩარიცხვები უცვლელია',
        studentUpdated: 'სტუდენტის მონაცემები განახლდა',
        saveFailedRetry: 'შენახვა ვერ მოხერხდა — სცადე ხელახლა',
        suspendUndone: 'შეჩერება დაბრუნდა',
        retried: 'ხელახლა დამუშავდა',
        /*
         * *(CE-2, CE-3, CE-10 — export 9)* Continuing education: no group (the fixed field and its hint),
         * the enrolment card's type and 365-day meta line, and the renewal's confirm lines and outcomes.
         */
        ceType: 'განგრძობითი სწავლება',
        ceNoGroup: 'ჯგუფის გარეშე',
        ceAccessHint: 'წვდომა 365 დღე ჩარიცხვიდან',
        ceFixed: 'შეუცვლელი',
        ceAccessDays: '365 დღე',
        dayWord: 'დღე',
        renewCurrent: 'მიმდინარე ვადა',
        renewExpired: 'ამოიწურა',
        renewAdds: 'ემატება მიმდინარე ვადას',
        renewFresh: 'ახალი 365 დღე დღეიდან',
        renewPlus: '+365 დღე',
        tagRenew: 'განახლდება',
        renewed: 'წვდომა განახლდა — ახალი ვადა',
      }),
    }),

    /**
     * The course and group administration panels — T056 (FR-PANEL-01 (3) and (4), R2-04).
     *
     * Three rules shape every line below.
     *
     * **The interface never promises a capability the accepted contract does not have.** There is no
     * unarchive operation, so archiving says so instead of offering a way back; section and material
     * authoring belongs to T059/T062, so the content view says what it is — a view — rather than
     * showing a control that would fail.
     *
     * **A server failure becomes Georgian from a stable code, never from a server string**
     * (`BOUNDARIES.md` § 6). `issues` carries one line per field-issue code
     * `functions/src/curriculum/curriculumPlan.ts` can emit; the three bounded refusals of
     * `curriculumReadPlan.ts` get their own sentences, because "narrow your filters" and "this course
     * is too large to show at all" are different facts and neither is "something went wrong".
     *
     * **The approved R2-04 wording is used where the design supplies it**, normalised from the
     * prototype's informal imperative to this catalogue's polite plural. Course-type wording follows
     * the frozen SPEC (`თვითსწავლებადი`), not the prototype's `დამოუკიდებელი`.
     */
    curriculum: Object.freeze({
      coursesNavLabel: 'კურსები',
      groupsNavLabel: 'ჯგუფები',

      coursesHeading: 'კურსები',
      coursesIntro: 'კურსის შინაარსი, ჯგუფები და პარამეტრები ერთ სივრცეში.',
      courseSearch: 'კურსის ძიება',
      createCourse: 'კურსის შექმნა',
      coursesTableLabel: 'კურსების ცხრილი',
      coursesEmptyHeading: 'შესაბამისი კურსები არ არის',
      coursesEmptyBody: 'შეცვალეთ ფილტრები ან შექმენით პირველი კურსი.',
      courseColumn: 'კურსი',
      manage: 'მართვა',

      groupsHeading: 'ჯგუფები',
      groupsIntro: 'ჯგუფები, აკადემიური განრიგი და კითხვის ნაგულისხმევი ვადები.',
      groupSearchNeedsCourse: 'სიის სანახავად აირჩიეთ ერთი კურსი; ან უბრალოდ აკრიფეთ ჯგუფის სახელი.',
      groupSearch: 'ჯგუფის ძიება',
      createGroup: 'ჯგუფის შექმნა',
      groupsTableLabel: 'ჯგუფების ცხრილი',
      groupsEmptyHeading: 'შესაბამისი ჯგუფები არ არის',
      groupsEmptyBody: 'შეცვალეთ ფილტრები ან შექმენით ჯგუფი.',
      groupColumn: 'ჯგუფი',
      courseGroupsHeading: 'კურსის ჯგუფები',
      academicPeriod: 'აკადემიური პერიოდი',
      needCourseFirst: 'ჯგუფის შესაქმნელად ჯერ საჭიროა მოქმედი კურსი.',

      tabContent: 'შინაარსი',
      tabGroups: 'ჯგუფები',
      tabSettings: 'პარამეტრები',
      tabOverview: 'მიმოხილვა',
      tabSchedule: 'განრიგი და პარამეტრები',
      /* *(AR-08)* The two tabs the Course Studio adds to the three T056 shipped. */
      tabMaterials: 'მასალები',
      tabStudents: 'სტუდენტები',
      courseTabsLabel: 'კურსის სექციები',
      groupTabsLabel: 'ჯგუფის სექციები',
      backToCourses: 'ყველა კურსი',
      backToCourseGroups: 'კურსის ჯგუფებში დაბრუნება',

      typeLabel: 'ტიპი',
      allTypes: 'ყველა ტიპი',
      allCourses: 'ყველა კურსი',
      courseFilterLabel: 'კურსი',
      sortLabel: 'დალაგება',
      types: Object.freeze({
        full: 'სრული კურსი',
        /* The frozen SPEC's term (FR-COURSE-01), not the design prototype's `დამოუკიდებელი კურსი`. */
        self_paced: 'თვითსწავლებადი კურსი',
        /* *(CE-1)* Continuing education, as export 9 names the type. */
        continuing: 'განგრძობითი სწავლება',
      }),
      statuses: Object.freeze({ draft: 'დრაფტი', active: 'მოქმედი', archived: 'არქივში' }),
      /* Derived from the interval the server returned — never a state the server itself reports. */
      lifecycles: Object.freeze({
        archived: 'არქივში',
        self_paced: 'თვითსწავლებადი',
        planned: 'დაგეგმილი',
        current: 'მიმდინარე',
        completed: 'დასრულებული',
      }),
      publication: Object.freeze({ published: 'გამოქვეყნებულია', hidden: 'დამალულია' }),
      sorts: Object.freeze({
        name_asc: 'სახელით: ა–ჰ',
        name_desc: 'სახელით: ჰ–ა',
        createdAt_desc: 'ჯერ ახლად შექმნილი',
        createdAt_asc: 'ჯერ ადრე შექმნილი',
      }),

      createCourseHeading: 'ახალი კურსი',
      courseFormHeading: 'კურსის პარამეტრები',
      courseName: 'კურსის სახელი',
      courseType: 'კურსის ტიპი',
      courseDescription: 'აღწერა',
      /*
       * *(002-course-content-reader T044)* The old cover-address label and its hint are **removed**.
       *
       * They named an administrator-typed internal storage address, and the hint existed only to
       * explain that the file was not uploaded from it and would not open as an image. FR-008/FR-009
       * replace that field with a real protected upload, so both strings described a control that no
       * longer exists — and a message no administrator can be shown is one that should not be kept.
       * The boundary check in `tests/boundaries/content-reader.test.ts` is what keeps the field name
       * itself out of this workspace from now on.
       */

      /* ---------------------------------------------------------------- *
       * *(002-course-content-reader T025)* The protected image upload
       *
       * One vocabulary for every surface that uploads a picture — the course cover, an inline
       * material image and an explanation image — so an administrator meets the same words wherever
       * they are. The refusals name what to do next rather than what went wrong internally.
       * ---------------------------------------------------------------- */

      imageUploadHeading: 'სურათი',
      imageChoose: 'სურათის არჩევა',
      imageReplace: 'სურათის შეცვლა',
      imageRemove: 'სურათის წაშლა',
      imageRetry: 'ხელახლა ცდა',
      imageUploading: 'იტვირთება…',
      imageVerifying: 'მოწმდება…',
      imagePreviewAlt: 'ატვირთული სურათის გადახედვა',
      imageEmpty: 'სურათი არ არის ატვირთული.',
      /** The accepted formats and the cap, stated once where the file picker is. */
      imageHint: 'JPEG, PNG ან WebP, 10 MiB-მდე.',
      imageAlt: 'ალტერნატიული ტექსტი · არასავალდებულო',
      imageAltHint: 'არასავალდებულო. აღწერეთ სურათი მათთვის, ვინც მას ვერ ხედავს.',
      imageCaption: 'წარწერა · არასავალდებულო',
      imageCaptionHint: 'არასავალდებულო. ჩანს სურათის ქვეშ, როგორც საკითხავი ტექსტი.',
      imageWidth: 'სიგანე',
      imageWidthHint: 'ვიწრო ეკრანზე სურათი მაინც სრულ სიგანეს იკავებს.',
      imageInsert: 'სურათის ჩასმა',
      imageUpdate: 'სურათის განახლება',
      /* The three ways an upload can end badly, each with the action that resolves it. */
      imageErrorRejected: 'ფაილი არ მიიღება. აირჩიეთ JPEG, PNG ან WebP 10 MiB-მდე.',
      imageErrorTransfer: 'ატვირთვა შეწყდა. სცადეთ ხელახლა.',
      imageErrorUnusable: 'ფაილი დაზიანებულია ან სურათი არ არის. აირჩიეთ სხვა ფაილი.',
      /** The read side: the pictures of a document that could not be opened. */
      imageUnavailable: 'სურათები ვერ ჩაიტვირთა.',

      saveCourse: 'კურსის შენახვა',
      courseCreated: 'კურსი შეიქმნა.',
      courseSaved: 'კურსის პარამეტრები შენახულია.',
      typeLockedByGroup: 'კურსს უკვე აქვს ჯგუფი, ამიტომ ტიპი აღარ იცვლება.',

      /* ---------------------------------------------------------------- *
       * AR-08 — the Course Studio
       * ---------------------------------------------------------------- */

      /** The searchable course context that sits above every course-owned tab. */
      studioCourseSelector: 'კურსის არჩევა',
      studioCourseSelectorHint: 'აკრიფეთ სახელის ნაწილი და გადაერთეთ სხვა კურსზე.',
      studioGroupCount: 'ჯგუფი',
      studioStudentCount: 'სტუდენტი',
      studioSectionCount: 'სექცია',
      studioMaterialCount: 'მასალა',
      studioOverviewHeading: 'კურსის მიმოხილვა',
      /* ---------------------------------------------------------------- *
       * *(AR-08 correction)* The operational overview.
       *
       * Every line below is a fact the surface has really loaded. There is no completion percentage,
       * no trend and no "health" score: the product measures none of those, and a number nobody can
       * check is worse than no number.
       * ---------------------------------------------------------------- */
      /* *(002-course-content-reader T043)* The cover and the description, as a student meets them. */
      overviewIdentityHeading: 'როგორ გამოიყურება კურსი',
      overviewSummaryHeading: 'სად არის ეს კურსი',
      overviewGroupsLabel: 'ჯგუფები',
      overviewStudentsLabel: 'ჩარიცხული სტუდენტები',
      overviewMaterialsLabel: 'სექციები და მასალები',
      overviewMaterialsValue: '{sections} სექცია · {materials} მასალა',
      overviewScheduleLabel: 'ნაგულისხმევი განრიგი',
      overviewScheduleValue: 'კვირაში {meetings}, ლექცია {minutes} წუთი',
      overviewOpenGroups: 'ჯგუფების ნახვა',
      overviewOpenStudents: 'სტუდენტების ნახვა',
      overviewOpenMaterials: 'მასალების ნახვა',
      overviewOpenSchedule: 'განრიგის შეცვლა',
      overviewNextHeading: 'რას საჭიროებს ეს კურსი',
      overviewNextEmpty: 'დაუსრულებელი აშკარა ნაბიჯი არ ჩანს.',
      overviewNeedsGroup: 'ჯგუფი ჯერ არ არის შექმნილი.',
      overviewNeedsSection: 'სექცია ჯერ არ არის შექმნილი.',
      overviewNeedsPublish: 'გამოუქვეყნებელი სექცია ან მასალა: {count}.',
      overviewNeedsStudents: 'ჩარიცხული სტუდენტი ჯერ არ არის.',
      overviewArchivedNotice: 'კურსი არქივშია: ახალი ჩარიცხვა და ახალი კითხვა აღარ იქმნება.',
      overviewRecentHeading: 'ამ კურსის ბოლო ცვლილებები',
      overviewRecentEmpty: 'ამ კურსზე ჩაწერილი ცვლილება ჯერ არ არის.',
      overviewOpenAudit: 'სრული ისტორია',
      overviewCreatedAt: 'შექმნილია',
      overviewUpdatedAt: 'ბოლო ცვლილება',
      studioStudentsHeading: 'კურსის სტუდენტები',
      studioStudentsIntro: 'ამ კურსზე ჩარიცხული სტუდენტები, ყველა ჯგუფის ჩათვლით.',
      studioStudentsEmptyHeading: 'ჩარიცხული სტუდენტი არ არის',
      studioStudentsEmptyBody: 'ჩარიცხვა ჯგუფის სიიდან იწყება.',
      studioStudentsTableLabel: 'კურსის სტუდენტების ცხრილი',

      /* ---------------------------------------------------------------- *
       * AR-09/AR-10 — the reusable course template
       * ---------------------------------------------------------------- */

      templateHeading: 'ნაგულისხმევი განრიგი',
      templateIntro: 'ეს მნიშვნელობები გამოიყენება ახალი ჯგუფის შექმნისას. არსებული ჯგუფები თავისით არ იცვლება.',
      meetingsPerWeek: 'შეხვედრა კვირაში',
      meetingsPerWeekHint: 'ნაგულისხმევად ერთი; დასაშვებია 1-დან 7-მდე.',
      lectureDuration: 'ლექციის ხანგრძლივობა',
      lectureDurationHint: 'აირჩიეთ ერთ-ერთი დასაშვები ხანგრძლივობა.',
      minutesValue: '{minutes} წუთი',
      /* *(ledger 45/46)* The course's length and its ordered meeting/topic template. */
      courseWeeks: 'კურსის ხანგრძლივობა (კვირა)',
      courseWeeksHint: 'ახალი ჯგუფის დასრულების თარიღი დაწყების თარიღსა და ამ კვირების რაოდენობას ეყრდნობა.',
      courseTopics: 'ლექციების თემები',
      courseTopicsHint: 'ახლა {count} თემაა. ახალი ჯგუფის განრიგი ამ თანმიმდევრობით შედგება.',
      /*
       * *(FR-007)* The numbered rows, and the four things an administrator does to them.
       *
       * Every action names the row it acts on, because "წაშლა" repeated eighteen times is eighteen
       * identical accessible names in one form — a screen reader user would hear the action and never
       * the topic. The number in the name is the meeting number shown beside the row.
       */
      courseTopicRow: 'თემა {number}',
      courseTopicPlaceholder: 'შეხვედრის თემა',
      courseTopicAdd: 'თემის დამატება',
      courseTopicRemove: 'თემა {number}-ის წაშლა',
      courseTopicMoveUp: 'თემა {number}-ის აწევა',
      courseTopicMoveDown: 'თემა {number}-ის ჩამოწევა',
      courseTopicsEmpty: 'თემები ჯერ არ არის დამატებული.',
      /*
       * *(ledger 46, 58)* How many meetings the course's own term contains.
       *
       * Stated beside the topic count so the limit is visible while the syllabus is being written,
       * rather than discovered when the template is saved or, worse, when a group refuses to generate.
       */
      courseTopicsCapacity: 'განრიგში {capacity} შეხვედრა ეტევა: {weeks} კვირა × {meetings} შეხვედრა კვირაში.',
      courseTopicsCapacityUnknown: 'შეხვედრების ტევადობა კვირებისა და კვირეული შეხვედრების რაოდენობიდან გამოითვლება.',
      templateSummary: '{weeks} კვ · {meetings}×{minutes} წთ · {topics} თემა',
      saveTemplate: 'ნაგულისხმევი განრიგის შენახვა',
      templateSaved: 'ნაგულისხმევი განრიგი შენახულია.',
      templateUnit: 'წუთი',
      templateInherited: 'ჯგუფმა შექმნისას გადმოიღო კურსის ნაგულისხმევი განრიგი.',

      propagateHeading: 'ნაგულისხმევი განრიგის გავრცელება',
      propagateIntro: 'გავრცელება ხელით ხდება და მხოლოდ ჯერ დაუწყებელ ჯგუფებზე მოქმედებს.',
      propagatePreview: 'ცვლილების ნახვა',
      propagateConfirm: 'დიახ, გავრცელდეს',
      propagateHeadingConfirm: 'გავრცელდეს ნაგულისხმევი განრიგი?',
      propagateBody: 'აირჩეულ ჯგუფებში ნაგულისხმევი განრიგი შეიცვლება კურსის მიმდინარე მნიშვნელობებით. აქტიური და დასრულებული ჯგუფები არ იცვლება.',
      propagateNoCandidates: 'გასავრცელებელი ჯგუფი არ არის: ყველა ჯგუფი დაწყებულია, დაარქივებულია, ან უკვე იმავე მნიშვნელობებზეა.',
      propagateSelectGroups: 'აირჩიეთ ჯგუფები',
      propagateSearchGroups: 'ჯგუფის ძებნა',
      propagateApplied: 'ნაგულისხმევი განრიგი გავრცელდა.',
      /* Why one group is not offered — the same order the server refuses in. */
      templateAlreadyCurrent: 'უკვე იმავე მნიშვნელობებზეა',
      /*
       * *(AR-09 correction)* What the **server** answered when it was asked what a propagation would
       * do. The interface offers only the groups it expects to be eligible, but the decision is the
       * server's, so the confirmation lists its answer — including a group that stopped being
       * eligible between the offer and the question.
       */
      propagateReviewHeading: 'რას შეცვლის გავრცელება',
      propagateWouldApply: 'შეიცვლება',
      propagateWouldSkip: 'არ შეიცვლება',
      propagateWouldFail: 'ვერ შემოწმდა',
      propagatePreviewEmpty: 'არჩეულ ჯგუფებში არაფერი შეიცვლება.',
      propagateChecking: 'მოწმდება…',
      /** The propagation's own reasons; the shared batch list is written for other operations. */
      templateReasons: Object.freeze({
        GROUP_NOT_FOUND: 'ჯგუფი ვერ მოიძებნა ამ კურსში.',
        GROUP_ARCHIVED: 'ჯგუფი დაარქივებულია.',
        GROUP_STARTED: 'ჯგუფი უკვე დაწყებულია.',
        COURSE_ARCHIVED: 'კურსი დაარქივებულია.',
        COURSE_DRAFT: 'კურსი ჯერ დრაფტია — ჩარიცხვა გააქტიურების შემდეგ შეიძლება.',
        NO_CHANGE: 'უკვე იმავე მნიშვნელობებზეა.',
      }),
      propagateCurrent: 'მიმდინარე',
      propagateNext: 'გახდება',

      archiveCourse: 'არქივში გადატანა',
      archiveCourseHeading: 'კურსის არქივში გადატანა',
      archiveCourseBody: 'არქივი აჩერებს ახალ ჩარიცხვებსა და კითხვებს. არსებული მასალების კითხვა რჩება.',
      archiveCourseConfirm: 'დიახ, გადავიდეს არქივში',
      courseArchived: 'კურსი გადავიდა არქივში.',
      archiveGroup: 'ჯგუფის არქივში გადატანა',
      archiveGroupHeading: 'ჯგუფის არქივში გადატანა',
      archiveGroupBody: 'უკვე მინიჭებული კითხვისა და მასალების წვდომა არ უქმდება. არქივში მყოფ ჯგუფში ახალი ჩარიცხვა აღარ იქმნება.',
      archiveGroupConfirm: 'დიახ, გადავიდეს არქივში',
      groupArchived: 'ჯგუფი გადავიდა არქივში.',
      /*
       * *(OD-7)* Archive is **reversible**, and the interface now says so.
       *
       * The superseded sentence here promised the opposite — "archive is one-way, there is no way
       * back from the interface" — because the frozen contract carried no unarchive operation. The
       * owner amendment adds one (`admin-redesign-addendum.md` § 8.2), so the promise is replaced by
       * the fact rather than left standing beside a control that contradicts it.
       */
      archiveReversible: 'არქივიდან დაბრუნება ნებისმიერ დროს შეიძლება; მონაცემები არ იშლება.',

      /* ---------------------------------------------------------------- *
       * *(OD-7)* The Course and Group lifecycle — `admin-redesign-addendum.md` § 8.
       *
       * Three rules shape every line below.
       *
       * **Archive and Trash are different words for different things.** არქივი pauses a course;
       * წაშლილები is a thirty-day window before permanent removal. Nothing here calls one the other.
       *
       * **Nothing promises more than the server does.** The backup caveat is stated because § 8.5
       * requires it: a purge removes the content from the product, and a rolling managed backup can
       * still hold an inaccessible copy for up to thirty more days. Claiming otherwise would be the
       * one sentence in this catalogue that is untrue.
       *
       * **An Account is never the subject.** Deleting a course removes that course's access records
       * and nothing about the person, which the confirmation says in as many words (§ 8.1).
       * ---------------------------------------------------------------- */

      viewLabel: 'ხედი',
      views: Object.freeze({
        active: 'მოქმედი',
        archived: 'არქივი',
        trash: 'წაშლილები',
        draft: 'დრაფტები',
      }),

      unarchiveCourse: 'არქივიდან დაბრუნება',
      unarchiveCourseHeading: 'კურსის არქივიდან დაბრუნება',
      unarchiveCourseBody: 'კურსი კვლავ გახდება მოქმედი: ახალი ჩარიცხვა და ახალი კითხვა კვლავ დაიშვება.',
      unarchiveCourseConfirm: 'დიახ, დაბრუნდეს',
      courseUnarchived: 'კურსი დაბრუნდა არქივიდან.',
      unarchiveGroup: 'არქივიდან დაბრუნება',
      unarchiveGroupHeading: 'ჯგუფის არქივიდან დაბრუნება',
      unarchiveGroupBody: 'ჯგუფი კვლავ გახდება მოქმედი და ახალი ჩარიცხვა კვლავ დაიშვება.',
      unarchiveGroupConfirm: 'დიახ, დაბრუნდეს',
      groupUnarchived: 'ჯგუფი დაბრუნდა არქივიდან.',

      deleteCourse: 'წაშლა',
      deleteCourseHeading: 'კურსის წაშლა',
      deleteGroup: 'წაშლა',
      deleteGroupHeading: 'ჯგუფის წაშლა',
      deleteConfirm: 'დიახ, წაიშალოს',
      courseDeleted: 'კურსი გადავიდა წაშლილებში.',
      groupDeleted: 'ჯგუფი გადავიდა წაშლილებში.',

      /** What the read-only preview is doing while the server counts. */
      deletePreviewChecking: 'მოწმდება, რა წაიშლება…',
      deletePreviewHeading: 'რა წაიშლება',
      deletePreviewIntro: 'ქვემოთ ჩამოთვლილი ჩანაწერები 30 დღეში სამუდამოდ წაიშლება.',
      deletePreviewApproximate: 'რაოდენობა მინიმალურია: ჩანაწერები დასათვლელად ძალიან ბევრია.',
      /** Every count is a fact the server returned for this exact target. */
      impact: Object.freeze({
        groups: 'ჯგუფი',
        sections: 'სექცია',
        materials: 'მასალა',
        files: 'ფაილი',
        enrollments: 'ჩარიცხვა',
        preInvites: 'მოწვევა',
        questions: 'კითხვა',
      }),
      deleteRestorableFor: '30 დღის განმავლობაში აღდგენა შესაძლებელია.',
      /* § 8.1 — the sentence that keeps course cleanup from being read as account deletion. */
      deleteAccountsSafe: 'სტუდენტების ანგარიშები არ იშლება. იშლება მხოლოდ ამ კურსის წვდომის ჩანაწერები.',
      /* § 8.4 — a course deletion deliberately includes every group, members or not. */
      deleteIncludesGroups: 'წაიშლება კურსის ყველა ჯგუფი, მათ შორის სტუდენტებიანი.',
      /* § 8.5 — the caveat the interface is required to state truthfully. */
      deleteBackupCaveat: 'სარეზერვო ასლებში ასლი კიდევ 30 დღემდე შეიძლება დარჩეს; ის მიუწვდომელია.',
      /* § 8.4 — the one named precondition failure, and the alternative the interface offers. */
      deleteBlockedByStudents: 'ჯგუფში სტუდენტია ჩარიცხული, ამიტომ ცალკე წაშლა არ შეიძლება. გამოიყენეთ არქივი.',

      restore: 'აღდგენა',
      restoreCourseHeading: 'კურსის აღდგენა',
      restoreCourseBody: 'კურსი დაბრუნდება ზუსტად იმ მდგომარეობაში, რომელშიც წაშლამდე იყო.',
      restoreGroupHeading: 'ჯგუფის აღდგენა',
      restoreGroupBody: 'ჯგუფი დაბრუნდება ზუსტად იმ მდგომარეობაში, რომელშიც წაშლამდე იყო.',
      restoreConfirm: 'დიახ, აღდგეს',
      courseRestored: 'კურსი აღდგა.',
      groupRestored: 'ჯგუფი აღდგა.',

      trashColumn: 'წაშლილია',
      trashDueColumn: 'სამუდამოდ წაიშლება',
      trashDaysRemaining: '{days} დღე დარჩა',
      trashDueToday: 'წაიშლება უახლოეს გაწმენდაზე',
      coursesTrashEmptyHeading: 'წაშლილი კურსი არ არის',
      coursesTrashEmptyBody: 'წაშლილი კურსი 30 დღე აქ რჩება და აღდგენა შეიძლება.',
      groupsTrashEmptyHeading: 'წაშლილი ჯგუფი არ არის',
      groupsTrashEmptyBody: 'წაშლილი ჯგუფი 30 დღე აქ რჩება და აღდგენა შეიძლება.',
      coursesArchiveEmptyHeading: 'არქივში კურსი არ არის',
      coursesArchiveEmptyBody: 'არქივში გადატანილი კურსები აქ გამოჩნდება.',
      groupsArchiveEmptyHeading: 'არქივში ჯგუფი არ არის',
      groupsArchiveEmptyBody: 'არქივში გადატანილი ჯგუფები აქ გამოჩნდება.',

      createGroupHeading: 'ახალი ჯგუფი',
      groupFormHeading: 'განრიგი და პარამეტრები',
      groupName: 'ჯგუფის სახელი',
      groupCourse: 'კურსი',
      inheritedFromCourse: 'კურსი და ტიპი ჯგუფს მემკვიდრეობით აქვს და აღარ იცვლება.',
      startDate: 'აკადემიური დასაწყისი · თბილისის დრო',
      startTime: 'დაწყების საათი · თბილისის დრო',
      endDate: 'აკადემიური დასასრული · თბილისის დრო',
      endTime: 'დასრულების საათი · თბილისის დრო',
      extraWeeks: 'კითხვის დამატებითი ვადა · კვირა',
      /*
       * *(ledger 44/45/46/47)* The derived facts the group form states instead of asking for.
       *
       * The copy says the schedule is **drawn up** from the start date and the course's weekly
       * rhythm — never that it was "generated". B11 is the rule: the interface describes the human
       * workflow and the visible state, not the mechanism that produced it, and that rule is the
       * same here as it is for an answer.
       */
      groupWeeks: 'ხანგრძლივობა (კვირა)',
      groupWeeksHint: 'ნაგულისხმევად კურსის ხანგრძლივობა; დასრულების თარიღი აქედან გამოითვლება.',
      derivedEndDate: 'დასრულების თარიღი (გამოთვლილი)',
      derivedEndTime: 'ლექციის დასრულების საათი (გამოთვლილი)',
      derivedEndTimeHint: 'დაწყების საათს დამატებული კურსის ლექციის ხანგრძლივობა.',
      weekdayPrefix: 'დღე',
      generateSchedule: 'განრიგის შედგენა',
      regenerateSchedule: 'განრიგის ხელახლა შედგენა',
      regenerateHeading: 'განრიგის ხელახლა შედგენა',
      regenerateBody: 'არსებული ლექციების თარიღები და საათები ჩანაცვლდება კურსის შაბლონის მიხედვით ხელახლა შედგენილით.',
      regenerateConfirm: 'დიახ, ხელახლა შედგეს',
      scheduleGeneratedHint: 'განრიგი დაწყების თარიღისა და კურსის კვირეული რიტმის მიხედვით შედგა. თითოეული ლექციის საათი ცალკე იცვლება.',
      scheduleNeedsStart: 'შედგენისთვის ჯერ მიუთითეთ დაწყების თარიღი და საათი.',
      months: 'კითხვის ვადა · კალენდარული თვე',
      monthsHint: 'ვადა ითვლება თითოეული სტუდენტის პირადი ჩარიცხვის გახსნიდან.',
      deadlineNotice: 'უკვე გაცემული პირადი კითხვის ვადები უცვლელი რჩება. ნაგულისხმევი ვადის ცვლილება მხოლოდ მომავალ ჩარიცხვებზე იმოქმედებს.',
      saveGroup: 'ჯგუფის შენახვა',
      groupCreated: 'ჯგუფი შეიქმნა.',
      groupSaved: 'ჯგუფის პარამეტრები შენახულია.',

      lectureScheduleHeading: 'ლექციების განრიგი',
      addLecture: 'ლექციის დამატება',
      removeLecture: 'ლექციის წაშლა',
      lectureNumber: 'ლექციის ნომერი',
      lectureDate: 'ლექციის თარიღი · თბილისის დრო',
      lectureTime: 'ლექციის საათი · არასავალდებულო',
      lectureRowLabel: 'ლექცია',
      lecturesEmpty: 'ლექციები ჯერ არ არის. დასაწყებად დაამატეთ ლექციის ნომერი და თარიღი.',
      lectureMaxReached: 'ლექციის ნომრის მაქსიმუმია 1000.',

      overviewHeading: 'ჯგუფის მდგომარეობა',
      academicStart: 'დაწყება',
      /* *(ledger 42, 57)* The two columns the group registers gained. */
      studentCount: 'სტუდენტები',
      lectureDayTime: 'ლექციის დღე/საათი',
      academicEnd: 'დასრულება',
      extraWeeksValue: 'დამატებითი ვადა',
      defaultDeadline: 'კითხვის ნაგულისხმევი ვადა',
      /* *(ledger 59)* What a group overview reports about its own schedule and its members. */
      lecturesHeld: 'ჩატარებული ლექცია',
      selfPacedOverview: 'თვითტემპიანი კურსი — საერთო ვადა და ლექციების განრიგი არ აქვს.',
      lecturesRemaining: 'დარჩენილი ლექცია',
      questionsTotal: 'სულ კითხვები',
      overviewStudentsHeading: 'ჯგუფის სტუდენტები',
      groupTrailLabel: 'ჯგუფის ნავიგაცია',
      lecturesCount: 'ლექციები',
      weeksUnit: 'კვირა',
      monthsUnit: 'კალენდარული თვე',
      fullPeriodNotice: 'ჯგუფის სასწავლო პერიოდი და თითოეული სტუდენტის კითხვის ვადა განსხვავებული მონაცემებია. სწავლების დასრულების შემდეგ მასალების კითხვა რჩება.',
      selfPacedNotice: 'ამ კურსს საერთო დაწყების თარიღი არ აქვს. პირადი ვადა ჩარიცხვის გახსნიდან ითვლება.',
      editSchedule: 'განრიგის რედაქტირება',

      contentHeading: 'თემები და მასალები',
      sectionsCount: 'თემა',
      materialsCount: 'მასალა',
      contentEmptyHeading: 'თემები ჯერ არ არის',
      contentEmptyBody: 'როგორც კი კურსს თემები და მასალები დაემატება, სრული სტრუქტურა აქ გამოჩნდება.',
      sectionEmpty: 'ამ თემაში მასალა ჯერ არ არის.',
      sectionScopeCourse: 'კურსის ყველა ჯგუფისთვის',
      sectionScopeGroup: 'მხოლოდ ერთი ჯგუფისთვის',
      materialTypes: Object.freeze({
        video_embed: 'ვიდეო',
        rich_text: 'საკითხავი მასალა',
        external_link: 'გარე ბმული',
        file: 'ფაილი',
      }),
      lectureNumberValue: 'ლექცია',

      /*
       * *(T062-B, D8)* Authoring. Two rules: no control exists for a capability the accepted contract
       * lacks — there is no section or material delete operation, so hiding is what is offered — and
       * the protected-file order is stated before the administrator acts rather than discovered by a
       * refusal.
       */
      addSection: 'თემის დამატება',
      editSection: 'თემის რედაქტირება',
      sectionFormHeading: 'თემის პარამეტრები',
      sectionTitle: 'თემის სახელი',
      sectionScope: 'ვისთვის ჩანს',
      sectionScopeGroupSelect: 'ჯგუფი',
      saveSection: 'თემის შენახვა',
      sectionCreated: 'თემა შეიქმნა.',
      sectionSaved: 'თემა შენახულია.',

      addMaterial: 'მასალის დამატება',
      editMaterial: 'მასალის რედაქტირება',
      materialFormHeading: 'მასალის პარამეტრები',
      materialTitle: 'მასალის სახელი',
      materialType: 'მასალის ტიპი',
      materialEmbedUrl: 'ვიდეოს ბმული',
      materialEmbedHint: 'დაშვებულია YouTube, Vimeo, Gumlet და Google Drive — ბმული ან ჩასასმელი კოდი.',
      materialExternalUrl: 'გარე ბმული',
      materialExternalHint: 'დაშვებულია მხოლოდ https მისამართი.',
      materialBody: 'საკითხავი ტექსტი',
      materialLectureNumber: 'ლექციის ნომერი · არასავალდებულო',
      materialLectureNumberHint: 'მასალა ამ ნომრის ლექციის დაწყებიდან 3 საათში გაიხსნება თითოეული ჯგუფისთვის.',
      /* *(ledger 35)* The meeting is chosen from the course's own ordered list, by name. */
      materialLecture: 'რომელი შეხვედრის შემდეგ გაიხსნება',
      materialLectureHint: 'მასალა არჩეული შეხვედრის დაწყებიდან 3 საათში გაიხსნება თითოეული ჯგუფისთვის.',
      materialLectureNone: 'არ არის მიბმული შეხვედრაზე',
      /* *(ledger 36, 38)* Where a material will appear, said before it is saved. */
      scopeCourseLine: 'ეს მასალა კურსის შაბლონშია: გამოჩნდება ამ კურსის ყველა ჯგუფში.',
      scopeGroupLine: 'ეს მასალა მხოლოდ არჩეულ ჯგუფშია: სხვა ჯგუფებში არ გამოჩნდება.',
      scopeSectionCourse: 'თემა კურსისაა — მასალა ყველა ჯგუფს ხვდება.',
      scopeSectionGroup: 'თემა ერთი ჯგუფისაა — მასალა მხოლოდ იმ ჯგუფს ხვდება.',
      scopeSectionHint: 'ჯგუფის მითითების გარეშე თემა კურსის შაბლონია; ჯგუფის არჩევა მხოლოდ იმ ჯგუფისთვის ქმნის ცალკე ვერსიას.',
      materialLectureDate: 'ლექციის თარიღი · არასავალდებულო',
      materialLectureDateHint: 'მასალა ამ თარიღის ლექციის დაწყებიდან 3 საათში გაიხსნება.',
      saveMaterial: 'მასალის შენახვა',
      materialCreated: 'მასალა შეიქმნა.',
      materialSaved: 'მასალა შენახულია.',

      /* ---------------------------------------------------------------- *
       * *(002-course-content-reader T056)* Administrator explanations
       *
       * An explanation is a decoration on a passage of a reading material, so every string below
       * names the **passage** rather than a position: an administrator chooses words, and the words
       * are what they see afterwards. The refusals name the action that resolves them.
       * ---------------------------------------------------------------- */
      materialPreviewCollapse: 'აკეცვა',
      explanationsHeading: 'განმარტებები',
      explanationsOpen: 'განმარტებების მართვა',
      explanationsEmpty: 'ამ მასალას განმარტებები ჯერ არ აქვს.',
      explanationsCount: '{count} განმარტება',
      explanationAdd: 'განმარტების დამატება',
      explanationEdit: 'რედაქტირება',
      explanationDelete: 'წაშლა',
      explanationReanchor: 'სხვა მონაკვეთზე გადატანა',
      explanationSelectPassage: 'მონიშნეთ ტექსტის მონაკვეთი, რომელსაც განმარტება ეხება.',
      explanationSaveMaterialFirst: 'ჯერ შეინახეთ მასალის ტექსტის ცვლილებები, შემდეგ მონიშნეთ ტექსტი და დაამატეთ განმარტება.',
      explanationInitializeMaterialFirst: 'ამ ძველ მასალაზე განმარტების დასამატებლად ჯერ ერთხელ დააჭირეთ „მასალის შენახვას“, შემდეგ მონიშნეთ ტექსტი და დაამატეთ განმარტება.',
      explanationSelectionUnsupported: 'ამ ადგილას განმარტების დამატება ჯერ არ არის მხარდაჭერილი. მონიშნეთ აბზაცის, სათაურის ან სიის ტექსტი.',
      explanationSelectionEmpty: 'მონიშნულია მხოლოდ ცარიელი ადგილი. მონიშნეთ ტექსტი.',
      explanationSelectionTooLong: 'მონიშნული მონაკვეთი ძალიან გრძელია — მაქსიმუმ 8000 სიმბოლო. მონიშნეთ უფრო მცირე ნაწილი.',
      explanationSelectionInvalid: 'მონიშვნა ვერ წაიკითხა. მონიშნეთ ხელახლა.',
      explanationSelectedPassage: 'მონიშნული მონაკვეთი',
      explanationBody: 'განმარტების ტექსტი',
      explanationSave: 'განმარტების შენახვა',
      explanationSaved: 'განმარტება შენახულია.',
      explanationDeleted: 'განმარტება წაშლილია.',
      explanationDeleteConfirm: 'განმარტების წაშლა',
      explanationDeleteBody: 'განმარტება სამუდამოდ წაიშლება. მონიშნული ტექსტი უცვლელი დარჩება.',
      /** *(FR-019)* An explanation whose words an edit removed. Kept, and repaired deliberately. */
      explanationUnresolved: 'ტექსტი შეიცვალა — განმარტება ვეღარ პოულობს თავის მონაკვეთს.',
      explanationUnresolvedHint: 'აირჩიეთ ახალი მონაკვეთი და გადაიტანეთ განმარტება მასზე.',
      explanationMarkerLabel: 'განმარტება: {passage}',
      /* The refusals, each naming what to do next rather than what went wrong internally. */
      explanationIssues: {
        explanation_version_stale: 'ტექსტი ამასობაში შეიცვალა. დახურეთ და ხელახლა გახსენით განმარტებები.',
        explanation_range_invalid: 'მონიშნული მონაკვეთი ამ ტექსტში აღარ არსებობს. მონიშნეთ ხელახლა.',
        explanation_overlap: 'ამ მონაკვეთს უკვე აქვს განმარტება. აირჩიეთ სხვა მონაკვეთი.',
        explanation_revision_stale: 'განმარტება ამასობაში სხვამ შეცვალა. ჩატვირთეთ ხელახლა.',
        explanation_rich_text_invalid: 'განმარტების ტექსტი არ მიიღება.',
      },
      explanationIssueFallback: 'განმარტება ვერ შეინახა. სცადეთ ხელახლა.',

      publish: 'გამოქვეყნება',
      unpublish: 'დამალვა',
      moveUp: 'ზემოთ გადატანა',
      moveDown: 'ქვემოთ გადატანა',
      rowActionsLabel: 'მოქმედებები',
      /* Stated once, because the accepted API has no delete for a section or a material. */
      noDeleteNotice: 'თემისა და მასალის წაშლა არ არსებობს. ხელმისაწვდომია დამალვა.',

      /* The protected PDF lifecycle, in the exact server order (T060 D2/D6). */
      fileHeading: 'ფაილი',
      /* *(ledger 34)* A material has to exist before the two-step upload can attach bytes to it. */
      fileAfterSave: 'ფაილის ატვირთვა შენახვის შემდეგ გახდება შესაძლებელი.',
      fileNone: 'ფაილი ჯერ არ არის ატვირთული.',
      fileChoose: 'PDF ფაილის არჩევა',
      /** *(AR-04 correction)* What the chooser accepts, now that the browser no longer says so. */
      fileAccepts: 'მხოლოდ PDF ფაილი.',
      fileUpload: 'ატვირთვა',
      fileUploading: 'მიმდინარეობს ატვირთვა…',
      fileUploadProgress: 'ატვირთვის მიმდინარეობა',
      fileCancelUpload: 'ატვირთვის შეჩერება',
      fileDelete: 'ფაილის წაშლა',
      fileDeleted: 'ფაილი წაიშალა.',
      fileAttached: 'ფაილი მიმაგრებულია.',
      filePreview: 'ფაილის ნახვა',
      fileUnpublishFirst: 'ფაილის შესაცვლელად ჯერ დამალეთ მასალა.',
      fileDeleteFirst: 'ახალი ფაილის ასატვირთად ჯერ წაშალეთ არსებული.',
      fileRetryNewUpload: 'ატვირთვა შეწყდა. სცადეთ ხელახლა — დაიწყება ახალი ატვირთვა.',

      /* Per-group opening preview (T059 D3, FR-MAT-08). */
      previewGroupLabel: 'ჯგუფი',
      previewNoGroup: 'ჯგუფი არჩეული არ არის',
      /* Not an em dash: the catalogue is Georgian everywhere, and "choose a group" is also the more
         useful thing to say in a column that is empty because nothing was asked yet. */
      previewNotAsked: 'აირჩიეთ ჯგუფი',
      previewStates: Object.freeze({
        hidden: 'დამალულია',
        scheduled: 'გაიხსნება',
        visible: 'ხილვადია',
        'missing-schedule': 'განრიგში თარიღი არ არის',
      }),
      /* The FR-MAT-08 warning, stated as the administrator's own next action. */
      previewMissingSchedule: 'ამ ლექციის ნომერს არჩეული ჯგუფის განრიგში თარიღი არ აქვს, ამიტომ მასალა ამ ჯგუფისთვის დამალულია.',
      previewSelfPaced: 'ხილვადია ჩარიცხვიდან',

      /*
       * *(PRD-09)* The owner-frozen Course Studio preview action.
       *
       * `DESIGN.md` § Navigation and Composition and `docs/design/product-redesign-addendum.md` § 5.7
       * freeze the wording: "The course-studio preview action is labelled `სტუდენტის ხედვა`". The same
       * word names the studio header's way in and the explicit apply control of the group-scoped card,
       * because they are the two halves of one action and a second wording for either would be the
       * drift FR-UX-05 forbids.
       *
       * The body says what the action really does. It does **not** open a student's screen: the studio
       * re-reads its own structure with `admin.courses.get({previewGroupId})` and every material row
       * then carries that group's visibility. Promising more than that would be a copy that lies.
       */
      studentView: 'სტუდენტის ხედვა',
      studentViewHeading: 'სტუდენტის ხედის შემოწმება',
      studentViewBody: 'აირჩიეთ ჯგუფი და დაადასტურეთ — თითოეულ მასალას არჩეული ჯგუფის ხილვადობა მიეწერება.',
      studentViewClear: 'ხედვის გასუფთავება',

      unsavedHeading: 'შეუნახავი ცვლილებები',
      unsavedBody: 'ფორმაში შეუნახავი ცვლილებებია. გასვლისას ისინი დაიკარგება.',
      unsavedConfirm: 'გასვლა შენახვის გარეშე',

      courseNotFound: 'კურსი ვერ მოიძებნა.',
      groupNotFound: 'ჯგუფი ვერ მოიძებნა.',
      formInvalid: 'შეამოწმეთ მონიშნული ველები.',
      scanTooLarge: 'შედეგები ძალიან ბევრია. დაავიწროვეთ ფილტრები ან ძიება და სცადეთ ხელახლა.',
      structureTooLarge: 'ამ კურსის სტრუქტურა სრულად ვერ ჩაიტვირთა, ამიტომ ნაწილობრივი შინაარსი არ იჩვენება.',
      cursorInvalid: 'გვერდების მიმდევრობა მოძველდა. დაბრუნდით პირველ გვერდზე და სცადეთ ხელახლა.',
      issueFallback: 'მნიშვნელობა მისაღები არ არის. შეამოწმეთ ველი.',
      issues: Object.freeze({
        /*
         * One client-only code. The server expresses the same refusal as a schema issue on
         * `params.name` with no curriculum code of its own, so the form checks it locally and says
         * exactly what is missing instead of falling back to the generic line.
         */
        group_name_required: 'ჯგუფს სახელი სჭირდება.',
        course_type_locked_by_enrollment: 'აქტიური ჩარიცხვის არსებობისას კურსის ტიპი ვერ იცვლება.',
        /*
         * *(OD-7 · FR-014)* One stored cover belongs to one course. The remedy is a new upload, not a
         * corrected address: there is no address any more.
         */
        course_cover_unavailable: 'ეს სურათი ყდად ვერ გამოიყენება. ატვირთეთ სხვა სურათი.',
        course_type_locked_by_group: 'კურსს უკვე აქვს ჯგუფი, ამიტომ ტიპი ვერ იცვლება.',
        /* *(CE-1)* Continuing education is chosen at creation, and its course has no groups to edit. */
        course_type_continuing_fixed: 'განგრძობითი სწავლების ტიპი მხოლოდ კურსის შექმნისას აირჩევა და შემდეგ ვერ იცვლება.',
        continuing_group_fixed: 'განგრძობით სწავლებას ჯგუფები არ აქვს.',
        group_course_immutable: 'ჯგუფი სხვა კურსზე ვერ გადადის.',
        group_type_inherited: 'ჯგუფის ტიპი კურსიდან მოდის და მისგან ვერ განსხვავდება.',
        group_instant_invalid: 'მითითებული თარიღი და საათი რეალურ დროს არ შეესაბამება.',
        group_start_required: 'სრული კურსის ჯგუფს დაწყების თარიღი და საათი სჭირდება.',
        group_end_required: 'სრული კურსის ჯგუფს დასრულების თარიღი და საათი სჭირდება.',
        group_end_not_after_start: 'დასრულება დაწყებაზე გვიან უნდა იყოს.',
        group_extra_weeks_invalid: 'დამატებითი კვირები უნდა იყოს მთელი რიცხვი, ნული ან მეტი.',
        /* *(ledger 45)* The term is chosen in weeks, so the week count is what a reader repairs. */
        group_weeks_invalid: 'ხანგრძლივობა უნდა იყოს მთელი რიცხვი 1-დან 104 კვირამდე.',
        group_months_not_allowed: 'სრული კურსის ჯგუფს თვეების რაოდენობა არ აქვს.',
        group_months_required: 'თვითსწავლებად ჯგუფს სჭირდება თვეების მთელი რაოდენობა, მინიმუმ ერთი.',
        group_start_not_allowed: 'თვითსწავლებად ჯგუფს საერთო დაწყება არ აქვს.',
        group_end_not_allowed: 'თვითსწავლებად ჯგუფს საერთო დასრულება არ აქვს.',
        group_extra_weeks_not_allowed: 'თვითსწავლებად ჯგუფს დამატებითი კვირები არ აქვს.',
        group_schedule_not_allowed: 'თვითსწავლებად ჯგუფს ლექციების განრიგი არ აქვს.',
        lecture_number_invalid: 'ლექციის ნომერი უნდა იყოს მთელი რიცხვი 1-დან 1000-მდე.',
        lecture_number_duplicate: 'ეს ლექციის ნომერი უკვე გამოყენებულია.',
        lecture_date_invalid: 'ლექციის თარიღი რეალური კალენდარული დღე არ არის.',
        lecture_date_outside_interval: 'ლექციის თარიღი ჯგუფის პერიოდს სცილდება.',
        lecture_time_invalid: 'ლექციის საათი უნდა იყოს 00:00-დან 23:59-მდე.',
        lecture_time_outside_interval: 'ლექციის საათი ჯგუფის პერიოდს სცილდება.',
        /*
         * *(AR-09)* Two client-only codes. The server expresses both as schema issues on
         * `params.template.*` with no curriculum code of its own, so the form checks the owner-stated
         * bounds locally and says exactly which one was missed.
         */
        course_meetings_per_week_range: 'შეხვედრების რაოდენობა კვირაში უნდა იყოს მთელი რიცხვი 1-დან 7-მდე.',
        course_lecture_duration_range: 'ლექციის ხანგრძლივობა უნდა იყოს მთელი რიცხვი 60-დან 240 წუთამდე.',
        /* *(ledger 45/46)* The two fields the course template gained. */
        course_weeks_range: 'კურსის ხანგრძლივობა უნდა იყოს მთელი რიცხვი 1-დან 104 კვირამდე.',
        course_topics_too_many: 'თემების რაოდენობა 200-ს არ უნდა აღემატებოდეს.',
        course_topic_too_long: 'თემის დასახელება ძალიან გრძელია.',
        /*
         * *(ledger 46, 58)* The template must fit the term it belongs to.
         *
         * The server refuses the same state on `params.template.topics` with this same code, so one
         * line serves the local check and the refusal.
         */
        course_topics_exceed_schedule: 'თემები კურსის ხანგრძლივობას სცილდება. შეამცირეთ თემები ან გაზარდეთ კვირების ან კვირეული შეხვედრების რაოდენობა.',
      }),

      /**
       * *(T062-B)* Every stable code the T059 content writers and the T060 file lifecycle emit on this
       * surface, plus the few this client decides locally.
       *
       * Separate from `issues` above because that map belongs to the course/group forms; keeping the
       * content codes in their own block is what lets `content-models.ts` prove it covers all of them
       * without asserting anything about the group form's set.
       *
       * The size line is deliberately qualitative: the cap is engineering configuration and is not on
       * the wire (`filePlan.fileSizeAboveCap` carries no number), so quoting one here would be an
       * invention that could disagree with the deployment.
       */
      contentIssues: Object.freeze({
        section_title_required: 'თემას სახელი სჭირდება.',
        section_group_required: 'ერთი ჯგუფისთვის განკუთვნილ თემას ჯგუფი უნდა მიეთითოს.',
        section_scope_locked: 'თემაში მასალა უკვე არის, ამიტომ დანიშნულება ვეღარ იცვლება.',
        material_title_required: 'მასალას სახელი სჭირდება.',
        material_embed_required: 'ვიდეოს ბმული სავალდებულოა.',
        material_embed_not_allowed: 'დაშვებულია მხოლოდ YouTube, Vimeo, Gumlet ან Google Drive ბმული.',
        material_link_required: 'გარე ბმული სავალდებულოა.',
        material_link_https_only: 'ბმული უნდა იწყებოდეს https:// -ით.',
        material_doc_invalid: 'ტექსტი შეიცავს დაუშვებელ ელემენტს და ვერ შეინახება.',
        material_not_file: 'ეს მასალა ფაილის ტიპის არ არის.',
        material_file_published: 'ფაილის შესაცვლელად ჯერ დამალეთ მასალა.',
        material_file_attached: 'მასალას უკვე აქვს ფაილი. ჯერ წაშალეთ არსებული.',
        material_file_pending: 'მასალას ჯერ ფაილი არ აქვს.',
        material_type_locked_by_file: 'მიმაგრებული ფაილის გამო ტიპი ვერ იცვლება. ჯერ წაშალეთ ფაილი.',
        file_size_above_cap: 'ფაილი აღემატება დაშვებულ ზომას. აირჩიეთ უფრო მცირე ფაილი.',
        upload_not_pdf: 'დაშვებულია მხოლოდ PDF ფაილი.',
        upload_empty: 'ფაილი ცარიელია.',
        upload_size_invalid: 'ატვირთული ფაილის ზომა მიუღებელია.',
        upload_size_above_cap: 'ატვირთული ფაილი აღემატება დაშვებულ ზომას.',
        upload_content_type_mismatch: 'ატვირთული ფაილი PDF არ არის.',
        upload_object_missing: 'ატვირთული ფაილი ვერ მოიძებნა. სცადეთ ხელახლა.',
        upload_not_current: 'მასალა ატვირთვის დაწყების შემდეგ შეიცვალა. დაიწყეთ ატვირთვა ხელახლა.',
        upload_id_invalid: 'ატვირთვის იდენტიფიკატორი მიუღებელია. დაიწყეთ ატვირთვა ხელახლა.',
        preview_group_outside_course: 'არჩეული ჯგუფი ამ კურსს არ ეკუთვნის.',
        /* *(OD-2026-10-03, OD-2026-10-12)* The audience „ვის უჩანს“ and the move by section choice. */
        material_audience_not_allowed: 'ამ სექციის მასალა ცალკეულ ჯგუფებს ვერ მიეთითება.',
        material_audience_outside_course: 'არჩეული ჯგუფი ამ კურსის აქტიური ჯგუფი არ არის.',
        material_audience_empty: 'აირჩიეთ „მთელი კურსი“ ან ერთი ჯგუფი მაინც.',
        material_audience_duplicate: 'ერთი ჯგუფი ორჯერ არის არჩეული.',
        material_audience_ambiguous: 'მასალის ადრესატი ორჯერ არის მითითებული. სცადეთ ხელახლა.',
        material_moving: 'მასალა ახლა გადაიტანება — სცადე ცოტა ხანში.',
        material_move_lecture_unmapped: 'ამ თარიღის ლექცია ჯგუფის განრიგში არ არის — ჯერ ლექცია დაამატე ან შეცვალე.',
        material_move_too_large: 'მასალა ძალიან ბევრ სურათს შეიცავს და ვერ გადაიტანება.',
        material_move_same_section: 'მასალა უკვე ამ სექციაშია.',
        PURGE_STARTED: 'ვეღარ აღდგება — სამუდამო წაშლა უკვე დაიწყო.',
        FILE_MISSING: 'ფაილის გარეშე მასალა ვერ გამოქვეყნდება.',
        NO_CHANGE: 'მდგომარეობა უკვე ასეთია.',
        NOT_FOUND: 'ჩანაწერი ვერ მოიძებნა.',
      }),
    }),

    /**
     * The group roster and the enrolment write flows — T056 WP4 (FR-PANEL-01 (6) and (7), R2-05).
     *
     * Three rules shape every line.
     *
     * **The three states an administrator must not conflate each get their own words.** An account
     * restriction, a closed enrolment and an expired asking window are different facts with different
     * remedies, so `rights*` names the state and `access*` says separately whether the course is still
     * readable. Nothing here implies that closing one enrolment touches the account, or that
     * deactivating an account closed an enrolment.
     *
     * **Closing ends this course.** `FR-ENR-05` makes administrator closure one of the two exceptions
     * to permanent access, and `functions/src/curriculum/studentCourses.ts` refuses a closed
     * enrolment's course outright. The confirmation says so rather than promising retained materials.
     *
     * **The approved R2 wording is used where the design supplies it**, normalised from the
     * prototype's informal imperative to this catalogue's polite plural.
     */
    enrollment: Object.freeze({
      tabStudents: 'მოსწავლეები',
      rosterUnit: 'მოსწავლე',
      activeEnrollments: 'აქტიური ჩარიცხვები',
      addExisting: 'არსებული მოსწავლის დამატება',
      preInvite: 'წინასწარი მოწვევა',
      search: 'მოსწავლის ძიება',
      tableLabel: 'ჯგუფის ჩარიცხვების ცხრილი',
      cardsLabel: 'ჯგუფის ჩარიცხვები',

      select: 'მონიშვნა',
      columnStudent: 'მოსწავლე',
      /* *(ledger 49)* The two columns the roster keeps instead of its date columns. */
      columnCourses: 'მიმდინარე კურსები',
      columnQuestionsInGroup: 'კითხვები ამ ჯგუფში',
      columnOpened: 'ჩარიცხვის გახსნა',
      columnPeriod: 'კითხვის პერიოდი',
      columnRights: 'უფლებები',
      columnActions: 'მოქმედებები',

      emptyHeading: 'ჩარიცხვა ვერ მოიძებნა',
      emptyBody: 'შეცვალეთ ფილტრები ან დაამატეთ მოსწავლე ჯგუფში.',
      deniedHeading: 'მოქმედება მიუწვდომელია',
      deniedBody: 'ამ მოქმედების შესრულების უფლება ამ ანგარიშს არ აქვს.',

      /* The six source-backed right states, in the order the decision is made. */
      rightClosed: 'ჩარიცხვა დახურულია',
      rightAccountRestricted: 'ანგარიში შეზღუდულია',
      rightCourseArchived: 'კურსი არქივშია · მასალები ხელმისაწვდომია',
      rightNotStarted: 'კითხვის დასმა გაიხსნება',
      rightDaysLeft: 'დღე დარჩა',
      rightExpired: 'კითხვების ვადა დასრულდა · მასალები ხელმისაწვდომია',
      accessScheduled: 'მასალების წვდომა განრიგის მიხედვით',
      accessRestricted: 'კურსის წვდომა შეზღუდულია',
      /*
       * There is deliberately no transfer-count line. `admin.students.list` projects no group
       * history — see `enrollmentState` in `functions/src/access/students.ts` — so a count here would
       * be a number the read cannot support. The history itself belongs to `admin.audit.list`.
       */

      /*
       * The overflow control's *name*, not its glyph.
       *
       * The `…` it draws is decoration: it carries no meaning, translates to nothing and is hidden
       * from assistive technology by `RowActionsMenu`, which renders it as a literal. Only this
       * Georgian name is content, and it is what a screen reader announces.
       */
      rowActionsLabel: 'ჩარიცხვის მოქმედებები',
      changeDeadline: 'ვადის შეცვლა',
      transfer: 'ჯგუფში გადაყვანა',
      close: 'ჩარიცხვის დახურვა',

      enrolHeading: 'მოსწავლის კურსზე ჩარიცხვა',
      enrolNote: 'ერთ კურსზე მხოლოდ ერთი აქტიური ჩარიცხვა შეიძლება. სხვა ჯგუფში გადასასვლელად გამოიყენეთ „გადაყვანა".',
      enrolConfirm: 'ჩარიცხვა',
      enrolCandidates: 'დასამატებელი მოსწავლეები',
      enrolCandidatesEmpty: 'შესაბამისი დადასტურებული ანგარიში ვერ მოიძებნა.',
      openedAtLabel: 'ჩარიცხვის გახსნა',
      openedAtTimeLabel: 'დრო',
      openedAtHint: 'ცარიელი ველი ნიშნავს ჩარიცხვის რეალურ მომენტს. მითითებული დრო ითვლება თბილისის დროით და კითხვის ვადაც აქედან გამოითვლება.',
      openedAtInvalid: 'მიუთითეთ სწორი თარიღი და დრო.',
      enrolDone: 'ჩარიცხვები დასრულდა.',

      transferOfferHeading: 'ამ სტუდენტს ამავე კურსზე ჩარიცხვა უკვე აქვს',
      transferOffer: 'გადაყვანაზე გადასვლა',
      transferHeading: 'მოსწავლის სხვა ჯგუფში გადაყვანა',
      transferTarget: 'ახალი ჯგუფი',
      transferChoose: 'აირჩიეთ ჯგუფი',
      /* The one thing a transfer cannot be performed without, said rather than silently required. */
      transferTargetRequired: 'აირჩიეთ ახალი ჯგუფი.',
      transferEmpty: 'ამ კურსში სხვა მოქმედი ჯგუფი არ არის.',
      transferNote: 'მოსწავლე გადავა იმავე კურსის სხვა ჯგუფში. პირადი კითხვის ვადები შენარჩუნდება; მასალებისა და ჯგუფის პასუხების ხილვადობა ახალი ჯგუფის მიხედვით შეიცვლება.',
      transferNewEnd: 'უფლების ახალი დასრულება',
      transferNewEndHint: 'მითითებული დღე შეინახება იმავე დღის 23:59:59-ზე, თბილისის დროით.',
      transferNoteLabel: 'შენიშვნა',
      transferOptional: 'არასავალდებულო',
      transferConfirm: 'გადაყვანის დადასტურება',
      transferDone: 'სტუდენტი გადაყვანილია.',

      closeHeading: 'კურსზე ჩარიცხვის დახურვა',
      /*
       * One sentence assembled from three facts: `{name} {closeBodyLead} {course}{closeBodyTail}`.
       *
       * Every entry here is trimmed, like every other string in this catalogue — the spacing that
       * joins them belongs to the composition, not to the translatable text, so a later language can
       * change the words without inheriting somebody's padding.
       */
      closeBodyLead: 'დაკარგავს ამ კურსის მასალებსა და კითხვა-პასუხზე წვდომას:',
      closeBodyTail: '. სხვა კურსები და ანგარიში უცვლელი რჩება.',
      closeDone: 'ჩარიცხვები დაიხურა.',

      extendSelected: 'მონიშნული ვადების გაგრძელება',
      extendHeading: 'კითხვის ვადის გაგრძელება',
      extendUnitLabel: 'გაგრძელების ფორმა',
      extendUnits: Object.freeze({
        weeks: 'კვირები',
        months: 'კალენდარული თვეები',
        exact: 'ზუსტი თარიღი',
      }),
      extendAmountLabel: 'რაოდენობა',
      extendExactLabel: 'ახალი ბოლო დღე · თბილისის დრო',
      extendReasonLabel: 'მიზეზი',
      extendNote: 'შემდეგ ნაბიჯზე ნახავთ თითოეულ მოსწავლეს, კონკრეტულ კურსს, ძველ და ახალ ვადას. გაგრძელება მასალების წვდომას ან სხვა კურსის უფლებებს არ ცვლის.',
      extendPreviewAction: 'ვადების წინასწარი ნახვა',
      extendPreviewHeading: 'გაგრძელების წინასწარი ნახვა',
      extendTableLabel: 'გაგრძელების შედეგების ცხრილი',
      extendColumnTarget: 'მოსწავლე / კურსი',
      extendColumnOld: 'ძველი ვადა',
      extendColumnNew: 'ახალი ვადა',
      extendColumnResult: 'შედეგი',
      extendWillApply: 'გაგრძელდება',
      /* Same rule as the closure sentence: `{applied} {lead} {skipped} {tail}`, spacing at the seam. */
      extendSummaryLead: 'ვადა გაგრძელდება;',
      extendSummaryTail: 'ჩანაწერი გამოტოვებულია. სხვა ჩარიცხვები უცვლელი რჩება.',
      extendApply: 'ვადების გაგრძელების დადასტურება',
      extendDone: 'ვადები განახლდა.',
      amountInvalid: 'მიუთითეთ მთელი რიცხვი, არანაკლებ ერთისა.',
      exactDateInvalid: 'მიუთითეთ არსებული კალენდარული თარიღი.',

      partialHeading: 'ნაწილი ვერ შესრულდა',
      retryRemaining: 'დარჩენილის ხელახლა შესრულება',
      retryRemainingHint: 'უკვე შესრულებული ჩანაწერები მეორედ არ შეიცვლება.',
    }),

    /**
     * The administrator Q&A workspace — T049.
     *
     * Two rules shape every line below. **B11**: the interface describes the human workflow and the
     * visible state, never the implementation — so nothing here says a draft was generated,
     * automatic, produced by a model or named after a provider, and „მომზადება" is what the product
     * itself calls the step. **`BOUNDARIES.md` § 6**: the server sends codes, never display text, so
     * every operational outcome, failure code and audit action has its line here and an explicit
     * fallback for a value this catalogue has not seen yet.
     */
    qa: Object.freeze({
      navLabel: 'კითხვები',
      heading: 'კითხვების სამუშაო რიგი',
      intro: 'გაფილტრეთ კითხვები, შეამოწმეთ მომზადებული პასუხი და დაადასტურეთ გამოქვეყნება.',

      /* Filters. */
      searchLabel: 'ტექსტით ძიება',
      searchHint: 'ძიება მოიცავს კითხვას, გამოქვეყნებულ პასუხს, სტუდენტის სახელსა და ელფოსტას.',
      sortLabel: 'დალაგება',
      sortNewest: 'ჯერ ახალი',
      sortOldest: 'ჯერ ძველი',
      accountId: 'სტუდენტის იდენტიფიკატორი',
      /* *(AR-12)* The name-based selectors that replace the three identifier fields. */
      studentSelector: 'სტუდენტი',
      studentNeedsCourse: 'ჯერ აირჩიეთ კურსი, რომ სტუდენტი დაზუსტდეს.',
      allStudents: 'ყველა სტუდენტი',
      /*
       * *(follow-up 4)* One labelled interval replaces the two separate date fields. `dateFrom` and
       * `dateTo` are kept here because the tests assert that neither label is on the page any more —
       * a removed key could only be asserted against a literal, which is the drift FR-UX-05 forbids.
       */
      dateRange: 'თარიღების შუალედი',
      dateFrom: 'თარიღიდან',
      dateTo: 'თარიღამდე',
      /*
       * *(ledger 24)* "Replace or clarify the incomprehensible 'needs attention only' control so its
       * purpose is explicit without guesswork."
       *
       * The old label named an internal flag. What the flag actually means is one thing an operator
       * recognises: the answer could not be prepared, so somebody has to look. The label says that,
       * and the help line under it says what the filter does to the queue — neither of them names
       * how an answer is prepared, which is B11's rule and the rest of this catalogue's habit.
       */
      needsAttentionFilter: 'მხოლოდ ის, სადაც პასუხი ვერ მომზადდა',
      needsAttentionHint: 'აჩვენებს მხოლოდ კითხვებს, რომლებზეც პასუხის მომზადება ჩაიშალა და ადმინისტრატორის ჩარევაა საჭირო.',
      deletedFilter: 'წაშლილები',
      deletedFilterHint: 'აჩვენებს მხოლოდ წაშლილ კითხვებს ჩვეულებრივის ნაცვლად.',
      /* *(ledger 21–23)* The multiple-selection scope filters. */
      coursesSelector: 'კურსები',
      groupsSelector: 'ჯგუფები',
      studentsSelector: 'სტუდენტები',
      groupsNeedCourse: 'ჯერ აირჩიეთ კურსი, რომ ჯგუფები დაზუსტდეს.',
      /* *(ledger 23)* A student is a member of a group, so a group is what opens this level. */
      studentsNeedGroup: 'ჯერ აირჩიეთ ჯგუფი, რომ მისი სტუდენტები ჩამოიწეროს.',
      idHint: 'კურსისა და ჯგუფის სახელებით არჩევა მოგვიანებით დაემატება; ახლა იდენტიფიკატორი გამოიყენება.',
      /* The rarely used narrowing controls, folded away but never hidden while they are in force. */
      advancedFilters: 'დამატებითი ფილტრები',
      advancedFiltersActive: 'დამატებითი ფილტრი აქტიურია',
      searchTooWide: 'მოძებნილი ნაკრები ძალიან დიდია. ჯერ დაავიწროვეთ კურსით, ჯგუფით, სტატუსით ან თარიღით და შემდეგ მოძებნეთ.',

      /* The queue. */
      queueHeading: 'რიგი',
      backToQueue: 'რიგში დაბრუნება',
      tableLabel: 'კითხვების ცხრილი',
      totalLabel: 'ნაპოვნია',
      questionColumn: 'კითხვა',
      authorColumn: 'სტუდენტი',
      contextColumn: 'კურსი და ჯგუფი',
      emptyHeading: 'კითხვები ჯერ არ არის',
      emptyBody: 'როგორც კი სტუდენტი კითხვას დასვამს, ის აქ გამოჩნდება.',
      noResultsHeading: 'შესაბამისი კითხვები ვერ მოიძებნა',
      noResultsBody: 'შეცვალეთ ფილტრები ან საძიებო ტექსტი.',

      /* The four administrator statuses and the markers beside them. */
      statuses: Object.freeze({
        new: 'ახალი კითხვა',
        preparing: 'პასუხი მზადდება',
        prepared: 'პასუხი მომზადებულია',
        confirmed: 'დადასტურებულია',
      }),
      deletedMarker: 'წაშლილია',
      editedMarker: 'რედაქტირებულია',
      staleMarker: 'მოძველებული',
      overrideMarker: 'დადასტურებულია ხელახლა',
      authorScopeMarker: 'მხოლოდ ავტორისთვის',
      groupVisibleMarker: 'ჯგუფისთვის ხილული',
      needsAttention: 'ყურადღება სჭირდება',

      /* The detail. */
      detailHeading: 'კითხვის დეტალები',
      chooseQuestion: 'დეტალების სანახავად აირჩიეთ კითხვა რიგიდან.',
      questionHeading: 'კითხვა',
      askedAt: 'დასმულია',
      courseLabel: 'კურსი',
      groupLabel: 'ჯგუფი',
      authorLabel: 'სტუდენტი',
      /*
       * *(T093, owner annotations 04–07)* The detail's metadata values are filter controls.
       *
       * Each name is the whole action, because a control announced as "ნინო ბერიძე" would say what
       * it *is* and never what pressing it does. The queue keeps every other filter in force and the
       * existing „გასუფთავება" clears them all — there is no second chip vocabulary here to name.
       */
      filterByStudent: 'რიგის გაფილტვრა ამ სტუდენტით',
      filterByDay: 'რიგის გაფილტვრა ამ დღით',
      filterByCourse: 'რიგის გაფილტვრა ამ კურსით',
      filterByGroup: 'რიგის გაფილტვრა ამ ჯგუფით',
      filterApplied: 'რიგი ამ მონაცემით გაიფილტრა. ყველა ფილტრს „გასუფთავება" ხსნის.',
      textHistoryHeading: 'კითხვის ტექსტის ისტორია',
      textVersionOriginal: 'ორიგინალი',
      textVersionEdited: 'რედაქტირებული',
      versionsHeading: 'პასუხის ვერსიები',
      versionsEmpty: 'პასუხის ვერსია ჯერ არ არსებობს.',
      versionNumber: 'ვერსია',
      versionSourcePrepared: 'მომზადებული მონახაზი',
      versionSourceAdmin: 'ადმინისტრატორის ვერსია',
      versionWords: 'სიტყვა',
      versionAuthor: 'ავტორი',
      textVersionAuthor: 'შემსწორებელი',
      auditActor: 'შემსრულებელი',
      auditTransition: 'სტატუსი',
      versionCurrentDraft: 'მიმდინარე მონახაზი',
      versionPublished: 'გამოქვეყნებული',
      /* *(T093, annotations 11–12)* One version, opened: its full text and what it changed. */
      versionFullText: 'სრული ტექსტი',
      versionChanges: 'ცვლილება წინა ვერსიასთან',
      versionFirst: 'ეს პირველი ვერსიაა, ამიტომ შესადარებელი წინა ვერსია არ არსებობს.',
      /*
       * *(T094, annotation 12)* Restoring an earlier version.
       *
       * The dialog says what the operation actually does — appends this text as a new version — rather
       * than "returns to" it, because nothing in the history is removed or rewritten and an
       * administrator deciding to publish an old answer should know which of the two it is.
       */
      restoreVersion: 'ამ ვერსიის აღდგენა',
      restoreHeading: 'პასუხის ძველი ვერსიის აღდგენა',
      restoreBody: 'ამ ვერსიის ტექსტი ახალ ვერსიად დაემატება და მიმდინარე პასუხი გახდება. ისტორია უცვლელი რჩება.',
      restorePublishedBody: 'ამ ვერსიის ტექსტი ახალ ვერსიად დაემატება და გამოქვეყნებულ პასუხს ჩაანაცვლებს. სტუდენტები მაშინვე მას დაინახავენ; ისტორია უცვლელი რჩება.',
      restoreConfirm: 'დიახ, აღდგეს',
      restored: 'ძველი ვერსია ახალ ვერსიად აღდგა.',
      restoreCurrent: 'ეს ვერსია უკვე მიმდინარე პასუხია.',

      /* ---------------------------------------------------------------- *
       * *(T096)* The selection-scoped AI edit
       *
       * B11 forbids naming the mechanism, so none of the copy below does: to a reader the action is
       * "გადაწერა" — rewriting the passage they marked — and the three states describe what is
       * happening to their request, not what a provider is doing. The keys keep the internal name
       * because identifiers are not copy.
       * ---------------------------------------------------------------- */

      /** The toolbar action, and what it opens. */
      aiEdit: 'გადაწერა',
      aiEditHeading: 'მონიშნული მონაკვეთის გადაწერა',
      aiEditBody: 'დაწერეთ მითითება. მოთხოვნა მხოლოდ მონიშნულ მონაკვეთს შეეხება; პასუხი უცვლელი რჩება, სანამ ცხადად არ ჩაანაცვლებთ.',
      aiEditInstruction: 'მითითება',
      aiEditInstructionHint: 'მაგალითად: „გაამარტივე", „დაამატე კონკრეტული დოზა", „უფრო მოკლედ".',
      aiEditPassage: 'მონიშნული მონაკვეთი',
      aiEditSubmit: 'გაგზავნა',
      aiEditSent: 'მოთხოვნა გაიგზავნა. შედეგი აქვე გამოჩნდება.',

      /*
       * Why a selection cannot be sent — one sentence per limit, never a generic "არასწორი".
       *
       * *(002-course-content-reader T035, FR-001)* `aiEditCrossBlock` is **removed**. It said that a
       * selection had to fit inside one paragraph or heading, which is exactly the limit the owner
       * asked to lift: a range across several adjacent paragraphs, a heading and a list is now an
       * ordinary request. A message describing a refusal nothing can produce is a message no
       * administrator can ever be shown, so it goes with the rule it explained.
       */
      aiEditNoSelection: 'ჯერ მონიშნეთ პასუხის ის ნაწილი, რომლის გადაწერაც გსურთ.',
      aiEditUnsupportedBlock: 'ამ ადგილის გადაწერა ჯერ არ არის მხარდაჭერილი. მონიშნეთ აბზაცის, სათაურის ან სიის ტექსტი.',
      aiEditEmpty: 'მონიშნულია მხოლოდ ცარიელი ადგილი. მონიშნეთ ტექსტი.',
      aiEditTooLong: 'მონიშნული მონაკვეთი ძალიან გრძელია — მაქსიმუმ 8000 სიმბოლო. მონიშნეთ უფრო მცირე ნაწილი.',
      aiEditRangeInvalid: 'მონიშვნა ვერ წაიკითხა. მონიშნეთ ხელახლა.',
      /** *(FR-001)* How many blocks one pending edit covers, shown beside its passage. */
      aiEditBlockCount: 'მონიშნულია {count} ბლოკი.',
      aiEditOverlap: 'ამ მონაკვეთს უკვე ეხება სხვა დაუსრულებელი გადაწერა. ჯერ ის დაასრულეთ ან მონიშნეთ სხვა ადგილი.',

      /** The durable jobs on the question being read. */
      selectionEditsHeading: 'მონაკვეთების გადაწერა',
      selectionEditsEmpty: 'მონიშნულ მონაკვეთზე გადაწერა ჯერ არ მოგითხოვიათ.',
      selectionEditInstruction: 'მითითება',
      selectionEditResult: 'შემოთავაზებული ტექსტი',
      selectionEditReplace: 'ჩანაცვლება',
      selectionEditRetry: 'ახლიდან',
      selectionEditReplaced: 'ჩანაცვლდა',
      selectionEditReplacedIn: 'ვერსია',
      selectionEditFailed: 'ვერ მომზადდა. სცადეთ ახლიდან.',
      /* *(T097)* The workspace now watches for the result itself, so the copy no longer asks for a refresh. */
      selectionEditWaiting: 'მიმდინარეობს — შედეგი აქვე გამოჩნდება, როგორც კი მზად იქნება.',
      selectionEditReplaceHeading: 'მონაკვეთის ჩანაცვლება',
      selectionEditReplaceBody: 'მონიშნული მონაკვეთი ჩანაცვლდება შემოთავაზებული ტექსტით და პასუხის ახალი ვერსია შეიქმნება. ისტორია უცვლელი რჩება.',
      selectionEditReplaceConfirm: 'დიახ, ჩანაცვლდეს',
      selectionEditReplaced_: 'მონაკვეთი ჩანაცვლდა.',

      /** The administrator-only substatus: three words, and never a public Question status. */
      substatusLabel: 'გადაწერის მდგომარეობა',
      substatusAny: 'ნებისმიერი',
      substatuses: Object.freeze({
        running: 'მიმდინარეობს',
        ready: 'მზადაა',
        error: 'შეცდომა',
      }),
      substatusHint: 'ფილტრავს კითხვებს, რომლებზეც მონიშნული მონაკვეთის გადაწერა ამ მდგომარეობაშია.',
      substatusExclusive: 'ამ ფილტრთან ერთად სტატუსი, „ვერ მომზადდა" და „წაშლილები" არ გამოიყენება.',
      auditHeading: 'მოქმედებების ისტორია',
      auditEmpty: 'ჩანაწერები ჯერ არ არის.',
      auditActionUnknown: 'სხვა მოქმედება',
      auditActions: Object.freeze({
        'admin.questions.editText': 'კითხვის ტექსტის რედაქტირება',
        'admin.questions.setVisibilityScope': 'ხილვადობის შეცვლა',
        'admin.questions.delete': 'კითხვის წაშლა',
        'admin.questions.restore': 'კითხვის აღდგენა',
        'admin.questions.purge': 'კითხვის სამუდამოდ წაშლა',
        'admin.answers.saveDraft': 'მონახაზის შენახვა',
        'admin.answers.overrideStale': 'მონახაზის ხელახლა დადასტურება',
        'admin.answers.confirm': 'პასუხის დადასტურება',
        'admin.answers.editPublished': 'გამოქვეყნებული პასუხის რედაქტირება',
        'admin.answers.unpublish': 'გამოქვეყნების გაუქმება',
        'admin.answers.restoreVersion': 'პასუხის ძველი ვერსიის აღდგენა',
        'admin.answers.regenerate': 'პასუხის ხელახლა მომზადება',
        'admin.ai.prepareNow': 'მომზადების დაწყება',
        'admin.ai.prepareAll': 'ყველა ახლის მომზადება',
      }),

      /* Operational state — administrator-only, and never the server's own words. */
      opsHeading: 'სამუშაო მდგომარეობა',
      opsAttempts: 'მცდელობები',
      opsLastAttempt: 'ბოლო მცდელობა',
      opsLeaseActive: 'მომზადება ამჟამად მიმდინარეობს',
      opsLastOutcome: 'ბოლო შედეგი',
      opsLastError: 'ბოლო შეფერხება',
      opsOutcomeUnknown: 'სხვა შედეგი',
      opsOutcomes: Object.freeze({
        success: 'პასუხი მომზადდა',
        failure: 'მომზადება ვერ დასრულდა',
        skipped_deleted: 'კითხვა წაშლილი იყო და გამოტოვდა',
        discarded_deleted: 'შედეგი გაუქმდა, რადგან კითხვა წაიშალა',
        lease_lost: 'მომზადება სხვა გაშვებამ დაასრულა',
        skipped_ineligible: 'კითხვა მომზადებას აღარ საჭიროებდა',
      }),
      /*
       * The preparation checklist (owner request 2026-09-28): the conditions an AI answer must pass,
       * and which of them the newest attempt failed. Steps follow `functions/src/ai/attemptStages.ts`.
       */
      opsQueue: 'მომზადების რიგი',
      opsQueueNext: '✓ ახალი კითხვა — შემდეგი საათობრივი მომზადება {time}-ზე',
      opsQueueExhausted: '✗ 3-ვე საათობრივი მცდელობა გამოყენებულია — მხოლოდ ხელით, „მომზადების დაწყება“',
      opsQueuePreparing: '… მომზადება მიმდინარეობს',
      opsQueueDone: '✓ პასუხი მომზადებულია',
      opsQueueDeleted: '✗ კითხვა წაშლილია — არ მზადდება',
      opsAttemptRun: 'ბოლო გაშვება',
      opsAttemptNone: 'ჩანაწერი არ არის — ჯერ არ ცდილა, ან ჩანაწერი 30 დღეზე ძველია',
      opsAttemptRunning: 'მიმდინარეობს',
      opsTriggers: Object.freeze({
        hourly: 'საათობრივი',
        manual_one: 'ხელით',
        manual_all: 'ხელით, ყველა ახალი',
      }),
      opsStages: Object.freeze({
        redaction: 'პირადი მონაცემების გაფილტვრა',
        retrieval: 'მასალის მოძიება ცოდნის ბაზაში',
        assembly: 'მოთხოვნის აწყობა და უსაფრთხოების შემოწმება',
        evidence: 'გაგზავნის ჩანაწერის შექმნა',
        provider: 'გარე სერვისის პასუხი',
        validation: 'პასუხის შემოწმება',
        draft: 'მონახაზის შენახვა',
      }),
      opsStagePassed: '✓ შესრულდა',
      opsStageFailed: '✗ ვერ შესრულდა',
      opsStageNotReached: '— არ მიუღწევია',
      opsErrorUnknown: 'შეფერხება დაფიქსირდა; სცადეთ ხელახლა მომზადება.',
      opsErrors: Object.freeze({
        PROVIDER_ERROR: 'გარე სერვისი დროებით მიუწვდომელი იყო.',
        EMPTY_ANSWER: 'დაბრუნებული ტექსტი ცარიელი იყო.',
        RICH_TEXT_INVALID: 'ტექსტის სტრუქტურა დაუშვებელი აღმოჩნდა.',
        MODEL_CONFIG_INVALID: 'მომზადების პარამეტრები დაუშვებელია; საჭიროა ინჟინერიის შემოწმება.',
        PRIVACY_REDACTION_FAILED: 'პირადი მონაცემების გაფილტვრა ვერ დასრულდა, ამიტომ გაგზავნა შეჩერდა.',
        EVIDENCE_WRITE_FAILED: 'ჩანაწერი ვერ შეიქმნა, ამიტომ მომზადება არ დაწყებულა.',
        PROVIDER_CALL_COUNT_INVALID: 'გარე სერვისის ანგარიში არასანდო აღმოჩნდა.',
        PROVIDER_CONFIG_MISMATCH: 'გარე სერვისის პასუხი მოთხოვნას არ შეესაბამებოდა.',
        PROVIDER_RESEARCH_NOT_PERMITTED: 'პასუხი დაუშვებელ გარე წყაროებს იყენებდა.',
        PROVIDER_SEARCH_USAGE_INVALID: 'გარე სერვისის ანგარიში არასანდო აღმოჩნდა.',
        PROVIDER_TOKEN_USAGE_INVALID: 'გარე სერვისის ანგარიში არასანდო აღმოჩნდა.',
        RETRIEVAL_USAGE_INVALID: 'მასალის მოძიების ანგარიში არასანდო აღმოჩნდა.',
        /* AS-007: the attempt stopped part-way and its time ran out; the question returned to the queue. */
        LEASE_EXPIRED: 'მომზადება შუა გზაზე შეწყდა და მისი დრო ამოიწურა.',
      }),
      /*
       * AR-007b (owner decision 2026-10-14): what the answer check found, said from its stable code only.
       * The offending value itself is never shown; confirming stays the administrator's decision.
       */
      answerNotices: Object.freeze({
        EMPTY_ANSWER: 'შესამოწმებელია: პასუხის ძირითადი ტექსტი ცარიელია.',
        WORD_COUNT_OUT_OF_RANGE: 'შესამოწმებელია: პასუხის სიგრძე რეკომენდებულ ფარგლებს სცდება.',
        INTERNAL_SOURCE_LEAK: 'შესამოწმებელია: პასუხი შიდა მასალის აღნიშვნას შეიცავს.',
        UNSUPPORTED_URL: 'შესამოწმებელია: პასუხში არის ბმული, რომლის წყარო დადასტურებული არ არის.',
        SOURCES_SECTION_WITHOUT_RESEARCH: 'შესამოწმებელია: წყაროების ჩამონათვალი გადაუმოწმებელია.',
      }),
      answerNoticeUnknown: 'შესამოწმებელია: პასუხს დამატებითი გადახედვა სჭირდება.',

      /* The editor. */
      editorHeading: 'პასუხი',
      editorLabel: 'პასუხის ტექსტი',
      toolbarLabel: 'ტექსტის ფორმატირება',
      toolbarParagraph: 'აბზაცი',
      toolbarHeading1: 'სათაური 1',
      toolbarHeading2: 'სათაური 2',
      toolbarHeading3: 'სათაური 3',
      toolbarBold: 'მუქი',
      toolbarItalic: 'დახრილი',
      toolbarBulletList: 'სია',
      toolbarOrderedList: 'დანომრილი სია',
      toolbarQuote: 'ციტატა',
      toolbarLink: 'ბმული',
      toolbarUnlink: 'ბმულის მოხსნა',
      toolbarUndo: 'დაბრუნება',
      toolbarRedo: 'გამეორება',
      /* The in-app link editor. A native prompt has no label, no validation and no focus contract. */
      linkHeading: 'ბმულის დამატება',
      linkBody: 'დაშვებულია მხოლოდ https მისამართი.',
      linkUrlLabel: 'ბმულის მისამართი',
      linkInvalid: 'მისამართი უნდა იწყებოდეს https:// -ით.',
      linkApply: 'ბმულის ჩასმა',
      linkRemove: 'ბმულის წაშლა',
      saveDraft: 'მონახაზის შენახვა',
      savePublished: 'გამოქვეყნებულის განახლება',
      saving: 'ინახება…',
      saved: 'შენახულია.',
      unsaved: 'შეუნახავი ცვლილებები',
      readOnlyDraft: 'ამ მდგომარეობაში პასუხის რედაქტირება დახურულია.',
      invalidDoc: 'ტექსტი შეიცავს დაუშვებელ ელემენტს და ვერ შეინახება. შეასწორეთ მონიშნული ადგილი.',
      invalidStoredDoc: 'შენახული პასუხის სტრუქტურა დაზიანებულია, ამიტომ ის მხოლოდ ტექსტად ჩანს და რედაქტირება დახურულია.',
      corruptDraft: 'მონახაზის ვერსია ვერ მოიძებნა. ეს მდგომარეობა შესასწავლია — რედაქტირება დახურულია, რომ ისტორია არ დაიკარგოს.',
      corruptPublished: 'გამოქვეყნებული ვერსია ვერ მოიძებნა. რედაქტირება დახურულია.',

      /* Local protection for unsaved work. */
      draftRestored: 'აღდგენილია შეუნახავი ტექსტი ამ ბრაუზერიდან.',
      draftRestoredDiscard: 'აღდგენილის გაუქმება',
      draftStale: 'ამ ბრაუზერში დარჩენილი შეუნახავი ტექსტი სხვა ვერსიას ეყრდნობა. შეადარეთ და გადაწყვიტეთ.',
      draftStaleUse: 'შენახული ტექსტის ჩასმა',
      draftStaleDiscard: 'შენახული ტექსტის წაშლა',
      draftStorageUnavailable: 'ამ ბრაუზერში შეუნახავი ტექსტის დაცვა მიუწვდომელია. შეინახეთ ხელით.',

      /* Version conflict. */
      conflictHeading: 'პასუხი სხვამ შეცვალა',
      conflictBody: 'თქვენი ტექსტი შენარჩუნებულია. შეადარეთ და გადაწყვიტეთ, რომელი დარჩეს.',
      conflictKeepMine: 'ჩემი ტექსტის ახალ ვერსიად შენახვა',
      conflictUseTheirs: 'ახალი ვერსიის აღება',

      /* The diff. */
      diffHeading: 'ვერსიების შედარება',
      diffPrevious: 'წინა ვერსია',
      diffNext: 'ახალი ვერსია',
      diffAdded: 'დამატებულია',
      diffRemoved: 'ამოღებულია',
      diffIdentical: 'ტექსტი იდენტურია.',
      diffCompare: 'შედარება',
      diffClose: 'შედარების დახურვა',
      diffMine: 'ჩემი ტექსტი',

      /* Question text and visibility. */
      editText: 'კითხვის ტექსტის რედაქტირება',
      textLabel: 'კითხვის ტექსტი',
      textCounter: 'სიმბოლო',
      saveText: 'ტექსტის შენახვა',
      cancelText: 'გაუქმება',
      scopeLabel: 'ხილვადობა',
      /* The select's own accessible name: the section heading already says „ხილვადობა". */
      scopeSelectLabel: 'ვის უჩანს ეს კითხვა',
      scopeGroup: 'ჯგუფისთვის',
      scopeAuthor: 'მხოლოდ ავტორისთვის',
      saveScope: 'ხილვადობის შენახვა',
      scopeSelfPaced: 'თვითსწავლებად კურსზე კითხვა მხოლოდ ავტორს უჩანს, ამიტომ ხილვადობა არ იცვლება.',

      /* Preparation and publication. */
      prepareNow: 'ახლავე მოამზადე',
      prepareNowHeading: 'მომზადების დაწყება',
      prepareNowBody: 'მონიშნულ კითხვებზე მომზადება დაიწყება. მდგომარეობა სიაში თავისით განახლდება.',
      prepareNowConfirm: 'მომზადების დაწყება',
      prepareAll: 'ყველა ახლის მომზადება',
      prepareAllHeading: 'ყველა ახალი კითხვის მომზადება',
      prepareAllBody: 'მომზადება დაიწყება ყველა ახალ კითხვაზე, რომელიც ამჟამად შესაფერისია.',
      prepareAllConfirm: 'დაწყება',
      prepareAllResult: 'დაიწყო',
      prepareAllSkipped: 'გამოტოვდა',
      regenerate: 'ხელახლა მომზადება',
      enqueued: 'მომზადება დაიწყო. მდგომარეობა აქვე განახლდება.',
      alreadyRunning: 'მომზადება უკვე მიმდინარეობს ამ კითხვაზე.',
      overrideStale: 'პასუხი კვლავ შესაბამისია',
      staleDraft: 'კითხვის ტექსტი შეიცვალა, ამიტომ მონახაზი მოძველებულია. ან მოამზადეთ ხელახლა, ან ცხადად დაადასტურეთ.',
      confirmAction: 'დადასტურება',
      confirmHeading: 'პასუხების დადასტურება',
      confirmBody: 'ქვემოთ ნაჩვენებია, რა შესრულდება. დადასტურებული პასუხი სტუდენტს გამოუჩნდება.',
      confirmConfirm: 'დადასტურება და გამოქვეყნება',
      confirmPreviewHeading: 'შემოწმების შედეგი',
      confirmBlockedByDraft: 'პასუხში შეუნახავი ცვლილებებია. ჯერ შეინახეთ მონახაზი, შემდეგ დაადასტურეთ — დადასტურება სერვერზე შენახულ ვერსიას აქვეყნებს.',
      previewChecking: 'მოწმდება…',
      previewFailed: 'შემოწმება ვერ დასრულდა, ამიტომ დადასტურება ჯერ მიუწვდომელია.',
      previewRetry: 'შემოწმების გამეორება',
      previewApplied: 'შესრულდება',
      previewSkipped: 'გამოტოვდება',
      previewFailedRows: 'ვერ შესრულდება',
      confirmBlocked: 'დადასტურება ამ მდგომარეობაში შეუძლებელია.',
      unpublishAction: 'გამოქვეყნების გაუქმება',
      unpublishHeading: 'გამოქვეყნების გაუქმება',
      unpublishBody: 'მონიშნული პასუხები სტუდენტებს აღარ გამოუჩნდებათ. ყველა ვერსია შენარჩუნდება.',
      /* A dialog's confirm never repeats the word its own cancel uses, nor the bar action's label. */
      unpublishConfirm: 'დიახ, გაუქმდეს გამოქვეყნება',
      deleteAction: 'წაშლა',
      deleteHeading: 'კითხვების წაშლა',
      deleteBody: 'მონიშნული კითხვები სტუდენტებს აღარ გამოუჩნდებათ. აღდგენა ინტერფეისიდან შეუძლებელია.',
      deleteConfirm: 'დიახ, წაიშალოს',
      deletedNotice: 'ეს კითხვა წაშლილია; მოქმედებები დახურულია.',
      unsavedLeaveHeading: 'შეუნახავი ცვლილებები',
      unsavedLeaveBody: 'პასუხში შეუნახავი ცვლილებებია. სხვა კითხვაზე გადასვლისას ისინი დაიკარგება.',
      unsavedLeaveConfirm: 'გადასვლა შენახვის გარეშე',

      /*
       * *(Claude Design Q&A screen, `design/app/admin-qa`)* The words the owner's reviewed design
       * (`Simple-QA-Admin-QA-{Desktop,Mobile}`) says in places its adapter has to compose itself —
       * counts, toasts, save states and empty states — quoted from the design's own logic so the
       * screen says exactly what was drawn. `savedLocally` is the one line of new copy (OD-D6.1): the
       * design's save indicator had no words for a local-only protection.
       */
      design: Object.freeze({
        questionsCount: 'კითხვა',
        emptyAllTitle: 'კითხვა არ არის',
        emptyAllBody: 'ყველა კითხვას პასუხი გაეცა.',
        noResultsTitle: 'შედეგი არ მოიძებნა',
        emptyDeletedTitle: 'წაშლილი კითხვა არ არის',
        emptyFilterTitle: 'ამ ფილტრით კითხვა არ არის',
        emptyDeletedBody: 'წაშლილი კითხვები აქ ჩნდება და აღდგენადია.',
        emptyFilterBody: 'შეცვალე ფილტრი ან ძებნა.',
        answersCount: 'პასუხი',
        batchWillPublish: 'გამოქვეყნდება',
        batchPublished: 'გამოქვეყნდა',
        batchSkipped: 'გამოტოვდა',
        batchWillSkip: 'გამოტოვდება',
        skipConfirmed: 'უკვე დადასტურებულია',
        skipStale: 'მოძველებული დრაფტი',
        skipEmpty: 'ცარიელი დრაფტი',
        saveClean: 'ცვლილება არ არის',
        saveFailed: 'შენახვა ვერ მოხერხდა',
        draftSaved: 'დრაფტი შენახულია',
        publishedOn: 'გამოქვეყნდა',
        updatedOn: 'განახლდა',
        movedToTrash: 'კითხვა გადავიდა წაშლილებში',
        questionRestored: 'კითხვა აღდგა',
        questionPurged: 'კითხვა სამუდამოდ წაიშალა',
        visibilityChanged: 'ხილვადობა შეიცვალა',
        textSaved: 'კითხვის ტექსტი შენახულია',
        answerPublished: 'პასუხი დადასტურდა',
        /* *(CE-9, export 9)* A continuing-education question's fixed visibility, in the publish toast. */
        ceBase: 'განგრძობითი სწავლების ბაზა',
        answerUnpublished: 'დადასტურება მოიხსნა — სტუდენტს პასუხი აღარ უჩანს',
        answersUnpublished: 'პასუხს მოეხსნა დადასტურება',
        updatePublished: 'განახლება გამოქვეყნდა',
        versionRestored: 'ვერსია აღდგა',
        staleKept: 'დრაფტი შენარჩუნდა — გამოქვეყნება ხელმისაწვდომია',
        selectionQueued: 'მონიშნული ფრაგმენტი რიგშია გადასამუშავებლად — დანარჩენი პასუხი უცვლელია',
        nothingToPrepare: 'მოსამზადებელი ახალი კითხვა არ არის',
        prepareQueued: 'კითხვა მომზადების რიგშია',
        validationEmpty: 'პასუხი ცარიელია',
        validationLink: 'ბმული დაუშვებელია — მხოლოდ https://',
        validationFormat: 'პასუხში მხარდაუჭერელი ფორმატია',
        recoveryDiscarded: 'აღდგენილი ტექსტი გაუქმდა',
        minutesAgo: 'წუთის წინ',
        hoursAgo: 'საათის წინ',
        yesterday: 'გუშინ',
        daysAgo: 'დღის წინ',
        /** *(OD-D6.1)* The save indicator reports the local protection, not a server save. */
        savedLocally: 'შენახულია მხოლოდ ამ ბრაუზერში',
      }),
    }),

    /*
     * *(Claude Design Home screen, `design/app/admin-home`)* The words the owner's reviewed design
     * (`Simple-QA-Admin-Home-{Desktop,Mobile}`) says in places its adapter composes itself — work
     * rows, facts, toasts, the timetable and the search groups — quoted from the design's own logic so
     * the screen says exactly what was drawn. The ages are the design's short form („2 სთ წინ“, OD-H2).
     */
    home: Object.freeze({
      design: Object.freeze({
        workCount: 'სამუშაო',
        urgentTag: 'გადაუდებელი',
        questionTag: 'კითხვა',
        accessTag: 'წვდომა',
        minutesAgo: 'წთ წინ',
        hoursAgo: 'სთ წინ',
        daysAgo: 'დღის წინ',
        factAuthor: 'ავტორი',
        factGroup: 'ჯგუფი',
        factAsked: 'დასმულია',
        factAnswers: 'პასუხები',
        goToAnswer: 'პასუხზე გადასვლა',
        requestsRegistration: 'ითხოვს რეგისტრაციას',
        registration: 'რეგისტრაცია',
        requestBody: 'რეგისტრაციის მოთხოვნა შეიქმნა Google-ით პირველი შესვლისას და დასამტკიცებელია.',
        factGoogleName: 'სახელი Google-ში',
        factEmail: 'ელფოსტა',
        factRequestTime: 'მოთხოვნის დრო',
        approveAccess: 'წვდომის დამტკიცება',
        accessApproved: 'რეგისტრაცია დამტკიცდა',
        accessRejected: 'რეგისტრაციის მოთხოვნა უარყოფილია',
        actionFailed: 'მოქმედება ვერ შესრულდა',
        lecture: 'ლექცია',
        groupPrefix: 'ჯგუფი',
        timetableAria: 'განრიგი',
        timetableDone: 'დასრულდა',
        timetableLive: 'მიმდინარე',
        timetableNext: 'შემდეგი',
        timetablePlanned: 'დაგეგმილი',
        untimed: 'დრო არ არის',
        /** A running lecture's end: „14:00-მდე“. */
        until: 'მდე',
        searchQuestions: 'კითხვები',
        searchCourses: 'კურსები',
        searchGroups: 'ჯგუფები',
        searchStudents: 'სტუდენტები',
        /** The search's partial-failure detail: „სტუდენტების შედეგები არ ჩანს.“ */
        searchFailedKinds: Object.freeze({
          questions: 'კითხვების',
          courses: 'კურსების',
          groups: 'ჯგუფების',
          students: 'სტუდენტების',
        }),
        searchPartialBody: 'შედეგები არ ჩანს.',
        studentsCount: 'სტუდენტი',
        courseAria: 'კურსი',
        fillRequired: 'შეავსე სავალდებულო ველები',
        inviteExists: 'მოწვევა უკვე არსებობს',
        inviteAdded: 'მოწვევა დაემატა',
        inviteFailed: 'მოწვევა ვერ დაემატა',
        inviteDuplicate: 'ამ ელფოსტაზე მოწვევა უკვე მოლოდინშია',
      }),
    }),

    /** The link the shared batch-result notice offers whenever the server reserved a batch (T071). */
    openBatch: 'ჯგუფური ოპერაციის გახსნა',

    /**
     * The action history — T071 (FR-PANEL-01 (10), FR-AUD-01/02).
     *
     * Two rules govern this block. **A stored value reaches the screen only because a named field
     * put it there**: `audit-change.ts` is an allowlist, `fields` is its label set, and `values` is
     * the enumeration vocabulary it formats with — so a payload this catalogue has not described
     * degrades to `changedKeys` instead of being printed. **The action names are data, not copy**:
     * `actions` maps the exact strings the server stores, and `actionUnknown` is what a string
     * written by a later task resolves to until somebody names it here.
     */
    /*
     * *(Claude Design Groups screen, `design/app/admin-groups`)* The words the owner's design
     * (`Simple-QA-Admin-Groups-{Desktop,Mobile}`) says in places its adapter composes itself from real
     * data — toasts, previews and results, the template diff, the create sheet's summary and field
     * errors — quoted from the design's own logic so the screen says exactly what was drawn. Nothing is
     * sent to a student: an invitation waits for the address's first Google sign-in (OD-D2.2).
     */
    groups: Object.freeze({
      design: Object.freeze({
        /* *(OD-2026-10-12)* „ამ ჯგუფიდან მოხსნა“, in the design's words. */
        materialUnshared: 'მასალა მოიხსნა ჯგუფიდან',
        materialMenuAria: 'მასალის მოქმედებები',
        unshareFromGroup: 'ამ ჯგუფიდან მოხსნა',
        deleteForAll: 'წაშლა ყველასთვის',
        lectureWord: 'ლექცია',
        extraLecture: 'დამატებითი ლექცია',
        dayWord: 'დღე',
        weekWord: 'კვირა',
        monthWord: 'თვე',
        minutesWord: 'წთ',
        toSuffix: '-ზე',
        untilSuffix: '-მდე',
        editorLabel: 'მასალის შიგთავსი',
        groupSectionTitle: 'ჯგუფის მასალები',
        groupNotFound: 'ჯგუფი ვერ მოიძებნა',
        groupsCapped: 'ნაჩვენებია თითოეული დირექტორიის პირველი 500 ჯგუფი',
        signOutBody: 'მიმდინარე სესია დაიხურება.',
        undone: 'მოქმედება დაბრუნდა',
        restored: 'ჯგუფი აღდგა რეესტრში',
        archived: 'ჯგუფი დაარქივდა',
        unarchived: 'ჯგუფი დაბრუნდა რეესტრში',
        deleted: 'ჯგუფი გადავიდა წაშლილებში — აღდგენა შესაძლებელია',
        created: 'ჯგუფი შეიქმნა',
        createdWithSchedule: 'ჯგუფი შეიქმნა — განრიგი აეწყო კურსის შაბლონიდან',
        lectureCount: 'ლექცია',
        nameMin3: 'დასახელება სავალდებულოა — მინიმუმ 3 სიმბოლო',
        nameTaken: 'ამ კურსში ასეთი დასახელება უკვე არსებობს',
        nameSaved: 'დასახელება შენახულია',
        pickCourse: 'აირჩიე კურსი',
        pickStart: 'აირჩიე დაწყების თარიღი',
        timeFormat: 'მიუთითე დრო ფორმატით 18:00',
        monthsMin: 'თვეების რაოდენობა მინიმუმ 1 უნდა იყოს',
        endLabel: 'დასრულება:',
        endComputed: 'დასრულება გამოითვლება დაწყების თარიღიდან — კურსის',
        courseWeeks: 'კურსის',
        accessOnly: 'მხოლოდ წვდომა — ლექციები არ ემატება.',
        accessOnlyUntil: 'მხოლოდ წვდომა — მასალა სტუდენტს',
        accessOnlyTail: '-მდე რჩება, ლექციები არ ემატება.',
        schedAuto: 'განრიგი შეიქმნება ჯგუფის შექმნისას',
        schedFromTemplate: 'ლექცია კურსის შაბლონიდან',
        oncePerWeek: 'კვირაში ერთხელ',
        perWeekPrefix: 'კვირაში',
        perWeekSuffix: '-ჯერ',
        outOfPeriod: 'თარიღი ჯგუფის პერიოდს სცდება',
        outOfPeriodTail: '-ის შემდეგაა',
        scheduleSaved: 'განრიგი შენახულია',
        scheduleSaveFailed: 'განრიგი ვერ შეინახა — სცადე ხელახლა',
        regenerated: 'განრიგი ხელახლა აეწყო შაბლონიდან — შეინახე, რომ ძალაში შევიდეს',
        lecturesMovedTo: 'ლექცია გადაიწია',
        timeSaveFailed: 'დრო ვერ შეინახა — სცადე ხელახლა',
        extraSaved: 'კვირა წვდომა — ლექციები არ ემატება',
        extraRemoved: 'დამატებითი წვდომა მოიხსნა',
        monthsSaved: 'წვდომის ხანგრძლივობა',
        monthsSaveFailed: 'ხანგრძლივობა ვერ შეინახა — სცადე ხელახლა',
        diffAdded: 'დაემატა',
        diffRemoved: 'წაიშალა',
        diffMoved: 'გადალაგდა',
        diffFrom: 'დან',
        diffPosition: 'პოზიციაზე',
        firstFrom: 'პირველიდან',
        firstTo: 'პირველ',
        diffWeeks: 'კვირების რაოდენობა',
        diffMeetings: 'შეხვედრა კვირაში',
        diffDuration: 'ლექციის ხანგრძლივობა',
        tplApplied: 'შაბლონი განახლდა',
        tplAppliedTitle: '1 გამოყენდა',
        tplFailedTitle: '1 ვერ შესრულდა',
        tplSkippedTitle: '1 გამოტოვდა',
        tplAppliedWhy: 'ჯგუფი კურსის ამჟამინდელ შაბლონს შეესაბამება',
        courseMaterialAria: 'კურსის მასალა',
        groupMaterialAria: 'ჯგუფის მასალა',
        groupMatsMeta: 'მასალა · მხოლოდ ამ ჯგუფისთვის',
        unlockAria: 'გაიხსნება ლექციის დაწყებიდან 3 საათში',
        unlockNote: 'გაიხსნება ლექციის დაწყებიდან 3 საათში',
        materialAdded: 'მასალა დაემატა ამ ჯგუფს',
        /* The group material editor, as the course page's (owner, 2026-09-29; export 18). */
        materialUpdated: 'მასალა განახლდა',
        materialSheetTitle: 'მასალის დამატება',
        imagesOnCoursePage: 'სურათის ჩასმა ჯგუფის მასალაში ჯერ არ ხდება — სურათიანი მასალა კურსის გვერდზე დაამატე.',
        textHasImages: 'ამ მასალის ტექსტში სურათებია, ამიტომ ტექსტი აქ არ რედაქტირდება — გახსენი კურსის გვერდზე. სათაური, ლექცია და ხილვადობა აქაც იცვლება.',
        bodyLoadFailed: 'მასალის ტექსტი ვერ ჩაიტვირთა — დახურე და სცადე ხელახლა.',
        materialDeleted: 'მასალა წაიშალა ამ ჯგუფიდან',
        titleMin3: 'სათაური სავალდებულოა — მინიმუმ 3 სიმბოლო',
        // *(OD-2026-10-03)* The material window's course-section field.
        sectionRequired: 'აირჩიე სექცია',
        // *(Export 20)* A lecture changed in this session and not saved yet; its line carries „დაბრუნება“.
        lectureUnsaved: 'შეცვლილია — ჯერ არ შენახულა',
        sectionFixed: 'შექმნილი მასალის სექცია არ იცვლება — წაშალე და დაამატე სასურველ სექციაში.',
        uploadPdfFirst: 'ჯერ ატვირთე PDF ფაილი',
        urlRequired: 'მისამართი სავალდებულოა და უნდა იწყებოდეს https://-ით',
        urlInvalid: 'მისამართი არასწორია — დაიწყე https://-ით და მიუთითე სრული დომენი',
        videoHost: 'ვიდეო მხოლოდ YouTube-იდან, Vimeo-დან, Gumlet-იდან ან Google Drive-იდან — ჩასვი https:// ბმული ან ჩასასმელი კოდი',
        docInvalid: 'შიგთავსი ვერ შეინახება — შეამოწმე ტექსტი',
        fileUploaded: 'ფაილი აიტვირთა',
        fileRemoved: 'ფაილი მოიხსნა',
        uploadStopped: 'ატვირთვა შეჩერდა',
        uploadFailed: 'ატვირთვა ვერ მოხერხდა — სცადე ხელახლა.',
        pdfNotPdf: 'ეს ფორმატი არ არის დაშვებული — აირჩიე PDF.',
        pdfTooLarge: 'ფაილი 50 MB-ს აღემატება — შეამცირე ან დაყავი ნაწილებად.',
        pdfEmpty: 'ფაილი ცარიელია — აირჩიე სხვა PDF.',
        linkAdded: 'ბმული დაემატა',
        linkChanged: 'ბმული შეიცვალა',
        linkRemoved: 'ბმული მოიხსნა',
        tagDone: 'შესრულდა',
        tagSkipped: 'გამოტოვდა',
        tagFailed: 'ვერ შესრულდა',
        tagApplied: 'გამოყენდა',
        tagInvited: 'მოწვეულია',
        tagWillSkip: 'გამოტოვდება',
        tagWillClose: 'დაიხურება',
        tagWillExtend: 'გაგრძელდება',
        actionDone: 'შედეგი მზადაა',
        partialDone: 'შედეგი მზადაა — ნაწილი გამოტოვდა',
        retried: 'დარჩენილი ჩანაწერი ხელახლა გაიგზავნა',
        failedWhy: 'ვერ შესრულდა — სცადე ხელახლა',
        skipPending: 'მოწვევა ჯერ არ დადასტურებულა',
        closeActive: 'აქტიური ჩარიცხვა',
        closeExpiring: 'უფლება ისედაც იწურება',
        closedWhy: 'ჩარიცხვა დაიხურა',
        transferredPrefix: 'გადაყვანილია',
        transferredSuffix: '-ში',
        enrolledWhy: 'ჩაირიცხა ჯგუფში',
        otherGroupPrefix: 'ამ კურსის სხვა ჯგუფშია',
        useTransfer: 'გამოიყენე გადაყვანა',
        pickStudent: 'აირჩიე ან მოიწვიე მინიმუმ ერთი სტუდენტი',
        inviteAdded: 'მოწვევა დაემატა',
        inviteWillWait: 'დაემატება პირველი Google შესვლისას',
        enHint: 'მოძებნე არსებული სტუდენტი ან შეიყვანე ახალი ელფოსტა — მოწვევა დაემატება ამ ელფოსტით პირველი Google შესვლისას.',
        pendingWord: 'მოლოდინში',
        xNow: 'ახლა',
        xNew: 'ახალი',
        xUnchanged: 'უცვლელი',
        xWillExtend: 'გაგრძელდება',
        xWillSkip: 'გამოტოვდება',
        xPickDate: 'აირჩიე ახალი დასრულების თარიღი — ცვლილება ძალაში შედის „გაგრძელებაზე“ დაჭერის შემდეგ.',
        xSkipPending: 'მოწვევა ჯერ არ დადასტურებულა — წვდომის ვადა ჯერ არ დაწყებულა',
        xSkipEarly: 'არჩეული თარიღი მიმდინარე ვადაზე ადრეა ან ემთხვევა',
        nothingToExtend: 'გასაგრძელებელი ჩანაწერი არ არის — ყველა გამოტოვდა',
        previewFailed: 'გადახედვა ვერ ჩაიტვირთა — სცადე ხელახლა',
        extendedMany: 'სტუდენტს ვადა გაუგრძელდა',
        extendedOne: 'ვადა გაგრძელდა',
        skippedWord: 'გამოტოვდა',
      }),
    }),
    /** *(AD-6)* The Courses register and wizard on the Claude Design page — `design/app/admin-courses`. */
    courses: Object.freeze({
      design: Object.freeze({
        courseNotFound: 'კურსი ვერ მოიძებნა',
        editorLabel: 'კურსის აღწერა',
        coursesCapped: 'თითო ჩანართში ნაჩვენებია პირველი 200 კურსი — დანარჩენი ჯერ არ ჩანს',
        impactFailed: 'წაშლის შედეგის შემოწმება ვერ მოხერხდა — სცადე ხელახლა',
        // The register row's kicker line (OD-C2).
        kickerNext: 'შემდეგი',
        kickerTemplate: 'შაბლონი',
        kickerMaterials: 'მასალები',
        kickerEnded: 'დასრულდა',
        kickerPurge: 'წაიშლება',
        lectureWord: 'ლექცია',
        topicWord: 'თემა',
        materialWord: 'მასალა',
        hiddenWord: 'დამალული',
        noGroupYet: 'ჯგუფი ჯერ არ არის',
        noUpcoming: 'დაგეგმილი ლექცია არ არის',
        // *(CE-10)* A continuing-education row's kicker line, as export 9 draws it.
        kickerAccess: 'წვდომა',
        continuingAccess: '365 დღე ჩარიცხვიდან',
        // The design's own status labels (`lifeChip`).
        lifeLive: 'აქტიური',
        lifeDraft: 'დრაფტი',
        lifeArchived: 'დაარქივებული',
        lifeTrashed: 'წაშლილი',
        // Outcomes — the design's own toasts, said after the server has answered.
        activated: 'კურსი გააქტიურდა',
        unarchived: 'კურსი დაბრუნდა არქივიდან — აქტიურია',
        archived: 'კურსი დაარქივდა',
        trashed: 'კურსი გადავიდა წაშლილებში — აღდგენა შესაძლებელია',
        restoredPrefix: 'კურსი აღდგა და დაბრუნდა „',
        restoredSuffix: '“ სტატუსზე',
        purged: 'კურსი სამუდამოდ წაიშალა',
        created: 'კურსი შეიქმნა',
        updated: 'კურსი განახლდა',
        saved: 'შენახულია',
        draftSaved: 'დრაფტი შენახულია',
        // The wizard.
        toFix: 'გასასწორებელია',
        fieldWord: 'ველი',
        openStep: 'გახსენი შესაბამისი ნაბიჯი',
        step2: 'ნაბიჯი 2',
        step2Aria: 'გადასვლა ნაბიჯზე 2 — ლექციების შაბლონი',
        nameRequired: 'დასახელება სავალდებულოა — მინიმუმ 6 სიმბოლო',
        coverUploaded: 'ყდა აიტვირთა',
        coverPickLabel: 'ყდის ატვირთვა · PNG, JPG, WebP',
        coverWrongType: 'ფაილი ვერ აიტვირთა — მხოლოდ PNG, JPG ან WebP',
        coverTooLarge: 'ფაილი ვერ აიტვირთა — ზომა 10 MB-ს აღემატება',
        coverRefused: 'ფაილი ვერ აიტვირთა — სურათი ვერ წაიკითხა, აირჩიე სხვა',
        pasteRefused: 'ბუფერის წაკითხვა ვერ მოხერხდა — ჩასვი Ctrl+V-ით',
      }),
    }),
    /** *(AD-6)* The Course Studio on the Claude Design page — `design/app/admin-course-studio`. */
    studio: Object.freeze({
      /**
       * The words the adapter builds from real data (`design/app/admin-course-studio`). Most are the
       * design's own (its labels the adapter matches, its validation lines, its toasts); the few the
       * design has not drawn are marked.
       */
      design: Object.freeze({
        courseNotFound: 'კურსი ვერ მოიძებნა',
        // The design's own labels, matched on the generated menus, fields and controls.
        menuEdit: 'კურსის რედაქტირება',
        menuPreview: 'სტუდენტის ხედვა',
        menuArchive: 'დაარქივება',
        menuDelete: 'წაშლა',
        menuUnarchive: 'არქივიდან დაბრუნება',
        fieldLecture: 'დაკავშირებული ლექცია',
        /* *(OD-2026-10-12)* The material window's section chips: choosing another one moves the material. */
        fieldSection: 'სექცია',
        fieldGroup: 'ჯგუფი',
        fieldNote: 'შენიშვნა — არასავალდებულო',
        hide: 'დამალვა',
        publish: 'გამოქვეყნება',
        moveUp: 'ზემოთ გადატანა',
        moveDown: 'ქვემოთ გადატანა',
        changeLecture: 'ლექციაზე მიბმის შეცვლა',
        fmtLink: 'ბმული',
        signOutLabel: 'სისტემიდან გასვლა',
        profileLabel: 'პროფილის პარამეტრები',
        // Words of the drawn rows.
        lectureWord: 'ლექცია',
        groupWord: 'ჯგუფი',
        studentWord: 'სტუდენტი',
        materialWord: 'მასალა',
        sectionWord: 'სექცია',
        orderWord: 'რიგი',
        defaultMaterials: 'ნაგულისხმევი მასალა',
        wholeCourse: 'მთელი კურსი',
        noDate: 'თარიღი არ არის',
        noTime: 'დრო არ არის',
        noDateYet: 'თარიღი ჯერ არ არის',
        notScheduled: 'ჯერ არ არის დაგეგმილი',
        weekdays: Object.freeze(['კვირა', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი']),
        andWord: 'და',
        monthsFromEnrolment: 'თვე ჩარიცხვიდან',
        startsPrefix: 'იწყება',
        templateStale: 'შაბლონი მოძველდა',
        perWeek: 'კვირაში',
        meetingWord: 'შეხვედრა',
        minutesLong: 'წუთი',
        minutesShort: 'წთ',
        paragraphWord: 'აბზაცი',
        listWord: 'სია',
        videoWord: 'ვიდეო ჩანაწერი',
        noFile: 'ფაილი ჯერ არ არის ატვირთული',
        needsSource: 'წყარო აკლია',
        joined: 'ჩაირიცხა',
        active: 'აქტიური',
        expiring: 'იწურება',
        enrolmentStatus: 'ჩარიცხვის სტატუსი',
        questionRight: 'კითხვების უფლება',
        openedMaterials: 'გახსნილი მასალა',
        remainingPrefix: 'დარჩენილია',
        fromSuffix: '-დან',
        waitingQuestions: 'პასუხგაუცემელი კითხვა',
        noWaitingQuestions: 'პასუხგაუცემელი კითხვა არ არის',
        transferTo: 'ჯგუფში გადაყვანა',
        transferredPrefix: 'გადავიდა ჯგუფში',
        progressKept: 'პროგრესი შენარჩუნებულია',
        extendRight: 'კითხვების უფლების გახანგრძლივება',
        /*
         * *(CE-3, CE-10 — export 9)* A continuing-education course: its member's renewal outcome, the
         * overview's material fact, the invite's fixed „ჯგუფის გარეშე“ and its outcome.
         */
        ceRenewed: 'წვდომა განახლდა',
        ceAllOpen: 'ყველა ღია',
        ceNoGroup: 'ჯგუფის გარეშე',
        /* *(CE-9)* A continuing-education question's fixed visibility — Admin Q&A's own line; no switch. */
        ceVisibility: 'განგრძობითი სწავლების ბაზა · ყველა წევრი, ავტორის სახელით',
        ceVisibilityAria: 'ხილვადობა — განგრძობითი სწავლების ბაზა · ყველა წევრი, ავტორის სახელით',
        // The overview (OD-CS11).
        factRhythm: 'რიტმი',
        factMeetings: 'შეხვედრა',
        factDuration: 'ხანგრძლივობა',
        factLectures: 'ლექცია',
        factStudents: 'სტუდენტი',
        factGroup: 'ჯგუფი',
        groupStarts: 'ჯგუფი იწყება',
        publishedCount: 'გამოქვეყნებული',
        hiddenCount: 'დამალული',
        invitesWaiting: 'მოწვევა მოლოდინშია',
        openQuestionAria: 'კითხვის გახსნა',
        noNextDraft: 'კურსი დრაფტშია — ჯგუფის ლექციები გააქტიურების შემდეგ დაიგეგმება.',
        noNextSelfPaced: 'კურსი დამოუკიდებელი ტემპით მიმდინარეობს და განრიგი არ აქვს — სტუდენტი მასალას საკუთარი ტემპით გადის.',
        noNextNoGroups: 'კურსს ჯგუფი ჯერ არ აქვს — ლექციები ჯგუფის დაწყების თარიღიდან დაიგეგმება.',
        noNextActivate: 'კურსის გააქტიურება',
        noNextAddGroup: 'ჯგუფის დამატება',
        noNextTemplate: 'ლექციების შაბლონი',
        // Not drawn: a full course whose groups have held every lecture; its route sub-lines without a
        // starting group or a waiting invitation.
        noNextDone: 'დაგეგმილი ლექცია აღარ არის — ჯგუფების ლექციები ჩატარებულია.',
        groupsActive: 'ჯგუფი მიმდინარეობს',
        noGroupsYet: 'ჯგუფი ჯერ არ არის',
        noInvites: 'მოლოდინში მოწვევა არ არის',
        // The header (OD-CS10).
        activated: 'კურსი გააქტიურდა — სტუდენტებს ჩანს და ჩარიცხვა გახსნილია',
        archived: 'კურსი დაარქივდა',
        unarchived: 'კურსი დაბრუნდა არქივიდან',
        previewBanner: 'სტუდენტის ხედვა',
        previewTail: 'ადმინის მოქმედებები დამალულია',
        previewOn: 'სტუდენტის ხედვა ჩართულია',
        /** Not drawn: the student view with no running group to show. */
        previewNoGroup: 'სტუდენტის ხედვისთვის აქტიური ჯგუფი ჯერ არ არის',
        draftNoEnrolment: 'კურსი დრაფტშია — ჩარიცხვა გააქტიურების შემდეგ გახდება შესაძლებელი',
        noWaiting: 'პასუხგაუცემელი კითხვა არ არის',
        // The in-course search (OD-CS12).
        searchLectures: 'ლექციები',
        searchGroups: 'ჯგუფები',
        searchStudents: 'სტუდენტები',
        searchLectureAria: 'ლექციაზე გადასვლა',
        searchGroupAria: 'ჯგუფზე გადასვლა',
        searchStudentAria: 'სტუდენტზე გადასვლა',
        // Lectures (OD-CS3).
        lectureTitleRequired: 'სათაური სავალდებულოა — ლექცია უსათაუროდ ვერ შეინახება.',
        lectureUpdated: 'განახლდა',
        lectureAdded: 'დაემატა შაბლონს',
        // Sections and materials (OD-CS5, OD-CS6).
        sectionTitleRequired: 'სექციის სათაური სავალდებულოა.',
        /** Not drawn: a section scoped to a group that is no longer in the course. */
        sectionGroupMissing: 'ეს ჯგუფი აღარ მოიძებნა — აირჩიე სხვა არეალი.',
        sectionAdded: 'დაემატა',
        sectionUpdated: 'განახლდა',
        sectionPublished: 'გამოქვეყნდა',
        sectionHiddenPrefix: 'დამალულია —',
        sectionHiddenTail: 'მასალა სტუდენტებს აღარ ჩანს',
        sectionFirstAlready: 'სექცია უკვე პირველია',
        sectionLastAlready: 'სექცია უკვე ბოლოა',
        /** Not drawn: a material needs a section first. */
        sectionFirst: 'ჯერ დაამატე სექცია — მასალა სექციაში ემატება.',
        movedUp: 'გადავიდა ზემოთ',
        movedDown: 'გადავიდა ქვემოთ',
        materialFirstAlready: 'მასალა სექციაში უკვე პირველია',
        materialLastAlready: 'მასალა სექციაში უკვე ბოლოა',
        materialPublished: 'გამოქვეყნებულია',
        materialHidden: 'დამალულია',
        materialTitleRequired: 'სათაური სავალდებულოა — მასალა უსათაუროდ ვერ შეინახება.',
        richEmpty: 'ტექსტური მასალა ცარიელი ვერ იქნება — შეავსე შიგთავსი.',
        /** Not drawn: why a text material cannot be stored (2026-09-25). */
        richTooLarge: 'ტექსტი ერთი მასალისთვის ძალიან დიდია (ათასზე მეტი გვერდი) — დაყავი ორ ან მეტ მასალად.',
        richLinkUnsafe: 'ტექსტში არის ბმული, რომელიც https://-ით არ იწყება — შეასწორე ან წაშალე ის ბმული.',
        richInvalid: 'ჩასმული ტექსტის ნაწილი ვერ შეინახება — წაშალე ბოლოს ჩასმული ნაწილი და ჩასვი ხელახლა (კოდი: {code}).',
        pdfMissing: 'წყარო აკლია — აირჩიე მიმაგრებული ფაილი.',
        urlMissing: 'წყარო აკლია — მიუთითე მისამართი.',
        urlInvalid: 'მისამართი არასწორია — უნდა იწყებოდეს https:// -ით და არ შეიცავდეს ჰარეს.',
        materialAdded: 'მასალა დაემატა',
        materialUpdated: 'მასალა განახლდა',
        bodySaved: 'მასალის ტექსტი შენახულია',
        /** Not drawn: autosave (owner, 2026-09-25) — a text material is kept as a hidden draft while it is written. */
        draftUntitled: 'უსათაურო დრაფტი',
        autosavedAt: 'შენახულია',
        autosaving: 'ინახება…',
        bodyLoading: 'მასალის ტექსტი იტვირთება — ცოტა მოიცადე.',
        bodyLoadFailed: 'მასალის ტექსტი ვერ ჩაიტვირთა — დახურე და ხელახლა გახსენი.',
        autosaveFailedShort: 'ვერ შეინახა',
        draftKept: 'დრაფტი შენახულია დამალულ მასალად — გააგრძელე ნებისმიერ დროს.',
        autosaveFailed: 'ცვლილებები ვერ შეინახა — სცადე „ტექსტის შენახვა“.',
        movedToTrash: 'გადავიდა სანაგვეში 30 დღით',
        restoredFromTrash: 'დაბრუნდა სანაგვიდან',
        /* *(OD-2026-10-12)* The move by section choice and the section Trash, in the design's words. */
        materialMoved: 'მასალა გადატანილია',
        sectionDeleted: 'სექცია წაიშალა',
        sectionRestored: 'სექცია დაბრუნდა სანაგვიდან',
        materialRestored: 'მასალა დაბრუნდა სანაგვიდან',
        sectionDeleteMoving: 'სექციაში მასალა ახლა გადაიტანება — სცადე ცოტა ხანში.',
        alreadyInTrash: 'მასალა უკვე სანაგვეშია.',
        trashByAdmin: 'ადმინისტრატორი',
        extraAdded: 'დამატებითი მასალა დაემატა',
        extraRemoved: 'დამატებითი მასალა მოიხსნა',
        selfPacedProgress: 'დამოუკიდებელი ტემპი — ლექციების განრიგი არ აქვს',
        openRule: 'ლექციის დაწყებიდან 3 საათში',
        openedPrefix: 'გაიხსნა',
        opensPrefix: 'გაიხსნება',
        openUnknown: 'გახსნის დრო ჯერ არ არის',
        imageType: 'სურათი უნდა იყოს JPG, PNG ან WebP.',
        /** Not drawn: the picture's upload and insertion. */
        imageUploading: 'სურათი იტვირთება…',
        imageInserted: 'სურათი ჩაისვა',
        imageFailed: 'სურათი ვერ აიტვირთა — სცადე ხელახლა.',
        // Explanations (OD-CS7).
        saveBodyFirst: 'ჯერ შეინახეთ მასალის ტექსტის ცვლილებები, შემდეგ მონიშნეთ ტექსტი და დაამატეთ განმარტება.',
        selectPassage: 'მონიშნეთ ტექსტის მონაკვეთი, რომელსაც განმარტება ეხება.',
        /** Not drawn: an explanation needs a saved reading material. */
        saveMaterialFirst: 'განმარტება ემატება შენახულ ტექსტურ მასალას — ჯერ შეინახე მასალა.',
        xpNoSelection: 'მონიშვნა ცარიელია — მონიშნეთ ტექსტის მონაკვეთი დოკუმენტში.',
        xpBodyRequired: 'განმარტების ტექსტი სავალდებულოა — მინიმუმ 3 სიმბოლო.',
        /** Not drawn: the selection is not one passage of text the server can anchor to. */
        xpPassageInvalid: 'მონიშნეთ ტექსტის მონაკვეთი აბზაცის ან სათაურის შიგნით.',
        xpNoVersion: 'მასალის ტექსტი ჯერ არ არის შენახული — შეინახე მასალა და სცადე ხელახლა.',
        xpSaved: 'განმარტება შენახულია',
        xpUpdated: 'განმარტება განახლდა',
        xpMoved: 'განმარტება გადავიდა ახალ მონაკვეთზე',
        xpDeleted: 'განმარტება წაიშალა',
        xpPickNew: 'აირჩიეთ ახალი მონაკვეთი და გადაიტანეთ განმარტება მასზე.',
        xpEditAria: 'რედაქტირება',
        xpDeleteAria: 'წაშლა',
        xpMoveAria: 'სხვა მონაკვეთზე გადატანა',
        xpWord: 'განმარტება',
        xpListLoading: 'სია იტვირთება',
        xpListFailed: 'სია ვერ ჩაიტვირთა',
        /** The rich body editor's accessible name (the drawn body field's own label). */
        xpBodyLabel: 'განმარტების ტექსტი',
        /** Not drawn: a plain body cannot hold this explanation's formatting or images (release audit R02). */
        xpRichUnsupported: 'ამ განმარტებას აქვს ფორმატირება, ბმული ან სურათი, რომელსაც ეს ველი ვერ შეინახავს — ტექსტის შეცვლა მათ წაშლიდა. შეგიძლიათ შეინახოთ უცვლელად ან გადაიტანოთ.',
        // Students (owner, 2026-09-26): people already on the platform are added; nobody is invited here.
        addNeedsGroup: 'აირჩიე ჯგუფი — სტუდენტი ჯგუფის გარეშე ვერ დაემატება.',
        addFailed: 'ვერ დაემატა — სცადე ხელახლა',
        // Questions (OD-CS9).
        answerEmptyDraft: 'პასუხი ცარიელია — დრაფტი ვერ შეინახება.',
        answerEmptyPublish: 'პასუხი ცარიელია — გამოქვეყნება შეუძლებელია.',
        draftSaved: 'დრაფტი შენახულია',
        answerPublished: 'პასუხი გამოქვეყნდა',
        visibilityChanged: 'ხილვადობა შეიცვალა',
        /** Not drawn (OD-D6.8 interim): leaving unsaved answer text; repeated within 6 s it leaves. */
        leaveUnsaved: 'პასუხში შეუნახავი ცვლილებებია — შეინახე ან გაიმეორე გასვლა (ტექსტი ამ ბრაუზერში დარჩება).',
        // Undrawn announcements.
        selectionMissing: 'არჩეული ჩანაწერი ამ კურსში ვერ მოიძებნა',
        rosterCapped: 'ნაჩვენებია კურსის პირველი 200 სტუდენტი და მოწვევა',
        questionsCapped: 'ნაჩვენებია კურსის ბოლო 200 კითხვა',
      }),
    }),
    audit: Object.freeze({
      navLabel: 'მოქმედებების ისტორია',
      heading: 'მოქმედებების ისტორია',
      intro: 'მოძებნეთ ადმინისტრაციული ჩაწერები მოქმედების, შემსრულებლის, ობიექტის, ჯგუფური ოპერაციისა და თარიღის მიხედვით.',
      tableLabel: 'მოქმედებების ისტორიის ცხრილი',
      pageSizeNote: 'ერთ გვერდზე 50 ჩანაწერი',

      actionLabel: 'მოქმედება',
      allActions: 'ყველა მოქმედება',
      otherAction: 'სხვა — ზუსტი სახელით',
      otherActionLabel: 'მოქმედების ზუსტი სახელი',
      otherActionHint: 'ჩაწერეთ მოქმედების ზუსტი სახელი, როგორც ის ისტორიაშია დაფიქსირებული.',
      actorIdLabel: 'შემსრულებლის იდენტიფიკატორი',
      targetIdLabel: 'ობიექტის იდენტიფიკატორი',
      batchIdLabel: 'ჯგუფური ოპერაციის იდენტიფიკატორი',
      /*
       * Feature-local by decision: the Q&A queue owns its own interval and neither reaches into the
       * other.
       *
       * *(PRD-09)* `dateRange` is the one labelled interval this surface now shows, the same control
       * and the same wording the Q&A queue adopted under follow-up annotation 4 — `DESIGN.md`
       * § Navigation and Composition asks for the product's own date-range control *everywhere*, and
       * the trail was the last surface still asking for one interval as two separate questions.
       *
       * `dateFrom` and `dateTo` stay in the catalogue for the same reason the Q&A pair did: the suites
       * assert that neither label is on the page any more, and a removed key could only be asserted
       * against a literal. Both are still the wire fields on `admin.audit.list`.
       */
      dateRange: 'თარიღების შუალედი',
      dateFrom: 'თარიღიდან',
      dateTo: 'თარიღამდე',
      advanced: 'დამატებითი ფილტრები',
      advancedActive: 'ფილტრი აქტიურია',
      idHint: 'იდენტიფიკატორები ზუსტად უნდა ემთხვეოდეს; მიახლოებითი ძებნა აქ არ ხდება.',

      time: 'დრო',
      actor: 'შემსრულებელი',
      target: 'ობიექტი',
      change: 'ცვლილება',
      openBatchRow: 'ჯგუფური ოპერაცია',

      emptyHeading: 'შესაბამისი ჩანაწერები არ არის',
      emptyBody: 'შეცვალეთ ფილტრები ან აირჩიეთ სხვა თარიღები.',
      tooManyHeading: 'ჩანაწერები ძალიან ბევრია',
      tooManyBody: 'დაავიწროვეთ ფილტრები: მიუთითეთ მოქმედება, შემსრულებელი, ობიექტი, ჯგუფური ოპერაცია ან უფრო მოკლე თარიღების შუალედი.',
      cursorReset: 'გვერდის მისამართი აღარ მოქმედებს. სია პირველი გვერდიდან ჩაიტვირთა.',
      queryDropped: 'ბმულში მითითებული ფილტრი არასწორია და გამოტოვდა.',
      rangeInvalid: 'დაწყების თარიღი დასრულების თარიღზე გვიანია.',

      noChange: 'მნიშვნელობები არ შეცვლილა',
      changedKeys: 'შეცვლილი ველები',
      actionUnknown: 'სხვა მოქმედება',
      actorTypeUnknown: 'სხვა შემსრულებელი',
      targetTypeUnknown: 'სხვა ობიექტი',

      /*
       * *(AR-12 correction)* The audit trail keeps the stored identifier, because following one
       * incident across documents is what the trail is for — but it is labelled as the technical
       * detail it is, never presented as the record's name.
       */
      technicalId: 'ტექნიკური იდენტიფიკატორი',
      actorTypes: Object.freeze({
        admin: 'ადმინისტრატორი',
        system: 'სისტემა',
        engineering: 'ინჟინერია',
      }),
      targetTypes: Object.freeze({
        account: 'ანგარიში',
        accessRequest: 'შესვლის მოთხოვნა',
        preInvite: 'წინასწარი მიწვევა',
        enrollment: 'ჩარიცხვა',
        course: 'კურსი',
        group: 'ჯგუფი',
        section: 'თემა',
        material: 'მასალა',
        question: 'კითხვა',
        batch: 'ჯგუფური ოპერაცია',
        aiRun: 'მომზადების გაშვება',
        deletionRequest: 'წაშლის მოთხოვნა',
      }),

      /** Every action string the current writers store, in the order the server groups them. */
      actions: Object.freeze({
        'account.bootstrap.pending': 'რეგისტრაცია განხილვისთვის',
        'account.bootstrap.approved': 'ანგარიშის გახსნა მიწვევით',
        'account.bootstrap.admin': 'ადმინისტრატორის ანგარიშის გახსნა',
        'admin.requests.approve': 'მოთხოვნის დადასტურება',
        'admin.requests.reject': 'მოთხოვნის უარყოფა',
        'admin.students.update': 'სტუდენტის პროფილის ცვლილება',
        'admin.students.deactivate': 'ანგარიშის დეაქტივაცია',
        'admin.students.reactivate': 'ანგარიშის რეაქტივაცია',
        'admin.students.recordDeletionRequest': 'წაშლის მოთხოვნის დაფიქსირება',
        'admin.preInvites.create': 'წინასწარი მიწვევების დამატება',
        'admin.preInvites.update': 'წინასწარი მიწვევის ცვლილება',
        'admin.preInvites.delete': 'წინასწარი მიწვევის წაშლა',
        'admin.courses.create': 'კურსის შექმნა',
        'admin.courses.update': 'კურსის ცვლილება',
        'admin.courses.archive': 'კურსის არქივში გადატანა',
        'admin.courses.unarchive': 'კურსის არქივიდან დაბრუნება',
        'admin.courses.delete': 'კურსის წაშლა',
        'admin.courses.restore': 'კურსის აღდგენა',
        'system.retention.purgeCourse': 'კურსის სამუდამოდ წაშლა',
        // *(OD-2026-09-24, OD-C12)* The administrator's own permanent delete of a course in the Trash.
        'admin.courses.purge': 'კურსის სამუდამოდ წაშლა',
        'admin.groups.create': 'ჯგუფის შექმნა',
        'admin.groups.update': 'ჯგუფის ცვლილება',
        'admin.groups.archive': 'ჯგუფის არქივში გადატანა',
        'admin.groups.unarchive': 'ჯგუფის არქივიდან დაბრუნება',
        'admin.groups.delete': 'ჯგუფის წაშლა',
        'admin.groups.restore': 'ჯგუფის აღდგენა',
        'system.retention.purgeGroup': 'ჯგუფის სამუდამოდ წაშლა',
        'admin.sections.create': 'თემის შექმნა',
        'admin.sections.update': 'თემის ცვლილება',
        'admin.sections.reorder': 'თემების გადალაგება',
        'admin.sections.setPublished': 'თემის ხილვადობის ცვლილება',
        'admin.materials.create': 'მასალის შექმნა',
        'admin.materials.update': 'მასალის ცვლილება',
        'admin.materials.reorder': 'მასალების გადალაგება',
        'admin.materials.setPublished': 'მასალის ხილვადობის ცვლილება',
        // *(OD-2026-09-24, OD-G6)* The material Trash and its thirty-day removal.
        'admin.materials.delete': 'მასალის წაშლა',
        'admin.materials.restore': 'მასალის აღდგენა',
        'system.retention.purgeMaterial': 'მასალის სამუდამოდ წაშლა',
        // *(OD-2026-10-12)* The section Trash and its thirty-day removal.
        'admin.sections.delete': 'სექციის წაშლა',
        'admin.sections.restore': 'სექციის აღდგენა',
        'system.retention.purgeSection': 'სექციის სამუდამოდ წაშლა',
        'admin.materials.move': 'მასალის გადატანა',
        'admin.materials.finishUpload': 'ფაილის ატვირთვა',
        'admin.materials.deleteFile': 'ფაილის წაშლა',
        'admin.enrollments.create': 'ჯგუფში ჩარიცხვა',
        'admin.enrollments.transfer': 'სხვა ჯგუფში გადაყვანა',
        'admin.enrollments.close': 'ჩარიცხვის დახურვა',
        'entitlements.extend': 'კითხვის უფლების გახანგრძლივება',
        'admin.questions.editText': 'კითხვის ტექსტის რედაქტირება',
        'admin.questions.setVisibilityScope': 'ხილვადობის შეცვლა',
        'admin.questions.delete': 'კითხვის წაშლა',
        'admin.questions.restore': 'კითხვის აღდგენა',
        'admin.questions.purge': 'კითხვის სამუდამოდ წაშლა',
        'admin.answers.saveDraft': 'მონახაზის შენახვა',
        'admin.answers.overrideStale': 'მონახაზის ხელახლა დადასტურება',
        'admin.answers.confirm': 'პასუხის დადასტურება',
        'admin.answers.editPublished': 'გამოქვეყნებული პასუხის რედაქტირება',
        'admin.answers.unpublish': 'გამოქვეყნების გაუქმება',
        'admin.answers.restoreVersion': 'პასუხის ძველი ვერსიის აღდგენა',
        'admin.answers.regenerate': 'პასუხის ხელახლა მომზადება',
        'admin.ai.prepareNow': 'მომზადების დაწყება',
        'admin.ai.prepareAll': 'ყველა ახლის მომზადება',
        'questions.submit': 'კითხვის დასმა',
        'questions.deleteMine': 'სტუდენტმა კითხვა წაშალა',
        'system.ai.processQuestion.discarded_deleted': 'მომზადების შედეგი გაუქმდა',
        'system.retention.purgeDaily': 'მონაცემების გასუფთავება',
      }),

      /** The label of every field the projection may show, and of every safe batch parameter. */
      fields: Object.freeze({
        status: 'მდგომარეობა',
        accountStatus: 'ანგარიშის მდგომარეობა',
        requestStatus: 'მოთხოვნის მდგომარეობა',
        firstName: 'სახელი',
        lastName: 'გვარი',
        phone: 'ტელეფონი',
        email: 'ელფოსტა',
        deactivatedAt: 'დეაქტივაციის დრო',
        reactivatedAt: 'რეაქტივაციის დრო',
        deletionRequestedAt: 'წაშლის მოთხოვნის დრო',
        autoApproved: 'დადასტურდა მიწვევით',
        enrollmentsCreated: 'შექმნილი ჩარიცხვები',
        invitesConsumed: 'გამოყენებული მიწვევები',
        name: 'სახელი',
        type: 'ტიპი',
        description: 'აღწერა',
        /* *(002-course-content-reader)* The audit row names the structured description and the cover. */
        descriptionRichText: 'ფორმატირებული აღწერა',
        coverAssetId: 'ყდის სურათი',
        startAt: 'დაწყება',
        endAt: 'დასრულება',
        extraWeeks: 'დამატებითი კვირები',
        months: 'თვეები',
        lectureSchedule: 'ლექციების განრიგი',
        title: 'სათაური',
        scope: 'მოქმედების არეალი',
        order: 'რიგი',
        published: 'გამოქვეყნება',
        lectureNumber: 'ლექციის ნომერი',
        lectureDate: 'ლექციის თარიღი',
        fileName: 'ფაილის სახელი',
        richText: 'საკითხავი ტექსტი',
        embedUrl: 'ვიდეოს ბმული',
        externalUrl: 'გარე ბმული',
        courseId: 'კურსის იდენტიფიკატორი',
        sectionId: 'თემის იდენტიფიკატორი',
        groupId: 'ჯგუფის იდენტიფიკატორი',
        openedAt: 'გახსნის დრო',
        entStart: 'კითხვის უფლების დასაწყისი',
        entEnd: 'კითხვის უფლების ბოლო დღე',
        closedAt: 'დახურვის დრო',
        groupHistoryCount: 'ჯგუფის ცვლილებები',
        extension: 'გახანგრძლივება',
        reason: 'მიზეზი',
        rows: 'ჩანაწერები',
        decision: 'გადაწყვეტილება',
        needsAttention: 'ყურადღება სჭირდება',
        groupVisible: 'ჯგუფისთვის ხილვადი',
        deletedAt: 'წაშლის დრო',
        unpublishedAt: 'გამოქვეყნების გაუქმების დრო',
        visibilityScope: 'ხილვადობა',
        stale: 'მოძველებული მონახაზი',
        override: 'ხელახლა დადასტურება',
        answerVersion: 'პასუხის ვერსია',
        /** *(T094)* The version a restore brought the text back from. */
        restoredFrom: 'აღდგენილია ვერსიიდან',
        attempts: 'მცდელობები',
        enqueued: 'მომზადება დაიწყო',
        picked: 'შერჩეული',
        succeeded: 'მომზადდა',
        failed: 'ვერ მომზადდა',
        skipped: 'გამოტოვებული',
        outcome: 'შედეგი',
        billedCall: 'ანგარიშსწორებადი მიმართვა',
        text: 'კითხვის ტექსტი',
        questionsPurged: 'წაშლილი კითხვები',
        aiCallsPurged: 'წაშლილი მომზადების ჩანაწერები',
        accountsAnonymized: 'ანონიმიზებული ანგარიშები',
      }),

      /** The enumeration vocabulary the projection formats with. Feature-local, by decision. */
      values: Object.freeze({
        yes: 'დიახ',
        no: 'არა',
        valueAbsent: 'არ არის',
        valuePresent: 'მითითებულია',
        valueUpdated: 'განახლდა',
        questionStatuses: Object.freeze({
          new: 'ახალი კითხვა',
          preparing: 'პასუხი მზადდება',
          prepared: 'პასუხი მომზადებულია',
          confirmed: 'დადასტურებულია',
        }),
        curriculumStatuses: Object.freeze({ active: 'მოქმედი', archived: 'არქივში' }),
        enrollmentStatuses: Object.freeze({ active: 'მოქმედი', closed: 'დახურულია' }),
        courseTypes: Object.freeze({ full: 'სრული კურსი', self_paced: 'თვითსწავლებადი კურსი' }),
        materialTypes: Object.freeze({
          video_embed: 'ვიდეო',
          rich_text: 'საკითხავი მასალა',
          external_link: 'გარე ბმული',
          file: 'ფაილი',
        }),
        sectionScopes: Object.freeze({ course: 'მთელი კურსი', group: 'ერთი ჯგუფი' }),
        visibilityScopes: Object.freeze({ group: 'ჯგუფი', author: 'მხოლოდ ავტორი' }),
        decisions: Object.freeze({ approve: 'დადასტურება', reject: 'უარყოფა' }),
        accountActions: Object.freeze({ deactivate: 'დეაქტივაცია', reactivate: 'რეაქტივაცია' }),
        workerOutcomes: Object.freeze({ started: 'დაწყებული', discarded_deleted: 'გაუქმებული' }),
      }),
    }),

    /**
     * One batch, its outcome and its re-run — T071 (FR-PANEL-03).
     *
     * `rerunReasons` is the honest half of this block: a batch whose remaining objects cannot be
     * re-sent as the exact request the server accepted says which of the four reasons applies,
     * instead of offering a button that would send something else.
     */
    batches: Object.freeze({
      heading: 'ჯგუფური ოპერაცია',
      intro: 'ნახეთ ჯგუფური ოპერაციის შედეგი და საჭიროებისას გაუშვით ხელახლა მხოლოდ დარჩენილ ჩანაწერებზე.',
      idLabel: 'ჯგუფური ოპერაციის იდენტიფიკატორი',
      idHint: 'ჩასვით იდენტიფიკატორი მოქმედებების ისტორიიდან ან ოპერაციის შედეგიდან.',
      open: 'გახსნა',
      emptyHeading: 'აირჩიეთ ჯგუფური ოპერაცია',
      emptyBody: 'ჩასვით იდენტიფიკატორი ან გახსენით მოქმედებების ისტორიიდან.',
      notFound: 'ასეთი ჯგუფური ოპერაცია ვერ მოიძებნა.',

      summaryHeading: 'ოპერაციის მიმოხილვა',
      runBy: 'შემსრულებელი',
      startedAt: 'დაწყება',
      finishedAt: 'დასრულება',
      stillRunning: 'ჯერ არ დასრულებულა',
      requested: 'მოთხოვნილი',
      paramsHeading: 'პარამეტრები',
      paramsNone: 'დამატებითი პარამეტრები არ ყოფილა.',
      auditContext: 'ამ ოპერაციის ჩანაწერები ისტორიაში',

      appliedHeading: 'შესრულებული ჩანაწერები',
      skippedHeading: 'გამოტოვებული ჩანაწერები',
      failedHeading: 'შეცდომით დასრულებული ჩანაწერები',
      childrenHeading: 'თითოეული ობიექტის ისტორია',
      childrenEmpty: 'ობიექტების ჩანაწერები ჯერ არ არის.',
      openQuestion: 'კითხვის გახსნა',

      remainingHeading: 'დარჩენილი ჩანაწერები',
      remainingCount: 'დარჩენილია',
      rerun: 'დარჩენილებზე ხელახლა გაშვება',
      rerunHeading: 'დარჩენილ ჩანაწერებზე ხელახლა გაშვება',
      rerunBody: 'ხელახლა გაშვება მოქმედებს მხოლოდ იმ ჩანაწერებზე, რომლებზეც ჯერ არაფერი შესრულებულა. სერვერი თითოეულ მათგანს მიმდინარე მდგომარეობით ხელახლა შეამოწმებს: ნაწილი შეიძლება ახლა შესრულდეს, ნაწილი კვლავ გამოტოვდეს. უკვე შესრულებული ჩანაწერები ხელახლა არ იცვლება.',
      rerunConfirm: 'დიახ, გაეშვას ხელახლა',
      rerunReasons: Object.freeze({
        order: 'გადალაგება მთელ სიაზე სრულდება, ამიტომ მხოლოდ ნაწილზე ხელახლა გაშვება აქედან შეუძლებელია. გაიმეორეთ გადალაგება შესაბამის პანელში.',
        derivedIds: 'ამ ოპერაციის ჩანაწერების იდენტიფიკატორები სერვერზე გამოითვალა, ამიტომ თავდაპირველი მოთხოვნა აქედან ზუსტად ვერ აღდგება. გაიმეორეთ შესაბამის პანელში.',
        unknownAction: 'ამ მოქმედების ხელახლა გაშვება ამ ეკრანიდან არ არის მხარდაჭერილი. გაიმეორეთ შესაბამის პანელში.',
        unreadableParams: 'შენახული პარამეტრებით მოთხოვნა ზუსტად ვერ აღდგება, ამიტომ ხელახლა გაშვება აქედან შეუძლებელია.',
        nothingRemaining: 'დარჩენილი ჩანაწერი არ არის — ოპერაცია სრულად შესრულდა.',
      }),
      statuses: Object.freeze({
        running: 'მიმდინარეობს',
        completed: 'დასრულდა',
        partial: 'ნაწილობრივ შესრულდა',
        failed: 'ვერ შესრულდა',
        unknown: 'უცნობი მდგომარეობა',
      }),
      /* Feature-local: the enrolment panel owns its own unit labels and neither reaches into the other. */
      extendUnits: Object.freeze({
        weeks: 'კვირები',
        months: 'კალენდარული თვეები',
        exact: 'ზუსტი თარიღი',
      }),
    }),

    /**
     * The operational status surface — T071 (FR-OPS-04).
     *
     * This is the whole in-app alert surface: there is no email, no push and no badge anywhere in
     * the product. Copy stays outcome-shaped — „მომზადება" — because the design contract forbids
     * model, provider and automation vocabulary in any administrator screen.
     */
    system: Object.freeze({
      navLabel: 'სისტემის მდგომარეობა',
      heading: 'სისტემის მდგომარეობა',
      intro: 'პასუხების ბოლო მომზადება, ყურადღების მომლოდინე კითხვები, ასლისა და გასუფთავების მდგომარეობა.',

      runHeading: 'პასუხების ბოლო მომზადება',
      runNone: 'ჯერ არცერთი მომზადება არ შესრულებულა.',
      runInProgress: 'მიმდინარეობს',
      runStartedAt: 'დაწყება',
      runFinishedAt: 'დასრულება',
      picked: 'შერჩეული',
      succeeded: 'მომზადდა',
      failedCount: 'ვერ მომზადდა',
      skipped: 'გამოტოვებული',
      runErrorUnknown: 'მომზადების ნაწილი შეფერხდა.',
      runErrors: Object.freeze({
        ENQUEUE_FAILED: 'ნაწილი კითხვებისთვის მომზადება ვერ დაიწყო.',
      }),
      triggerUnknown: 'სხვა გაშვება',
      triggers: Object.freeze({
        hourly: 'გეგმური გაშვება',
        manual_one: 'ხელით, ერთი კითხვა',
        manual_all: 'ხელით, ყველა ახალი',
      }),

      attentionHeading: 'ყურადღება სჭირდება',
      attentionTotal: 'სულ',
      attentionPreview: 'ნაჩვენებია ბოლო 50 ჩანაწერი. სულ:',
      attentionEmpty: 'ყურადღების მომლოდინე კითხვა არ არის.',
      attentionTableLabel: 'ყურადღების მომლოდინე კითხვების ცხრილი',
      attentionAskedAt: 'დასმის დრო',

      backupHeading: 'უსაფრთხოების ასლი',
      backupSnapshot: 'ასლის დრო',
      backupExpires: 'ვადა',
      backupExpiresUnknown: 'უცნობია',
      backupLocation: 'ლოკაცია',
      backupId: 'ასლის იდენტიფიკატორი',
      backupNone: 'წარმატებული ასლი ჯერ არ არსებობს.',
      backupUnknown: 'ასლის მდგომარეობა ვერ დადგინდა.',
      backupUnavailable: Object.freeze({
        emulator: 'ლოკალურ გარემოში ასლის მდგომარეობა მიუწვდომელია.',
        unauthorized: 'ასლების მდგომარეობის წაკითხვის უფლება არ არის.',
        unreachable: 'ასლების მდგომარეობა ამჟამად მიუწვდომელია.',
      }),

      purgeHeading: 'ბოლო გასუფთავება',
      purgeNone: 'გასუფთავება ჯერ არ შესრულებულა.',
      purgeAt: 'შესრულების დრო',
      purgeQuestions: 'წაშლილი კითხვები',
      purgeAiCalls: 'წაშლილი მომზადების ჩანაწერები',
      purgeAccounts: 'ანონიმიზებული ანგარიშები',
      purgeAudit: 'გასუფთავების ჩანაწერები ისტორიაში',
    }),
  }),

  /**
   * The student course library, one course and one material — T062-B, FR-MAT-04, R2 SCREEN-MAP.
   *
   * Two rules shape it. **Nothing describes a material the server did not send.** Hidden and
   * scheduled rows are omitted by `courses.get` (T061), so there is no "opens later" line and no
   * locked row anywhere below: a material a student may not open yet simply is not on the page, and
   * an address that names one gets the same unavailable surface a refusal gets. **The asking right
   * is quoted, never recomputed** — `canAsk` and the remaining days come from the server clock.
   */
  courses: Object.freeze({
    navLabel: 'კურსები',
    heading: 'ჩემი კურსები',
    intro: 'თქვენი კურსები, მასალები და კითხვის უფლების მდგომარეობა.',
    signInBody: 'კურსებისა და მასალების სანახავად ჯერ შედით სისტემაში.',

    emptyHeading: 'კურსი ჯერ არ არის ხელმისაწვდომი',
    emptyBody: 'როგორც კი კურსზე ჩაირიცხებით, ის აქ გამოჩნდება.',
    openCourse: 'კურსის გახსნა',
    backToCourses: 'ყველა კურსი',
    backToCourse: 'კურსში დაბრუნება',

    /* The two enrolment facts FR-MAT-04 requires on the course page. */
    openedAt: 'კურსი გაიხსნა',
    entitlementHeading: 'კითხვის უფლება',
    entitlementActive: 'აქტიურია',
    entitlementExpired: 'ვადა გასულია',
    entitlementClosed: 'ჩარიცხვა დახურულია',
    entitlementUnknown: 'ინფორმაცია მიუწვდომელია',
    daysLeft: 'დარჩენილია დღე',
    groupLabel: 'ჯგუფი',
    groupStart: 'დაწყება',
    groupEnd: 'დასრულება',
    meetingDay: 'ლექციის დღე',
    meetingTime: 'ლექციის საათი',
    scheduleUnknown: 'არ არის მითითებული',
    /* Stated once, because a student whose asking window closed keeps reading (FR-ENR-05). */
    readingContinues: 'მასალების კითხვა ვადის გასვლის შემდეგაც რჩება.',

    structureHeading: 'კურსის შინაარსი',
    structureEmptyHeading: 'მასალა ჯერ არ არის',
    structureEmptyBody: 'როგორც კი მასალები გაიხსნება, ისინი აქ გამოჩნდება.',
    materialsCount: 'მასალა',

    previousMaterial: 'წინა მასალა',
    nextMaterial: 'შემდეგი მასალა',
    materialNavLabel: 'მასალებში ნავიგაცია',

    /* ---------------------------------------------------------------- *
     * *(PRD-08, `docs/design/product-redesign-addendum.md` §§ 3.4, 5.4/5.5)* Completion and progress
     *
     * Two exact strings the owner approved and the reconciliation table quotes: `დავასრულე` is the
     * act and `დასრულებულია` is the state. They are the **same two words** on the material toolbar,
     * on a course row and inside every fraction, because a student who marked a material finished and
     * a row that says so are describing one fact.
     *
     * The numbers are always the server's: `{completed}` and `{total}` are `progress.completedCount`
     * and `progress.visibleCount` from the projection itself (FREEZE-5), or — for a section heading —
     * that section's own already-projected materials. Nothing here is phrased so that a missing
     * denominator could be filled with a guess: `0 მასალა 0-დან` is a truthful sentence.
     * ---------------------------------------------------------------- */
    completionMark: 'დავასრულე',
    completionDone: 'დასრულებულია',
    completionFailed: 'მდგომარეობა ვერ შეინახა. სცადეთ ხელახლა.',
    progressPercent: '{percent}% დასრულებულია',
    progressFraction: '{completed} მასალა {total}-დან',
    progressSectionFraction: '{completed} / {total} დასრულებულია',
    /**
     * The progress bar's accessible name, in its two forms.
     *
     * The bar is a real `progressbar`, not decoration, so it needs a name. The *numbers* stay the
     * server's — the percentage arrives as `aria-valuenow` and `progressFraction` above becomes the
     * bar's `aria-valuetext` — so a reader who cannot see the two lines beside it hears exactly the
     * same two server-derived readings.
     *
     * Two forms, because a name only has to distinguish where there is something to distinguish
     * from. The library renders **one bar per course card**, and several bars called
     * „სასწავლო პროგრესი" are indistinguishable to a reader moving through the accessibility tree,
     * with nothing tying a bar to the heading above it; so those bars, and the course page's own,
     * carry the real course name from the same payload. The profile has exactly one aggregate bar
     * and no course to name, so it keeps the plain form.
     *
     * `{course}` is the course's own `name`, never an identifier — the two strings deliberately
     * share the same closing words, so a reader hears one vocabulary on both surfaces.
     */
    progressBarLabel: 'სასწავლო პროგრესი',
    progressBarLabelForCourse: '{course} — სასწავლო პროგრესი',

    /* ---------------------------------------------------------------- *
     * *(002-course-content-reader T064)* The paginated reader
     *
     * A page here is a **window onto one document**, never a stored thing, so the copy says
     * "გვერდი {page} / {total}" about what is currently in view rather than naming a position the
     * student could quote to somebody on another device. The controls are named as actions.
     * ---------------------------------------------------------------- */
    readerLabel: 'საკითხავი ტექსტი',
    readerNavLabel: 'გვერდებში ნავიგაცია',
    readerPrevious: 'წინა გვერდი',
    readerNext: 'შემდეგი გვერდი',
    readerProgress: 'გვერდი {page} / {total}',
    readerSpreadProgress: 'გვერდები {page}–{second} / {total}',
    /** Read out when the page changes, so a change nobody can see is still announced. */
    readerPageAnnounce: 'გვერდი {page} / {total}',
    readerLoading: 'ტექსტი ემზადება…',
    /* *(FR-022)* An explanation is opened from the text and returns focus to where it was opened. */
    readerExplanationLabel: 'განმარტება: {passage}',
    readerExplanationHeading: 'განმარტება',
    readerExplanationClose: 'დახურვა',
    readerHighlightLabel: 'მონიშვნა: {passage}',
    /** *(owner, 2026-10-02)* Over a Google Drive video: iPhone Safari can leave Drive's embedded player blank. */
    readerOpenInDrive: 'Google Drive-ში გახსნა',

    /* ---------------------------------------------------------------- *
     * *(002-course-content-reader T074)* Private marks, filters and bookmarks
     *
     * Everything here belongs to **one student** and is never shown to anybody else, so the copy
     * says "your" nowhere — there is nobody else's to distinguish it from. The colours are named
     * because a colour filter a reader cannot name is a filter they cannot describe to themselves.
     * ---------------------------------------------------------------- */
    readerMarkPassage: 'მონიშვნა',
    readerMarkRemove: 'მონიშვნის მოხსნა',
    readerMarkColor: 'ფერის შეცვლა',
    readerColors: {
      yellow: 'ყვითელი',
      green: 'მწვანე',
      blue: 'ლურჯი',
      pink: 'ვარდისფერი',
      orange: 'ნარინჯისფერი',
    },
    readerHighlightsHeading: 'მონიშვნები',
    readerHighlightsEmpty: 'მონიშვნები ჯერ არ არის.',
    readerFilterLabel: 'ფერით გაფილტვრა',
    readerFilterClear: 'ფილტრების მოხსნა',
    readerFilterEmpty: 'არჩეული ფერით მონიშვნა არ მოიძებნა.',
    readerJumpTo: 'მონიშვნაზე გადასვლა',
    /** *(FR-030)* A mark an edit stranded. Kept and shown, so a reader decides what to do with it. */
    readerUnresolved: 'ტექსტი შეიცვალა — ეს მონიშვნა ვეღარ პოულობს თავის ადგილს.',
    readerUnresolvedHeading: 'დაკარგული მონიშვნები',

    readerBookmarksHeading: 'სანიშნეები',
    readerBookmarksEmpty: 'სანიშნეები ჯერ არ არის.',
    readerBookmarkAdd: 'სანიშნის დამატება',
    readerBookmarkUpdate: 'სანიშნის განახლება',
    readerBookmarkHere: 'სანიშნი ამ გვერდზეა',
    readerBookmarkRemove: 'სანიშნის წაშლა',
    readerBookmarkJump: 'სანიშნეზე გადასვლა',
    /** *(FR-025)* The automatic position, distinguished from the explicit bookmarks beside it. */
    readerResumeHere: 'აქ გაჩერდით',
    readerResumeGo: 'გაგრძელება',

    /**
     * *(FR-026, FR-027)* What the server says when a private record cannot be stored.
     *
     * Keyed by the stable issue code, because the refusal has to name *which* limit was reached and
     * what removes it — a reader told only "failed" would keep trying the same act.
     */
    readerRecordIssues: Object.freeze({
      reader_highlight_limit:
        'ამ მასალაში მონიშვნების ზღვარი ამოიწურა. ახლის დასამატებლად ჯერ წაშალეთ ერთ-ერთი არსებული.',
      reader_bookmark_limit:
        'ამ მასალაში სანიშნეების ზღვარი ამოიწურა. ახლის დასამატებლად ჯერ წაშალეთ ერთ-ერთი არსებული.',
      reader_revision_stale: 'ეს ჩანაწერი სხვა მოწყობილობაზე შეიცვალა — განახლდა.',
    }),
    readerRecordIssueFallback: 'ჩანაწერი ვერ შეინახა. სცადეთ ხელახლა.',

    unavailableHeading: 'მასალა მიუწვდომელია',
    unavailableBody: 'ეს მასალა ამჟამად ხელმისაწვდომი არ არის.',
    courseUnavailableHeading: 'კურსი მიუწვდომელია',
    courseUnavailableBody: 'ეს კურსი ამჟამად ხელმისაწვდომი არ არის.',

    /* Per-type reading surfaces. */
    videoLabel: 'ვიდეო',
    videoUnavailable: 'ვიდეო ამ მისამართიდან ვერ ჩაიტვირთა.',
    linkHeading: 'გარე ბმული',
    linkOpen: 'ბმულის გახსნა',
    linkHost: 'მისამართი',

    /**
     * The Claude Design Student Courses page (`design/app/student-courses`): only the copy the
     * owner's decisions replace (OD-SC6, OD-SC8, OD-SC12); everything else is the design's own text.
     */
    design: Object.freeze({
      notYetOpenNote: 'ჩარიცხული ხარ — მასალები განრიგით გაიხსნება',
      opensOn: 'კურსი იხსნება',
      notYetOpenPlan: 'მასალები განრიგის მიხედვით გაიხსნება.',
      startsToday: 'იწყება დღეს',
      accountRole: 'სტუდენტი',
      profileSuffix: 'პროფილი',
    }),

    /**
     * The Claude Design Student Reader page (`design/app/student-reader`): the page's own words for
     * what really happened — a stored highlight, note, bookmark or completion — where the design's
     * demo sentence belongs to its prototype. OD-SR1…SR15.
     */
    readerDesign: Object.freeze({
      explanationTitle: 'რას ნიშნავს „{passage}“',
      pageLabel: 'გვერდი {page} / {total}',
      highlightCreated: 'ტექსტი მოინიშნა',
      highlightRecolored: 'მონიშვნის ფერი შეიცვალა',
      highlightRemoved: 'მონიშვნა მოიხსნა',
      highlightDeleted: 'მონიშვნა წაიშალა',
      noteSaved: 'შენიშვნა შენახულია',
      noteDeleted: 'შენიშვნა წაიშალა',
      bookmarkSet: 'აქ გავჩერდი — გვერდი {page}',
      bookmarkMoved: 'აქ გავჩერდი — სანიშნე გადავიდა გვერდ {page}-ზე',
      bookmarkRemoved: 'სანიშნე მოიხსნა',
      bookmarkSetAria: 'აქ გავჩერდი — გვერდი {page}',
      bookmarkMoveAria: 'აქ გავჩერდი — სანიშნის გადატანა გვერდ {page}-ზე',
      bookmarkHereAria: 'აქ გავჩერდი — სანიშნე გვერდ {page}-ზეა, მოხსნა',
      completed: 'მასალა დასრულებულად მოინიშნა',
      uncompleted: 'დასრულება გაუქმდა',
      openLinkFirst: 'ჯერ გახსენი ბმული, შემდეგ მონიშნე დასრულებულად',
      linkOpened: 'ბმული გაიხსნა ახალ ფანჯარაში',
    }),
  }),

  /**
   * The student profile — T056 WP4 (FR-PROF-02/04, FR-ENT-06, R2 `#profile`).
   *
   * The only surface that states the **exact** entitlement end and the only one that lists an
   * enrolment `courses.listMine` omits — a closed one, or one whose course has not opened yet. The
   * identity fields are read-only because only an administrator changes them (FR-PROF-02).
   *
   * A course whose name the student may not currently read is described by its state, never by its
   * identifier: `courseId` is an internal key, not a title.
   */
  profile: Object.freeze({
    navLabel: 'ჩემი პროფილი',
    heading: 'ჩემი პროფილი',
    intro: 'სახელსა და ტელეფონს ადმინისტრატორი მართავს.',
    signInBody: 'პროფილის სანახავად ჯერ შედით სისტემაში.',

    identityHeading: 'პირადი ინფორმაცია',
    phone: 'ტელეფონი',
    notProvided: 'არ არის მითითებული',
    accountActive: 'აქტიური ანგარიში',

    /*
     * *(PRD-08)* The learning-progress card, derived from the same `courses.listMine` payload the
     * rights table already reads — no second read, no second denominator and no invented statistic.
     * The percent and the fraction themselves reuse `messages.courses`, so the profile and the course
     * surfaces can never word the same number two ways.
     */
    learningHeading: 'სასწავლო პროგრესი',

    rightsHeading: 'ჩემი კურსები და უფლებები',
    rightsTableLabel: 'კურსებისა და უფლებების ცხრილი',
    columnCourse: 'კურსი',
    columnOpened: 'გახსნის თარიღი',
    columnDeadline: 'კითხვის დასმის ბოლო ვადა',
    columnState: 'მდგომარეობა',

    stateActive: 'კითხვის დასმა შესაძლებელია',
    stateExpired: 'კითხვის დასმის ვადა ამოიწურა',
    stateClosed: 'დახურული ჩარიცხვა',
    stateNotOpened: 'კურსი ჯერ არ გახსნილა',
    daysLeft: 'დარჩენილია დღე',
    openCourse: 'კურსის გახსნა',

    readingContinues: 'კითხვების დასმის ვადის გასვლა მასალებსა და გამოქვეყნებულ პასუხებზე წვდომას არ აუქმებს.',
    emptyHeading: 'კურსზე ჯერ არ ხართ ჩარიცხული',
    emptyBody: 'დამატებული კურსები აქ გამოჩნდება.',

    /**
     * The Claude Design Student Profile page (`design/app/student-profile`): the role, the renamed
     * third access group (OD-SP2) and the remaining-time line of a row (OD-SP5).
     */
    design: Object.freeze({
      roleStudent: 'სტუდენტი',
      roleAdmin: 'ადმინისტრატორი',
      askingExpiredGroup: 'კითხვის ვადა ამოიწურა',
      remaining: 'დარჩა',
      days: 'დღე',
      expired: 'ვადა გასულია',
      starts: 'იწყება',
      inDays: 'დღეში',
      startsToday: 'იწყება დღეს',
    }),
  }),

  /**
   * The one in-site PDF reader — T062-B, R2-01/R2-02, D10.
   *
   * Shared by student material reading and the administrator preview, so every line here has to be
   * true for both. Each of the ten states the reader can rest in has exactly one sentence, and none
   * of them quotes a server message, a URL or a storage path (`BOUNDARIES.md` § 6).
   */
  pdf: Object.freeze({
    readerLabel: 'დოკუმენტის მკითხველი',
    loading: 'დოკუმენტი იტვირთება…',
    pageOf: 'გვერდი',
    pageTotal: 'სულ',
    toolbarLabel: 'დოკუმენტის მართვა',
    fitWidth: 'სიგანეზე მორგება',
    zoomIn: 'გადიდება',
    zoomOut: 'დაპატარავება',
    download: 'ორიგინალის ჩამოტვირთვა',
    downloading: 'მიმდინარეობს ჩამოტვირთვა…',
    retry: 'ხელახლა ცდა',
    /* Honest about what the reader can and cannot offer for a scanned document. */
    noTextLayer: 'ამ დოკუმენტში ტექსტის მონიშვნა შეუძლებელია.',

    accessDeniedHeading: 'ფაილი მიუწვდომელია',
    accessDeniedBody: 'ამ ფაილის გახსნა ამჟამად შეუძლებელია.',
    unavailableHeading: 'ფაილი ვერ მოიძებნა',
    unavailableBody: 'ფაილი ამჟამად ხელმისაწვდომი არ არის.',
    corruptHeading: 'ფაილი ვერ იხსნება',
    corruptBody: 'დოკუმენტი დაზიანებულია ან არ არის სწორი PDF.',
    passwordHeading: 'დოკუმენტი პაროლითაა დაცული',
    passwordBody: 'ასეთი დოკუმენტი საიტზე ვერ იხსნება. შეგიძლიათ ორიგინალი ჩამოტვირთოთ.',
    renderFailureHeading: 'გვერდი ვერ დაიხატა',
    renderFailureBody: 'ამ გვერდის ჩვენება ვერ მოხერხდა. სცადეთ ხელახლა ან ჩამოტვირთეთ ორიგინალი.',
    accessFailureHeading: 'დოკუმენტი ვერ ჩაიტვირთა',
    accessFailureBody: 'კავშირი ვერ დამყარდა. შეამოწმეთ ინტერნეტი და სცადეთ ხელახლა.',
    unsupportedHeading: 'ბრაუზერი ვერ აჩვენებს დოკუმენტს',
    unsupportedBody: 'ამ ბრაუზერში საიტზე კითხვა შეუძლებელია. ჩამოტვირთეთ ორიგინალი.',
    downloadFailed: 'ჩამოტვირთვა ვერ მოხერხდა. სცადეთ ხელახლა.',
  }),

  shell: Object.freeze({
    notFoundHeading: 'გვერდი ვერ მოიძებნა',
    notFoundBody: 'მისამართი არასწორია ან გვერდი წაშლილია.',
    backToHome: 'მთავარ გვერდზე დაბრუნება',
    unexpectedHeading: 'მოულოდნელი შეცდომა',
    unexpectedBody: 'გვერდის ჩვენება ვერ მოხერხდა.',
  }),

  /** The walking-skeleton route (T006 operations). Visible only to a server-recognised administrator. */
  smoke: Object.freeze({
    navLabel: 'შემოწმება',
    heading: 'საბაზისო შემოწმება',
    intro: 'თითოეული ღილაკი ასრულებს ერთ სერვერულ ოპერაციას და აჩვენებს ზუსტ პასუხს.',
    markerLabel: 'ნიშნული',
    markerHint: 'პატარა ლათინური ასოები, ციფრები და დეფისი, 1–40 სიმბოლო.',
    /** A visible initial field value, so it belongs here and not in the route (FR-UX-05). */
    defaultMarker: 'shell-check',
    operationLabel: 'ოპერაცია',
    run: 'შესრულება',
    running: 'სრულდება…',
    confirmHeading: 'ოპერაციის დადასტურება',
    confirmBody: 'ოპერაცია სერვერზე ჩაწერს მონაცემს. გავაგრძელოთ?',
    confirm: 'დიახ, შესრულდეს',
    cancel: 'გაუქმება',
    resultsHeading: 'შედეგები',
    columnOperation: 'ოპერაცია',
    columnOutcome: 'შედეგი',
    columnDetail: 'დეტალები',
    columnTime: 'დრო',
    outcomeOk: 'შესრულდა',
    outcomeFailed: 'ვერ შესრულდა',
    writeLabel: 'ჩანაწერის შექმნა',
    enqueueLabel: 'დავალების დაყენება',
    signedUrlLabel: 'ფაილის ბმულები',
    noResults: 'ჯერ არცერთი ოპერაცია არ შესრულებულა.',
  }),

  /** Strings the reusable primitives need. A primitive never hard-codes text of its own. */
  components: Object.freeze({
    dialogClose: 'დახურვა',
    cancel: 'გაუქმება',
    paginationLabel: 'გვერდები',
    paginationPrevious: 'წინა',
    paginationNext: 'შემდეგი',
    paginationPage: 'გვერდი',
    paginationEllipsis: 'გამოტოვებული გვერდები',
    selectedCount: 'მონიშნულია',
    /** The visible word on a record card's selection control (`ACCEPTANCE.md` § 13). */
    selectRecord: 'მონიშვნა',
    clearSelection: 'მონიშვნის მოხსნა',
    batchBarLabel: 'ჯგუფური მოქმედებები',
    datePickerLabel: 'თარიღის არჩევა',
    /** A visible placeholder showing the accepted `YYYY-MM-DD` shape (FR-UX-05). */
    datePickerPlaceholder: '2026-01-31',
    datePickerPreviousMonth: 'წინა თვე',
    datePickerNextMonth: 'შემდეგი თვე',
    /** Accessible names react-day-picker would otherwise emit in English (FR-UX-05). */
    datePickerNavLabel: 'თვის ნავიგაცია',
    datePickerSelected: 'არჩეული',
    datePickerMonthDropdown: 'თვე',
    datePickerYearDropdown: 'წელი',
    requiredField: 'სავალდებულო ველი',

    /* ---------------------------------------------------------------- *
     * AR-04 — the shared control system
     * ---------------------------------------------------------------- */

    /** The search field: its clear control, and what an empty query means. */
    searchClear: 'ძებნის გასუფთავება',
    searchPlaceholder: 'ძებნა…',

    /** The select and the searchable selector. */
    selectPlaceholder: 'აირჩიეთ…',
    selectClear: 'არჩევანის მოხსნა',
    comboboxSearchPlaceholder: 'დაწერეთ სახელი…',
    comboboxNoResults: 'შესატყვისი ჩანაწერი ვერ მოიძებნა.',
    comboboxLoading: 'იტვირთება…',
    comboboxHint: 'აკრიფეთ სახელის ნაწილი და აირჩიეთ სიიდან.',
    comboboxOpen: 'სიის გახსნა',

    /** The three-dot menu every row's secondary actions live behind. */
    rowActions: 'ჩანაწერის მოქმედებები',
    /** The accessible name of the link that opens a register row's record. */
    openRecord: 'ჩანაწერის გახსნა',

    /** The time picker. */
    timePickerLabel: 'დროის არჩევა',
    timePickerPlaceholder: '18:00',
    timePickerHour: 'საათი',
    timePickerMinute: 'წუთი',

    /** The date picker's popover trigger. */
    datePickerOpen: 'კალენდრის გახსნა',

    /**
     * *(follow-up 4)* The date-range field: one label over one range calendar.
     *
     * The placeholder is a sentence rather than a format example, because an interval has no single
     * shape to teach — the trigger shows the two ISO days themselves once they are chosen.
     */
    dateRangeLabel: 'თარიღების შუალედის არჩევა',
    dateRangePlaceholder: 'აირჩიეთ თარიღების შუალედი',

    /**
     * *(browser comment 2)* The accessible name of a control that discloses one field's explanation.
     *
     * It is a glyph alone, so it needs a name, and the name carries the field's own label after it:
     * two identically named buttons on one row would tell a screen-reader user which is which only by
     * where they happen to be.
     */
    explain: 'განმარტება',

    /** Progress. */
    progressLabel: 'შესრულება',

    /** The loading placeholder that stands where content will be. */
    loadingPlaceholder: 'შიგთავსი იტვირთება',

    /* ---------------------------------------------------------------- *
     * The ledger's own controls
     * ---------------------------------------------------------------- */

    /** *(ledger 21–23, 56)* The bound the server puts on one multiple selection. */
    multiSelectMax: 'ერთდროულად მაქსიმუმ {max} ჩანაწერის არჩევა შეიძლება.',
    /** *(ledger 3)* The header control that holds the account and sign-out on a narrow layout. */
    accountMenu: 'ანგარიში',
    /** *(ledger 29, 36)* The two halves of a deliberate disclosure. */
    expand: 'გაშლა',
    collapse: 'დაკეცვა',
  }),

  /** Sunday-last, matching the Georgian week; index 0 is Monday. */
  weekdaysShort: Object.freeze(['ორ', 'სა', 'ოთ', 'ხუ', 'პა', 'შა', 'კვ'] as const),
  weekdaysLong: Object.freeze([
    'ორშაბათი',
    'სამშაბათი',
    'ოთხშაბათი',
    'ხუთშაბათი',
    'პარასკევი',
    'შაბათი',
    'კვირა',
  ] as const),
  /**
   * The Georgian months — a catalogue, and deliberately **nothing renders from it** (T093).
   *
   * The accepted owner rule prohibits a written-out month wherever a date is visible: every visible
   * date in the product is `DD.MM.YY`, and the calendar's captions and accessible names are numeric
   * too. The list stays because absence has to be asserted against a vocabulary rather than against
   * a literal — `lib/dates.test.ts` reads it to prove no source file outside this one renders a month
   * name, and several suites read it to prove no screen spells one. Adding a rendering use of it is
   * the regression those guards exist to catch.
   */
  months: Object.freeze([
    'იანვარი',
    'თებერვალი',
    'მარტი',
    'აპრილი',
    'მაისი',
    'ივნისი',
    'ივლისი',
    'აგვისტო',
    'სექტემბერი',
    'ოქტომბერი',
    'ნოემბერი',
    'დეკემბერი',
  ] as const),
});

/**
 * One line per stable error code, keyed by the message key `@simple-qa/shared` assigns it.
 * Each line says what happened and, where the user can act, what to do — never why the server
 * decided it, and never anything that would tell a caller whether a target it may not see exists.
 */
export const errorMessages = Object.freeze({
  'error.unauthenticated': 'სესია დასრულდა. გთხოვთ, ხელახლა შეხვიდეთ.',
  'error.forbidden': 'ამ მოქმედების ნებართვა არ გაქვთ.',
  'error.notFound': 'მოთხოვნილი ჩანაწერი ვერ მოიძებნა.',
  'error.invalidArgument': 'შეყვანილი მონაცემები არასწორია. შეამოწმეთ ველები.',
  'error.failedPrecondition': 'ამჟამინდელ მდგომარეობაში ეს მოქმედება შეუძლებელია.',
  'error.conflict': 'ჩანაწერი შეიცვალა. განაახლეთ გვერდი და სცადეთ ხელახლა.',
  'error.rateLimited': 'ძალიან ბევრი მოთხოვნაა. სცადეთ ცოტა ხანში.',
  'error.internal': 'მოულოდნელი შეცდომა. სცადეთ ხელახლა.',
} as const);

/**
 * R08: a refusal by the server's write fence (maintenance / incident) — a `FAILED_PRECONDITION` whose
 * issue is `write_fence`. Kept beside the table, which stays one message per stable code.
 */
export const WRITE_FENCE_MESSAGE_KEY = 'error.writeFence';
export const writeFenceMessage = 'პლატფორმაზე ტექნიკური სამუშაოები მიმდინარეობს — ცვლილებების შენახვა დროებით შეჩერებულია. დათვალიერება შეგიძლიათ.';

export type KnownErrorMessageKey = keyof typeof errorMessages;

/** The generic line, and the fallback of `errorMessage`. */
const GENERIC_ERROR_KEY: KnownErrorMessageKey = 'error.internal';

function isKnownErrorMessageKey(value: unknown): value is KnownErrorMessageKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(errorMessages, value);
}

/**
 * Resolve a message key the server sent into Georgian text.
 *
 * Total by construction: anything that is not a key this catalogue knows — a malformed value, a key
 * added to the contract before this catalogue caught up, or a server string that should never have
 * been sent — resolves to the generic line. The alternative, showing the raw key or the server's own
 * words, is exactly what `BOUNDARIES.md` § 6 forbids.
 */
export function errorMessage(key: unknown): string {
  if (key === WRITE_FENCE_MESSAGE_KEY) return writeFenceMessage;
  return errorMessages[isKnownErrorMessageKey(key) ? key : GENERIC_ERROR_KEY];
}
